import React, { useState, useEffect, useRef, createContext, useContext, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Car, Wrench, Clock, DollarSign, Star, MapPin,
  CheckCircle, XCircle, AlertTriangle, ArrowRight,
  ArrowLeft, Phone, Mail, User, Calendar, TrendingUp,
  Loader, Zap, Shield, X,
  LogOut, Eye, EyeOff, Lock, Building, Search, ChevronDown,
  Navigation, ClipboardList, MessageSquare, Award, TrendingDown, Flag,
  Edit3, Send, Sparkles, CreditCard, HelpCircle,
  Users, AlertCircle, Settings, Upload,
  Receipt, Crosshair, Truck, FileText
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { LoadScript, GoogleMap, Marker } from '@react-google-maps/api';
// Recharts moved to lazy-loaded chunks (MechanicDashboard, AdminDashboard)
import './App.css';
import LoadingSpinner from './components/LoadingSpinner';
import ConfirmModal from './components/ConfirmModal';
import { getTimeRemaining, formatCountdown, checkNoShowThreshold } from './utils/countdown';
import { supabaseFetch, SUPABASE_URL, SUPABASE_KEY, getAuthToken, getStripeErrorMessage, getStripeErrorDetail, sendSMS, sendEmail, buildMechanicSMSServiceCall, SERVICE_ZONES, getZoneFromZip, extractZipFromAddress } from './utils/supabase';
import { Analytics } from '@vercel/analytics/react';

// Lazy-loaded page chunks
const MechanicLandingPage = lazy(() => import('./pages/MechanicLanding'));
const MechanicDashboardPage = lazy(() => import('./pages/MechanicDashboard'));
const MechanicAuthPage = lazy(() => import('./pages/MechanicAuth'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const LinksPage = lazy(() => import('./pages/Links'));
const LazyMyVehicles = lazy(() => import('./pages/CustomerSettings').then(m => ({ default: m.MyVehiclesPage })));
const LazyPaymentMethods = lazy(() => import('./pages/CustomerSettings').then(m => ({ default: m.PaymentMethodsPage })));
const LazyMyReviews = lazy(() => import('./pages/CustomerSettings').then(m => ({ default: m.MyReviewsPage })));
const LazyHelpSupport = lazy(() => import('./pages/CustomerSettings').then(m => ({ default: m.HelpSupportPage })));
const LazySettings = lazy(() => import('./pages/CustomerSettings').then(m => ({ default: m.SettingsPage })));
const LazyTerms = lazy(() => import('./pages/Legal').then(m => ({ default: m.TermsPage })));
const LazyPrivacy = lazy(() => import('./pages/Legal').then(m => ({ default: m.PrivacyPage })));

// Google Maps Configuration
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const GOOGLE_MAPS_LIBRARIES = ['places'];


// Initialize Stripe
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// Promotion: 50% off first service call for new customers
const PROMO_FIRST_SERVICE_CALL_ENABLED = true;
const PROMO_FIRST_SERVICE_DISCOUNT = 0.5; // 50% off

// Sleek Modern Logo Component (kept for fallback)
// eslint-disable-next-line no-unused-vars
function RevvooLogo({ size = 32, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      className={`revvoo-logo ${className}`}
    >
      <defs>
        <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DC2626" />
          <stop offset="100%" stopColor="#EF4444" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="10" fill="url(#logoGradient)" />
      <path
        d="M12 10H22C25.866 10 29 13.134 29 17C29 19.577 27.577 21.825 25.464 23L29 30H23.5L20.5 24H17V30H12V10ZM17 19.5H21.5C22.881 19.5 24 18.381 24 17C24 15.619 22.881 14.5 21.5 14.5H17V19.5Z"
        fill="white"
      />
    </svg>
  );
}

// Alternative minimal logo (just the R mark)
// eslint-disable-next-line no-unused-vars
function RevvooLogoMark({ size = 32, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      className={`revvoo-logo-mark ${className}`}
    >
      <defs>
        <linearGradient id="logoMarkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DC2626" />
          <stop offset="100%" stopColor="#EF4444" />
        </linearGradient>
      </defs>
      <path
        d="M4 4H24C31.732 4 38 10.268 38 18C38 23.15 35.15 27.65 30.928 30L38 44H27L20 32H12V44H4V4ZM12 24H22C25.314 24 28 21.314 28 18C28 14.686 25.314 12 22 12H12V24Z"
        fill="url(#logoMarkGradient)"
        transform="scale(0.9) translate(2, -2)"
      />
    </svg>
  );
}

// Splash Screen Component
function SplashScreen({ isVisible }) {
  const [shouldRender, setShouldRender] = useState(true);

  useEffect(() => {
    if (!isVisible) {
      // Keep rendering during fade out, then remove from DOM
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
    setShouldRender(true);
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <div className={`splash-screen ${!isVisible ? 'fade-out' : ''}`}>
      <img src="/logo.png" alt="Revvoo" className="splash-logo" />
      <div className="splash-spinner"></div>
    </div>
  );
}

// Supabase utilities imported from ./utils/supabase
// (supabaseFetch, SUPABASE_URL, SUPABASE_KEY, etc. are now in src/utils/supabase.js)







// ========================================
// AUTHENTICATION
// ========================================

// Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsUserType, setNeedsUserType] = useState(false);
  const [needsMechanicOnboarding, setNeedsMechanicOnboarding] = useState(false);
  const [authError, setAuthError] = useState(null); // M5 FIX: Show OAuth errors to user
  const signOutRef = useRef(null); // H2: ref to avoid circular dep in token refresh

  // Check for existing session on mount
  useEffect(() => {
    const storedSession = localStorage.getItem('revvoo_session');
    const storedUser = localStorage.getItem('revvoo_user');

    if (storedSession && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setSession(JSON.parse(storedSession));
        setUser(parsedUser);
      } catch (e) {
        console.error('Corrupted session data in localStorage, clearing:', e);
        localStorage.removeItem('revvoo_session');
        localStorage.removeItem('revvoo_user');
      }
    }
    // If URL has OAuth hash tokens or error params, keep loading true — the OAuth callback useEffect will set it to false
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    if (hashParams.get('access_token') || queryParams.get('error') || queryParams.get('error_description')) {
      // OAuth callback in progress — loading stays true
      return;
    }
    setLoading(false);
  }, []);

  // H2 FIX: Token refresh logic — Supabase JWTs expire after ~1 hour.
  // Without refresh, the user appears logged in but all API calls fail silently.
  useEffect(() => {
    if (!session?.refresh_token) return;

    const refreshSession = async () => {
      try {
        const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refresh_token: session.refresh_token })
        });

        if (!response.ok) {
          console.warn('Token refresh failed, signing out');
          signOutRef.current();
          return;
        }

        const newSession = await response.json();
        if (newSession.access_token) {
          setSession(newSession);
          localStorage.setItem('revvoo_session', JSON.stringify(newSession));
          // Update user if needed
          if (newSession.user) {
            setUser(prev => prev ? { ...prev, ...newSession.user } : prev);
          }
        }
      } catch (err) {
        console.error('Token refresh error:', err);
      }
    };

    // Check if current token is expired or about to expire (within 5 minutes)
    try {
      const tokenPayload = JSON.parse(atob(session.access_token.split('.')[1]));
      const expiresAt = tokenPayload.exp * 1000;
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (now >= expiresAt - fiveMinutes) {
        // Token expired or about to expire — refresh now
        refreshSession();
      }
    } catch (e) {
      // Can't parse token — try refreshing anyway
      refreshSession();
    }

    // Set up periodic refresh every 50 minutes (tokens last ~60 min)
    const intervalId = setInterval(refreshSession, 50 * 60 * 1000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.refresh_token]);

  // Sign up with email/password
  const signUp = async (email, password, userData) => {
    try {

      const response = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      // Handle error responses (both formats)
      if (data.error) {
        throw new Error(data.error.message || 'Signup failed');
      }
      if (data.msg && data.code && data.code >= 400) {
        throw new Error(data.msg || 'Signup failed');
      }

      // Supabase returns different formats depending on email confirmation setting:
      // - With autoconfirm ON:  { user: { id, email, ... }, session: { ... } }
      // - With autoconfirm OFF: { id, email, ... } (user object directly, no session)
      const authUser = data.user || (data.id ? data : null);
      const authSession = data.session || null;


      if (authUser) {
        // Create user profile in users table
        try {
          await supabaseFetch('users', 'POST', {
            id: authUser.id,
            email: email,
            name: userData.name,
            phone: userData.phone,
            user_type: userData.userType
          });
        } catch (userError) {
          console.error('AuthProvider signUp: Failed to create user profile:', userError);
          // If it's a duplicate key error, the user already exists - that's OK
        }

        // If mechanic, create mechanic profile
        if (userData.userType === 'mechanic' && userData.mechanicData) {
          try {
            await supabaseFetch('mechanics', 'POST', {
              user_id: authUser.id,
              business_name: userData.mechanicData.businessName,
              email: email,
              phone: userData.phone || null,
              specialties: userData.mechanicData.specialties,
              service_call_fee: userData.mechanicData.serviceFee,
              service_area: userData.mechanicData.serviceAreas?.length > 0 ? JSON.stringify(userData.mechanicData.serviceAreas) : null,
              rating: 5.0,
              reviews_count: 0,
              available: false,
              status: 'inactive'
            });
          } catch (mechanicError) {
            console.error('AuthProvider signUp: Failed to create mechanic profile:', mechanicError);
            // Surface this error to the user since it means their profile is incomplete
            return { success: false, error: 'Account created but failed to save mechanic profile. Please try logging in and completing your profile.' };
          }
        }

        const userProfile = {
          ...authUser,
          name: userData.name,
          phone: userData.phone,
          user_type: userData.userType
        };

        setUser(userProfile);
        setSession(authSession);
        if (authSession) {
          localStorage.setItem('revvoo_session', JSON.stringify(authSession));
        }
        localStorage.setItem('revvoo_user', JSON.stringify(userProfile));
      }

      return { success: true, data };
    } catch (error) {
      console.error('AuthProvider signUp: Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  // Sign in with email/password
  const signIn = async (email, password) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message || 'Login failed');
      }

      // Get user profile from users table
      try {
        const encodedEmail = encodeURIComponent(email);
        const userProfiles = await supabaseFetch('users', 'GET', null, `?email=eq.${encodedEmail}`);
        const userProfile = userProfiles[0] || {};

        const fullUser = {
          ...data.user,
          ...userProfile
        };

        setUser(fullUser);
        setSession(data);
        localStorage.setItem('revvoo_session', JSON.stringify(data));
        localStorage.setItem('revvoo_user', JSON.stringify(fullUser));
      } catch (profileError) {
        console.error('signIn: Failed to fetch user profile:', profileError);
        setUser(data.user);
        setSession(data);
        localStorage.setItem('revvoo_session', JSON.stringify(data));
        localStorage.setItem('revvoo_user', JSON.stringify(data.user));
      }

      return { success: true, data };
    } catch (error) {
      console.error('signIn: Login failed:', error.message);
      return { success: false, error: error.message };
    }
  };

  // Sign in with Google (customer only)
  const signInWithGoogle = () => {
    const redirectUrl = encodeURIComponent(`${window.location.origin}`);
    const authUrl = `${SUPABASE_URL}/auth/v1/authorize?provider=google&redirect_to=${redirectUrl}`;
    window.location.href = authUrl;
  };

  // Handle OAuth callback (customer Google sign-in only)
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      // Check for error in query params (Supabase returns errors as query params)
      const queryParams = new URLSearchParams(window.location.search);
      const oauthError = queryParams.get('error_description') || queryParams.get('error');
      if (oauthError) {
        console.error('[Revvoo] OAuth error:', oauthError);
        // M5 FIX: Show error to user instead of silent fail
        setAuthError('Sign in failed. Please try again.');
        window.history.replaceState(null, '', window.location.pathname);
        setLoading(false);
        return;
      }

      if (accessToken) {
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);

        try {
          // Get user info from Supabase auth
          const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
            headers: {
              'apikey': SUPABASE_KEY,
              'Authorization': `Bearer ${accessToken}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to verify your account. Please try signing in again.');
          }

          const userData = await response.json();

          const sessionData = { access_token: accessToken, refresh_token: refreshToken };

          // Check if user exists in our users table
          try {
            const existingUsers = await supabaseFetch('users', 'GET', null, `?email=eq.${encodeURIComponent(userData.email)}`);

            if (existingUsers && existingUsers.length > 0) {
              // Existing user - log them in
              const fullUser = { ...userData, ...existingUsers[0] };
              setSession(sessionData);
              setUser(fullUser);
              localStorage.setItem('revvoo_session', JSON.stringify(sessionData));
              localStorage.setItem('revvoo_user', JSON.stringify(fullUser));
            } else {
              // New Google user — ask them to choose user type
              setSession(sessionData);
              setUser(userData);
              localStorage.setItem('revvoo_session', JSON.stringify(sessionData));
              localStorage.setItem('revvoo_user', JSON.stringify(userData));
              setNeedsUserType(true);
            }
          } catch {
            console.error('OAuth callback: Failed to check existing user');
            setSession(sessionData);
            setUser(userData);
            localStorage.setItem('revvoo_session', JSON.stringify(sessionData));
            localStorage.setItem('revvoo_user', JSON.stringify(userData));
            setNeedsUserType(true);
          }
        } catch (error) {
          // M5 FIX: Show network/auth errors to the user instead of spinning forever
          console.error('OAuth callback error:', error);
          setAuthError(error.message || 'Sign in failed due to a connection error. Please try again.');
        }
        // OAuth processing complete — stop loading
        setLoading(false);
      } else {
        // M5 FIX: If we got here with no access_token and no error, stop loading
        // This prevents the spinner from spinning forever on unexpected redirects
        setLoading(false);
      }
    };

    handleOAuthCallback();
  }, []);

  // Complete user type selection (for Google sign-in)
  const completeUserTypeSelection = async (userType) => {
    try {
      await supabaseFetch('users', 'POST', {
        id: user.id,
        email: user.email,
        name: user.user_metadata?.full_name || user.email.split('@')[0],
        user_type: userType
      });

      const updatedUser = {
        ...user,
        name: user.user_metadata?.full_name || user.email.split('@')[0],
        user_type: userType
      };

      setUser(updatedUser);
      localStorage.setItem('revvoo_user', JSON.stringify(updatedUser));
      setNeedsUserType(false);

      if (userType === 'mechanic') {
        setNeedsMechanicOnboarding(true);
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Complete mechanic onboarding
  const completeMechanicOnboarding = async (mechanicData) => {
    try {
      await supabaseFetch('mechanics', 'POST', {
        user_id: user.id,
        business_name: mechanicData.businessName,
        email: user.email || null,
        phone: mechanicData.phone || null,
        specialties: mechanicData.specialties,
        service_call_fee: mechanicData.serviceFee,
        service_area: mechanicData.serviceAreas?.length > 0 ? JSON.stringify(mechanicData.serviceAreas) : null,
        rating: 5.0,
        reviews_count: 0,
        available: false,
        status: 'inactive'
      });

      setNeedsMechanicOnboarding(false);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Sign out
  const signOut = () => {
    setUser(null);
    setSession(null);
    setNeedsUserType(false);
    setNeedsMechanicOnboarding(false);
    localStorage.removeItem('revvoo_session');
    localStorage.removeItem('revvoo_user');
    localStorage.removeItem('revvoo_view_mode');
  };

  // H2: Keep ref in sync for token refresh useEffect
  signOutRef.current = signOut;

  // Forgot password
  // H4 FIX: Include redirectTo so the reset email links to /reset-password
  const resetPassword = async (email) => {
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const response = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, redirectTo })
      });

      if (response.ok) {
        return { success: true };
      }
      throw new Error('Failed to send reset email');
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    setUser,
    session,
    loading,
    authError,
    setAuthError,
    needsUserType,
    needsMechanicOnboarding,
    setNeedsMechanicOnboarding,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    completeUserTypeSelection,
    completeMechanicOnboarding
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Auth hook
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// User Type Selection (for Google sign-in)
function UserTypeSelection() {
  const { user, completeUserTypeSelection } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // If user already has a user_type, skip the chooser and redirect
  useEffect(() => {
    if (user && user.user_type) {
      if (user.user_type === 'mechanic') {
        navigate('/mechanic-dashboard', { replace: true });
      } else if (user.user_type === 'customer') {
        navigate('/', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSelect = async (type) => {
    setLoading(true);
    await completeUserTypeSelection(type);
    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-container user-type-page">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo.png" alt="Revvoo" className="auth-logo-img" />
          </div>
          <h1>Welcome to Revvoo!</h1>
          <p>How would you like to use Revvoo?</p>
        </div>

        <div className="user-type-cards">
          <button
            className="user-type-card"
            onClick={() => handleSelect('customer')}
            disabled={loading}
          >
            <div className="type-icon customer">
              <User size={40} />
            </div>
            <h3>I'm a Customer</h3>
            <p>I need help with my car. Find mobile mechanics near me.</p>
            <span className="select-text">Select <ArrowRight size={16} /></span>
          </button>

          <button
            className="user-type-card"
            onClick={() => handleSelect('mechanic')}
            disabled={loading}
          >
            <div className="type-icon mechanic">
              <Wrench size={40} />
            </div>
            <h3>I'm a Mechanic</h3>
            <p>I want to offer my services and grow my business.</p>
            <span className="select-text">Select <ArrowRight size={16} /></span>
          </button>
        </div>

        {loading && (
          <div className="loading-overlay">
            <Loader className="spinner" size={32} />
          </div>
        )}
      </div>
    </div>
  );
}

// Mechanic Onboarding (for Google sign-in mechanics)
function MechanicOnboarding() {
  const { completeMechanicOnboarding } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    serviceFee: 45,
    specialties: [],
    serviceAreas: []
  });

  const specialtyOptions = [
    'Brakes', 'Engine', 'Transmission', 'Electrical', 'AC/Heating',
    'Oil Change', 'Tires', 'Suspension', 'Diagnostics', 'Emergency'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await completeMechanicOnboarding({
      businessName: formData.businessName,
      specialties: formData.specialties,
      serviceFee: parseFloat(formData.serviceFee),
      serviceAreas: formData.serviceAreas
    });
    if (result.success) {
      navigate('/mechanics/application-received', { replace: true });
    }
    setLoading(false);
  };

  const toggleSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const toggleServiceZone = (zoneName) => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: prev.serviceAreas.includes(zoneName)
        ? prev.serviceAreas.filter(z => z !== zoneName)
        : [...prev.serviceAreas, zoneName]
    }));
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo.png" alt="Revvoo" className="auth-logo-img" />
          </div>
          <h1>Set Up Your Business</h1>
          <p>Tell us about your mechanic business</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>Business Name</label>
            <div className="input-with-icon">
              <Building size={20} />
              <input
                type="text"
                value={formData.businessName}
                onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                placeholder="Your Auto Shop Name"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Service Call Fee ($)</label>
            <div className="input-with-icon">
              <DollarSign size={20} />
              <input
                type="number"
                value={formData.serviceFee}
                onChange={(e) => setFormData(prev => ({ ...prev, serviceFee: e.target.value }))}
                placeholder="50"
                min="0"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Specialties (select all that apply)</label>
            <div className="specialties-grid">
              {specialtyOptions.map(specialty => (
                <button
                  key={specialty}
                  type="button"
                  className={`specialty-btn ${formData.specialties.includes(specialty) ? 'active' : ''}`}
                  onClick={() => toggleSpecialty(specialty)}
                >
                  {specialty}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Service Area {formData.serviceAreas.length > 0 && <span className="zone-count">{formData.serviceAreas.length} zone{formData.serviceAreas.length !== 1 ? 's' : ''} selected</span>}</label>
            <small className="form-hint" style={{marginBottom: '10px', marginTop: '-4px'}}>Select the zones you serve</small>
            <div className="service-zones-grid">
              {SERVICE_ZONES.map(zone => (
                <button
                  key={zone.name}
                  type="button"
                  className={`service-zone-chip ${formData.serviceAreas.includes(zone.name) ? 'selected' : ''}`}
                  onClick={() => toggleServiceZone(zone.name)}
                >
                  <span className="zone-name">{zone.name}</span>
                  <span className="zone-areas">{zone.areas}</span>
                </button>
              ))}
            </div>
          </div>

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? <Loader className="spinner" size={20} /> : null}
            {loading ? 'Setting up...' : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Login Modal Component (Login Gate)
// Full Page Auth Component (Login/Signup)
function AuthPage({ mode: initialMode = 'signup' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMechanicOnboarding, setShowMechanicOnboarding] = useState(false);

  const { signIn, signUp, signInWithGoogle, user, setNeedsMechanicOnboarding } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  // Mechanic onboarding data
  const [mechanicData, setMechanicData] = useState({
    businessName: '',
    specialties: [],
    serviceFee: 45,
    serviceAreas: []
  });

  // Get the return URL and user type from query params
  const searchParams = new URLSearchParams(location.search);
  // H3 FIX: Validate returnTo is a safe relative path to prevent open redirect attacks.
  // /login?returnTo=https://evil.com would redirect to an external phishing site.
  const rawReturnTo = searchParams.get('returnTo') || '/';
  const returnTo = (rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//')) ? rawReturnTo : '/';
  const signupType = searchParams.get('type') || 'customer'; // 'customer' or 'mechanic'
  const isMechanicSignup = signupType === 'mechanic';

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(returnTo, { replace: true });
    }
  }, [user, navigate, returnTo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await signIn(formData.email, formData.password);

    if (result.success) {
      // Check user type and redirect accordingly
      // The user profile is fetched in signIn, check user_type
      const storedUser = localStorage.getItem('revvoo_user');
      let userData = null;
      try {
        userData = storedUser ? JSON.parse(storedUser) : null;
      } catch (e) {
        console.error('Corrupted user data in localStorage, clearing:', e);
        localStorage.removeItem('revvoo_user');
      }

      if (userData?.user_type === 'mechanic') {
        // Check mechanic status before redirecting
        try {
          const mechanicData = await supabaseFetch('mechanics', 'GET', null, `?user_id=eq.${userData.id}`);
          if (!mechanicData || mechanicData.length === 0) {
            // Mechanic user exists but no mechanics table entry — needs onboarding
            setNeedsMechanicOnboarding(true);
            navigate('/', { replace: true });
            return;
          }
          const mechanicStatus = mechanicData[0].status;
          if (mechanicStatus === 'active' || mechanicStatus === 'inactive') {
            navigate('/mechanic-dashboard', { replace: true });
          } else if (mechanicStatus === 'rejected') {
            navigate('/mechanics/application-rejected', { replace: true });
          } else {
            navigate('/mechanics/application-received', { replace: true });
          }
        } catch (err) {
          console.error('Error checking mechanic status:', err);
          navigate('/mechanic-dashboard', { replace: true });
        }
      } else {
        navigate(returnTo, { replace: true });
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // If mechanic signup, show onboarding first
    if (isMechanicSignup && !showMechanicOnboarding) {
      setShowMechanicOnboarding(true);
      setLoading(false);
      return;
    }

    // Validate mechanic onboarding fields
    if (isMechanicSignup && showMechanicOnboarding) {
      if (mechanicData.businessName.trim().length < 2) {
        setError('Business name must be at least 2 characters');
        setLoading(false);
        return;
      }
      if (mechanicData.serviceFee <= 0 || mechanicData.serviceFee > 200) {
        setError('Service call fee must be between $1 and $200');
        setLoading(false);
        return;
      }
      if (mechanicData.specialties.length === 0) {
        setError('Please select at least one specialty');
        setLoading(false);
        return;
      }
    }

    const userData = {
      name: formData.name,
      phone: formData.phone,
      userType: isMechanicSignup ? 'mechanic' : 'customer',
      mechanicData: isMechanicSignup ? mechanicData : null
    };

    const result = await signUp(formData.email, formData.password, userData);

    if (result.success) {
      // Redirect mechanics to dashboard, customers to home
      navigate(isMechanicSignup ? '/dashboard' : returnTo, { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleGoogleSignIn = () => {
    // Store return URL before redirecting to Google
    localStorage.setItem('revvoo_return_to', returnTo);
    signInWithGoogle();
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    const newPath = newMode === 'login' ? '/login' : '/signup';
    navigate(`${newPath}${location.search}`, { replace: true });
  };

  return (
    <div className="auth-page">
      <div className="auth-page-container">
        <Link to="/" className="auth-back-link">
          <ArrowLeft size={20} />
          Back to Home
        </Link>

        <div className="auth-card">
          <div className="auth-header">
            <Link to="/" className="auth-logo">
              <img src="/logo.png" alt="Revvoo" />
            </Link>
            {isMechanicSignup && mode === 'signup' && (
              <div className="mechanic-signup-badge">
                <Wrench size={16} />
                Mechanic Account
              </div>
            )}
            <h1>
              {mode === 'login'
                ? 'Welcome back!'
                : showMechanicOnboarding
                  ? 'Set up your business'
                  : isMechanicSignup
                    ? 'Join as a Mechanic'
                    : 'Create your account'}
            </h1>
            <p>
              {mode === 'login'
                ? 'Sign in to access your account'
                : showMechanicOnboarding
                  ? 'Tell us about your business to start receiving jobs'
                  : isMechanicSignup
                    ? 'Start earning on your own schedule'
                    : 'Sign up to book your mechanic'}
            </p>
          </div>

          {error && (
            <div className="auth-error">
              <XCircle size={18} />
              {error}
            </div>
          )}

          {/* Show mechanic onboarding form */}
          {showMechanicOnboarding ? (
            <form onSubmit={handleSignup} className="auth-form mechanic-onboarding-form">
              <div className="form-group">
                <label>Business Name</label>
                <div className="input-with-icon">
                  <Building size={20} />
                  <input
                    type="text"
                    value={mechanicData.businessName}
                    onChange={(e) => setMechanicData({...mechanicData, businessName: e.target.value})}
                    placeholder="e.g., Quick Fix Auto"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Service Call Fee ($)</label>
                <div className="input-with-icon">
                  <DollarSign size={20} />
                  <input
                    type="number"
                    value={mechanicData.serviceFee}
                    onChange={(e) => setMechanicData({...mechanicData, serviceFee: parseInt(e.target.value) || 0})}
                    min="25"
                    max="200"
                    required
                  />
                </div>
                <small className="form-hint">Customers pay this when you arrive (typically $50-$100)</small>
                {mechanicData.serviceFee > 200 && <span className="field-error-text">Maximum fee is $200</span>}
                {mechanicData.serviceFee <= 0 && <span className="field-error-text">Fee must be greater than $0</span>}
              </div>

              <div className="form-group">
                <label>Specialties (select all that apply)</label>
                <div className="specialty-grid">
                  {['General Repair', 'Brakes', 'Oil Change', 'Diagnostics', 'Electrical', 'Engine', 'Transmission', 'AC/Heating', 'Tires', 'Batteries'].map(specialty => (
                    <label key={specialty} className={`specialty-chip ${mechanicData.specialties.includes(specialty) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={mechanicData.specialties.includes(specialty)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMechanicData({...mechanicData, specialties: [...mechanicData.specialties, specialty]});
                          } else {
                            setMechanicData({...mechanicData, specialties: mechanicData.specialties.filter(s => s !== specialty)});
                          }
                        }}
                      />
                      {specialty}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Service Area {mechanicData.serviceAreas.length > 0 && <span className="zone-count">{mechanicData.serviceAreas.length} zone{mechanicData.serviceAreas.length !== 1 ? 's' : ''} selected</span>}</label>
                <small className="form-hint" style={{marginBottom: '10px', marginTop: '-4px'}}>Select the zones you serve</small>
                <div className="service-zones-grid">
                  {SERVICE_ZONES.map(zone => (
                    <button
                      key={zone.name}
                      type="button"
                      className={`service-zone-chip ${mechanicData.serviceAreas.includes(zone.name) ? 'selected' : ''}`}
                      onClick={() => {
                        setMechanicData(prev => ({
                          ...prev,
                          serviceAreas: prev.serviceAreas.includes(zone.name)
                            ? prev.serviceAreas.filter(z => z !== zone.name)
                            : [...prev.serviceAreas, zone.name]
                        }));
                      }}
                    >
                      <span className="zone-name">{zone.name}</span>
                      <span className="zone-areas">{zone.areas}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="onboarding-buttons">
                <button
                  type="button"
                  className="auth-back-btn"
                  onClick={() => setShowMechanicOnboarding(false)}
                >
                  <ArrowLeft size={18} />
                  Back
                </button>
                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={loading || mechanicData.businessName.trim().length < 2 || mechanicData.specialties.length === 0 || mechanicData.serviceFee <= 0 || mechanicData.serviceFee > 200}
                >
                  {loading ? <Loader className="spinner" size={20} /> : null}
                  {loading ? 'Creating account...' : 'Complete Signup'}
                </button>
              </div>
            </form>
          ) : (
            <>
              {/* Google Sign In - Primary (hide for mechanic onboarding) */}
              {!showMechanicOnboarding && (
                <button type="button" className="google-btn primary" onClick={handleGoogleSignIn} disabled={loading}>
                  <svg viewBox="0 0 24 24" className="google-icon">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
              )}

              <div className="auth-divider">
                <span>or</span>
              </div>

              {mode === 'signup' ? (
                <form onSubmit={handleSignup} className="auth-form">
                  <div className="form-group">
                    <div className="input-with-icon">
                      <User size={20} />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Full name"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-with-icon">
                      <Mail size={20} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email address"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-with-icon">
                      <Phone size={20} />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Phone number"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-with-icon password-input">
                      <Lock size={20} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Create password"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="auth-submit-btn" disabled={loading}>
                    {loading ? <Loader className="spinner" size={20} /> : null}
                    {loading ? 'Creating account...' : isMechanicSignup ? 'Continue' : 'Sign up with Email'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="auth-form">
                  <div className="form-group">
                    <div className="input-with-icon">
                      <Mail size={20} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email address"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-with-icon password-input">
                      <Lock size={20} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Password"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="auth-submit-btn" disabled={loading}>
                    {loading ? <Loader className="spinner" size={20} /> : null}
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
              )}
            </>
          )}

          <div className="auth-footer">
            {mode === 'signup' ? (
              <p>Already have an account? <button onClick={() => switchMode('login')}>Log in</button></p>
            ) : (
              <p>Don't have an account? <button onClick={() => switchMode('signup')}>Sign up</button></p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// MechanicAuthPage extracted to src/pages/MechanicAuth.js

// MechanicDashboardPage extracted to src/pages/MechanicDashboard.js

// Header Component
function Header({ onMyBookingsClick }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Mechanics should never see the customer header - redirect them
  useEffect(() => {
    if (user?.user_type === 'mechanic') {
      navigate('/mechanic-dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Close desktop dropdown on click outside or Escape key
  useEffect(() => {
    if (!desktopDropdownOpen) return;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDesktopDropdownOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setDesktopDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [desktopDropdownOpen]);

  const toggleMobileMenu = () => {
    const newState = !mobileMenuOpen;
    setMobileMenuOpen(newState);
    // M15 FIX: Toggle on both html and body to prevent scroll behind menu
    // without using position:fixed (which causes scroll-to-top on iOS Safari)
    document.body.classList.toggle('menu-open', newState);
    document.documentElement.classList.toggle('menu-open', newState);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    document.body.classList.remove('menu-open');
    document.documentElement.classList.remove('menu-open');
  };

  return (
    <header className="header">
      <div className="header-content">
        {/* Mobile Hamburger Menu Button */}
        <button
          className={`mobile-menu-btn ${mobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        <Link to="/" className="logo" onClick={closeMobileMenu}>
          <img src="/logo.png" alt="Revvoo" className="logo-image" />
        </Link>

        {/* Desktop Navigation */}
        <div className="header-right desktop-nav">
          {user ? (
            <>
              {user.user_type !== 'mechanic' && (
                <button className="my-bookings-btn" onClick={onMyBookingsClick}>
                  <ClipboardList size={18} />
                  My Bookings
                </button>
              )}

              <div className="desktop-user-dropdown" ref={dropdownRef}>
                <button
                  className="desktop-dropdown-trigger"
                  onClick={() => setDesktopDropdownOpen(prev => !prev)}
                  aria-expanded={desktopDropdownOpen}
                  aria-haspopup="true"
                >
                  <div className="user-avatar">
                    <User size={18} />
                  </div>
                  <span className="user-name">{user.name || user.email?.split('@')[0]}</span>
                  <ChevronDown size={16} className={`dropdown-chevron ${desktopDropdownOpen ? 'open' : ''}`} />
                </button>

                {desktopDropdownOpen && (
                  <div className="desktop-dropdown-menu" role="menu">
                    {user.user_type !== 'mechanic' && (
                      <>
                        <button className="desktop-dropdown-item" onClick={() => { onMyBookingsClick(); setDesktopDropdownOpen(false); }}>
                          <ClipboardList size={18} />
                          <span>My Bookings</span>
                        </button>
                        <button className="desktop-dropdown-item" onClick={() => { navigate('/my-vehicles'); setDesktopDropdownOpen(false); }}>
                          <Car size={18} />
                          <span>My Vehicles</span>
                        </button>
                        <button className="desktop-dropdown-item" onClick={() => { navigate('/payment-methods'); setDesktopDropdownOpen(false); }}>
                          <CreditCard size={18} />
                          <span>Payment Methods</span>
                        </button>
                        <button className="desktop-dropdown-item" onClick={() => { navigate('/my-reviews'); setDesktopDropdownOpen(false); }}>
                          <Star size={18} />
                          <span>My Reviews</span>
                        </button>
                        <div className="desktop-dropdown-divider"></div>
                      </>
                    )}
                    <button className="desktop-dropdown-item" onClick={() => { navigate('/help-support'); setDesktopDropdownOpen(false); }}>
                      <HelpCircle size={18} />
                      <span>Help & Support</span>
                    </button>
                    <button className="desktop-dropdown-item" onClick={() => { navigate('/settings'); setDesktopDropdownOpen(false); }}>
                      <Settings size={18} />
                      <span>Settings</span>
                    </button>
                    <div className="desktop-dropdown-divider"></div>
                    <button className="desktop-dropdown-item desktop-dropdown-logout" onClick={() => { signOut(); setDesktopDropdownOpen(false); }}>
                      <LogOut size={18} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="auth-links">
              <Link to="/mechanics" className="header-mechanics-link">
                Become a Mechanic
              </Link>
              <button className="header-login-btn" onClick={() => navigate('/login')}>
                Login
              </button>
              <button className="header-signup-btn" onClick={() => navigate('/signup')}>
                Sign Up
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
          onClick={closeMobileMenu}
        ></div>

        {/* Mobile Slide-in Menu */}
        <nav className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-content">
            <button className="mobile-menu-close" onClick={closeMobileMenu}>
              <X size={22} />
            </button>
            {user ? (
              <>
                {/* Profile Section */}
                <div className="nav-profile-section">
                  <div className="nav-profile-avatar">
                    {(user.name || user.email || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'U'}
                  </div>
                  <div className="nav-profile-details">
                    <span className="nav-profile-name">{user.name || user.email?.split('@')[0]}</span>
                    <span className="nav-profile-email">{user.email}</span>
                  </div>
                </div>
                <div className="nav-divider"></div>

                {/* Main Menu Items */}
                {user.user_type !== 'mechanic' && (
                  <>
                    <button className="nav-menu-item" onClick={() => { onMyBookingsClick(); closeMobileMenu(); }}>
                      <ClipboardList size={20} />
                      <span>My Bookings</span>
                    </button>
                    <button className="nav-menu-item" onClick={() => { navigate('/my-vehicles'); closeMobileMenu(); }}>
                      <Car size={20} />
                      <span>My Vehicles</span>
                    </button>
                    <button className="nav-menu-item" onClick={() => { navigate('/payment-methods'); closeMobileMenu(); }}>
                      <Receipt size={20} />
                      <span>Payments</span>
                    </button>
                    <button className="nav-menu-item" onClick={() => { navigate('/my-reviews'); closeMobileMenu(); }}>
                      <Star size={20} />
                      <span>My Reviews</span>
                    </button>
                    <button className="nav-menu-item" onClick={() => { closeMobileMenu(); navigate('/?book=true'); }}>
                      <Wrench size={20} />
                      <span>Book a Service</span>
                    </button>
                  </>
                )}
                <div className="nav-divider"></div>

                {/* Support & Settings */}
                <button className="nav-menu-item" onClick={() => { navigate('/help-support'); closeMobileMenu(); }}>
                  <HelpCircle size={20} />
                  <span>Help & Support</span>
                </button>
                <button className="nav-menu-item" onClick={() => { navigate('/settings'); closeMobileMenu(); }}>
                  <Settings size={20} />
                  <span>Settings</span>
                </button>
                <div className="nav-divider"></div>

                {/* Logout */}
                <button className="nav-menu-item nav-logout" onClick={() => { signOut(); closeMobileMenu(); }}>
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <div className="nav-guest-header">
                  <img src="/logo.png" alt="Revvoo" className="nav-guest-logo" />
                </div>
                <div className="nav-divider"></div>
                <button
                  className="nav-menu-item"
                  onClick={() => { navigate('/login'); closeMobileMenu(); }}
                >
                  <User size={20} />
                  <span>Login</span>
                </button>
                <button
                  className="nav-menu-item nav-signup-item"
                  onClick={() => { navigate('/signup'); closeMobileMenu(); }}
                >
                  <ArrowRight size={20} />
                  <span>Sign Up</span>
                </button>
                <div className="nav-divider"></div>
                <Link
                  to="/mechanics"
                  className="nav-menu-item"
                  onClick={closeMobileMenu}
                >
                  <Wrench size={20} />
                  <span>Become a Mechanic</span>
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}

// Footer Component
function Footer() {
  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-grid">
          {/* Column 1: Brand */}
          <div className="footer-brand">
            <div className="footer-logo">
              <img src="/logo.png" alt="Revvoo" className="footer-logo-image" />
            </div>
            <p className="footer-tagline">Mobile mechanics, on demand</p>
            <div className="footer-social">
              <a href="https://www.instagram.com/revvoo.app?igsh=NTc4MTIwNjQ2YQ%3D%3D&utm_source=qr" className="social-link" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/people/Revvoo/61587509303715/" className="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.tiktok.com/@revvoo.app?_r=1&_t=ZP-93KdRxwpqxT" className="social-link" aria-label="TikTok" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="footer-column">
            <h4>Quick Links</h4>
            <ul>
              <li><Link to="/how-it-works">How It Works</Link></li>
              <li><Link to="/mechanics">For Mechanics</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>

          {/* Column 3: Legal & Trust */}
          <div className="footer-column">
            <h4>Legal & Trust</h4>
            <ul>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/mechanics/benefits">Mechanic Benefits</Link></li>
              <li><Link to="/contact">Support</Link></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2026 Revvoo. All rights reserved. <span className="footer-location">| San Diego, CA</span></p>
        </div>
      </div>
    </footer>
  );
}

// Landing Page Component (Public) - Premium Minimal Design
function LandingPage({ onSubmitProblem, onBrowseMechanics }) {
  const [problem, setProblem] = useState('');
  const [selectedChip, setSelectedChip] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [activeMechanicCount, setActiveMechanicCount] = useState(5);

  const quickIssues = [
    "Won't Start",
    "Engine Light",
    "Brakes",
    "Oil Change",
    "Battery",
    "Overheating"
  ];

  // Fetch active verified mechanic count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await supabaseFetch('mechanics', 'GET', null,
          '?status=eq.active&stripe_onboarding_complete=eq.true&select=id');
        if (Array.isArray(data) && data.length > 0) {
          setActiveMechanicCount(data.length);
        }
      } catch (err) {
        // Keep default count - pill stays visible
      }
    };
    fetchCount();
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (problem.trim()) {
      onSubmitProblem(problem);
    }
  };

  const faqs = [
    {
      q: "How does Revvoo work?",
      a: "Describe your car problem, get an instant AI diagnosis, then choose from verified mechanics near you. They come to your location or you visit their shop."
    },
    {
      q: "How much does it cost?",
      a: "You pay a service call fee upfront (typically $45-65) to get your vehicle inspected. After inspection, your mechanic sends a detailed quote that you approve before any work begins."
    },
    {
      q: "How fast can a mechanic get to me?",
      a: "Most mechanics arrive within 15-30 minutes depending on your location and their availability. You'll see estimated arrival times before you book."
    },
    {
      q: "Are the mechanics verified?",
      a: "Yes. Every mechanic on Revvoo is background-checked, insured, and verified for proper certifications. You can also see their ratings and reviews from other customers."
    },
    {
      q: "What if I'm not satisfied with the service?",
      a: "We offer a satisfaction guarantee. If there's an issue with your service, contact our support team and we'll make it right."
    },
    {
      q: "What payment methods do you accept?",
      a: "We accept all major credit and debit cards through our secure payment system powered by Stripe. You only pay after approving the repair quote."
    }
  ];

  return (
    <div className="landing-page-v2">
      {/* HERO SECTION - Full Screen */}
      <section className="hero-section-v2">
        <div className="hero-container">
          <div className="hero-content-v2">
            <h1 className="hero-title-v2">
              A Mechanic to You
              <span className="hero-title-accent">in 30 Minutes.</span>
            </h1>
            <p className="hero-subtitle-v2">
              Describe your car problem. We'll match you with a verified pro near you.
            </p>

            {activeMechanicCount > 0 && (
              <div className="hero-live-pill">
                <span className="hero-live-dot"></span>
                {activeMechanicCount} mechanic{activeMechanicCount !== 1 ? 's' : ''} online in San Diego
              </div>
            )}

            <div className={`hero-pricing-callout ${PROMO_FIRST_SERVICE_CALL_ENABLED ? 'has-promo' : ''}`}>
              {PROMO_FIRST_SERVICE_CALL_ENABLED ? (
                <span>New users get <span className="hero-promo-highlight">50% off</span> their first service call.</span>
              ) : (
                <span>No hidden fees · You approve all costs</span>
              )}
            </div>

            <form onSubmit={handleSubmit} className="hero-form">
              <div className="hero-form-card">
                <div className="hero-input-wrapper">
                  <textarea
                    value={problem}
                    onChange={(e) => {
                      setProblem(e.target.value);
                      if (selectedChip && e.target.value !== selectedChip) {
                        setSelectedChip(null);
                      }
                    }}
                    placeholder="Describe your issue... (e.g., brakes grinding, won't start)"
                    className="hero-textarea"
                    rows={2}
                  />
                </div>
                <div className="hero-chips-section">
                  <div className="hero-chips-row">
                    {quickIssues.map((issue) => (
                      <button
                        key={issue}
                        type="button"
                        className={`hero-chip ${selectedChip === issue ? 'active' : ''}`}
                        onClick={() => {
                          if (selectedChip === issue) {
                            setSelectedChip(null);
                            setProblem('');
                          } else {
                            setSelectedChip(issue);
                            setProblem(issue);
                          }
                        }}
                      >
                        {issue}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button type="submit" className="hero-cta-btn" disabled={!problem.trim()}>
                <Zap size={20} />
                Get Instant Diagnosis
              </button>
              <button type="button" className="hero-secondary-btn" onClick={onBrowseMechanics}>
                See Mechanics Near You
                <ArrowRight size={16} />
              </button>
            </form>

          </div>
        </div>
      </section>

      {/* Location Strip removed — merged into hero live pill */}

      {/* HOW IT WORKS SECTION */}
      <section id="how-it-works" className={`section-v2 how-it-works-v2 animate-on-scroll ${isVisible['how-it-works'] ? 'visible' : ''}`}>
        <div className="section-container-v2">
          <h2 className="section-title-v2 light">How It Works</h2>
          <div className="steps-timeline">
            <div className="timeline-step stagger-item delay-1">
              <div className="timeline-number">1</div>
              <div className="timeline-content">
                <h3>Describe Your Problem</h3>
                <p>Our AI instantly diagnoses the issue and estimates cost.</p>
              </div>
            </div>
            <div className="timeline-connector stagger-item delay-2"></div>
            <div className="timeline-step stagger-item delay-3">
              <div className="timeline-number">2</div>
              <div className="timeline-content">
                <h3>Choose Your Mechanic</h3>
                <p>Browse rated pros with transparent, upfront pricing.</p>
              </div>
            </div>
            <div className="timeline-connector stagger-item delay-4"></div>
            <div className="timeline-step stagger-item delay-5">
              <div className="timeline-number">3</div>
              <div className="timeline-content">
                <h3>Get Fixed Fast</h3>
                <p>Mechanic arrives in minutes. Pay only when satisfied.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES WE COVER SECTION */}
      <section id="services" className={`section-v2 services-section-v2 animate-on-scroll ${isVisible['services'] ? 'visible' : ''}`}>
        <div className="section-container-v2">
          <h2 className="section-title-v2">Services We Cover</h2>
          <div className="services-compact">
            <div className="service-item-compact stagger-item delay-1">
              <Wrench size={20} />
              <span>Oil Changes</span>
            </div>
            <div className="service-item-compact stagger-item delay-2">
              <Car size={20} />
              <span>Brake Repair</span>
            </div>
            <div className="service-item-compact stagger-item delay-3">
              <Zap size={20} />
              <span>Battery Service</span>
            </div>
            <div className="service-item-compact stagger-item delay-4">
              <Search size={20} />
              <span>Diagnostics</span>
            </div>
            <div className="service-item-compact stagger-item delay-5">
              <Settings size={20} />
              <span>Engine Repair</span>
            </div>
            <div className="service-item-compact stagger-item delay-6">
              <Wrench size={20} />
              <span>Maintenance</span>
            </div>
            <div className="service-item-compact stagger-item delay-7">
              <Car size={20} />
              <span>Tire Service</span>
            </div>
            <div className="service-item-compact stagger-item delay-8">
              <Zap size={20} />
              <span>Electrical</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY REVVOO SECTION */}
      <section id="why-revvoo" className={`section-v2 why-section-v2 animate-on-scroll ${isVisible['why-revvoo'] ? 'visible' : ''}`}>
        <div className="section-container-v2">
          <h2 className="section-title-v2">The Revvoo Difference</h2>
          <div className="features-grid-v2">
            <div className="feature-card-v2 stagger-item delay-1">
              <div className="feature-icon-v2"><Clock size={22} /></div>
              <strong>15-30 Min Response</strong>
              <span>Mechanics dispatched to your location fast</span>
            </div>
            <div className="feature-card-v2 stagger-item delay-2">
              <div className="feature-icon-v2"><DollarSign size={22} /></div>
              <strong>Transparent Pricing</strong>
              <span>See costs upfront. Approve before work starts</span>
            </div>
            <div className="feature-card-v2 stagger-item delay-3">
              <div className="feature-icon-v2"><Star size={22} /></div>
              <strong>Verified Mechanics</strong>
              <span>Background-checked, insured, and reviewed</span>
            </div>
            <div className="feature-card-v2 stagger-item delay-4">
              <div className="feature-icon-v2"><Shield size={22} /></div>
              <strong>Satisfaction Guarantee</strong>
              <span>Not happy? We make it right</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section id="faq" className={`section-v2 faq-section-v2 animate-on-scroll ${isVisible['faq'] ? 'visible' : ''}`}>
        <div className="section-container-v2">
          <h2 className="section-title-v2">Frequently Asked Questions</h2>
          <div className="faq-list-v2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item-v2 ${openFaq === index ? 'open' : ''}`}
              >
                <button
                  className="faq-question-v2"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.q}</span>
                  <ChevronDown size={20} className="faq-chevron" />
                </button>
                <div className="faq-answer-v2">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR MECHANICS CTA SECTION */}
      <section id="mechanics-cta" className={`section-v2 mechanics-section-v2 animate-on-scroll ${isVisible['mechanics-cta'] ? 'visible' : ''}`}>
        <div className="section-container-v2">
          <div className="mechanics-content-v2">
            <span className="section-label light">For Mechanics</span>
            <h2 className="mechanics-title-v2">Grow Your Business with Revvoo</h2>
            <p className="mechanics-subtitle-v2">We send customers directly to you. No ads, no hassle.</p>

            <div className="mechanics-benefits-v2">
              <div className="benefit-item-v2 stagger-item delay-1">
                <DollarSign size={22} />
                <strong>85%</strong>
                <span>You keep of every job</span>
              </div>
              <div className="benefit-item-v2 stagger-item delay-2">
                <Users size={22} />
                <strong>Direct</strong>
                <span>Customers sent to you</span>
              </div>
              <div className="benefit-item-v2 stagger-item delay-3">
                <Calendar size={22} />
                <strong>Flexible</strong>
                <span>Work on your schedule</span>
              </div>
              <div className="benefit-item-v2 stagger-item delay-4">
                <Shield size={22} />
                <strong>$0</strong>
                <span>No upfront costs</span>
              </div>
            </div>

            <Link to="/mechanics" className="mechanics-cta-btn-v2">
              Become a Partner
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Problem Form Component
function ProblemForm({ onSubmit, onBack, initialProblem = '' }) {
  const { user } = useAuth();
  const [problem, setProblem] = useState(initialProblem);
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleMileage, setVehicleMileage] = useState('');
  const [urgency, setUrgency] = useState('can_wait');
  const [photos, setPhotos] = useState([]);
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [vehicleMode, setVehicleMode] = useState('manual'); // 'saved' or 'manual'

  // Fetch saved vehicles for logged-in users
  useEffect(() => {
    const fetchSavedVehicles = async () => {
      if (!user) return;
      try {
        const data = await supabaseFetch('vehicles', 'GET', null, `?user_id=eq.${user.id}&order=is_default.desc,created_at.desc`);
        if (data && data.length > 0) {
          setSavedVehicles(data);
          setVehicleMode('saved');
          // Auto-select default vehicle
          const defaultV = data.find(v => v.is_default);
          if (defaultV) {
            setSelectedVehicleId(defaultV.id);
            setVehicleYear(defaultV.year.toString());
            setVehicleMake(defaultV.make);
            setVehicleModel(defaultV.model);
            setVehicleMileage(defaultV.mileage ? defaultV.mileage.toString() : '');
          }
        }
      } catch (err) {
        console.error('Error fetching saved vehicles:', err);
      }
    };
    fetchSavedVehicles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleVehicleSelect = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    if (vehicleId === 'other') {
      setVehicleMode('manual');
      setVehicleYear('');
      setVehicleMake('');
      setVehicleModel('');
      setVehicleMileage('');
      return;
    }
    const v = savedVehicles.find(sv => sv.id === vehicleId);
    if (v) {
      setVehicleYear(v.year.toString());
      setVehicleMake(v.make);
      setVehicleModel(v.model);
      setVehicleMileage(v.mileage ? v.mileage.toString() : '');
    }
  };

  // Generate years from 2026 down to 1980
  const years = Array.from({ length: 47 }, (_, i) => 2026 - i);

  // Common vehicle makes (alphabetical, comprehensive)
  const makes = [
    'Acura', 'Alfa Romeo', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet',
    'Chrysler', 'Dodge', 'Fiat', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai',
    'Infiniti', 'Jaguar', 'Jeep', 'Kia', 'Land Rover', 'Lexus', 'Lincoln',
    'Mazda', 'Mercedes-Benz', 'Mini', 'Mitsubishi', 'Nissan', 'Pontiac',
    'Porsche', 'Ram', 'Saturn', 'Scion', 'Subaru', 'Suzuki', 'Tesla',
    'Toyota', 'Volkswagen', 'Volvo', 'Other'
  ];

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 3 - photos.length;
    const filesToAdd = files.slice(0, remainingSlots);

    filesToAdd.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPhotos(prev => {
            if (prev.length >= 3) return prev;
            return [...prev, {
              id: Date.now() + Math.random(),
              file: file,
              preview: event.target.result,
              name: file.name
            }];
          });
        };
        reader.readAsDataURL(file);
      }
    });

    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removePhoto = (photoId) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (problem.trim().length >= 20 && vehicleYear && vehicleMake && vehicleModel) {
      // Combine into car_info string
      const carInfo = `${vehicleYear} ${vehicleMake} ${vehicleModel}`;
      onSubmit({
        problem,
        carInfo,
        vehicleYear,
        vehicleMake,
        vehicleModel,
        vehicleMileage,
        urgency,
        photos
      });
    }
  };

  const isVehicleComplete = vehicleYear && vehicleMake && vehicleModel;
  const isProblemValid = problem.trim().length >= 20;

  return (
    <div className="problem-form-page">
      <div className="form-top-bar">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <div className="step-indicator">
          <span className="step-dot active"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
        </div>
        <span className="step-label">Step 1 of 5</span>
      </div>
      <div className="form-container">
        <h2 className="form-title">Tell Us What's Wrong</h2>
        <p className="form-subtitle">Describe your car problem and we'll match you with the right mechanic</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>What's the problem?</label>
            <textarea
              value={problem}
              onChange={(e) => setProblem(e.target.value)}
              placeholder="Describe what's happening — sounds, warning lights, when it started, what makes it worse..."
              rows={2}
              required
            />
            <div className="field-hint-row">
              {problem.trim().length > 0 && problem.trim().length < 20 && (
                <span className="field-error-text">Please describe your issue in more detail</span>
              )}
              <span className={`char-count ${problem.trim().length >= 20 ? 'valid' : ''}`}>
                {problem.trim().length >= 20 ? (
                  <><CheckCircle size={12} /> Good to go</>
                ) : (
                  <>{problem.trim().length}/20 characters</>
                )}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>Your Vehicle <span className="required-star">*</span></label>

            {/* Saved Vehicle Selector */}
            {savedVehicles.length > 0 && (
              <div className="saved-vehicle-selector">
                <div className="vehicle-select-wrapper">
                  <select
                    value={selectedVehicleId}
                    onChange={(e) => handleVehicleSelect(e.target.value)}
                    className={selectedVehicleId ? 'has-value' : ''}
                  >
                    <option value="">Select a saved vehicle</option>
                    {savedVehicles.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.year} {v.make} {v.model}{v.nickname ? ` — ${v.nickname}` : ''}
                      </option>
                    ))}
                    <option value="other">Enter a different vehicle</option>
                  </select>
                  <ChevronDown size={16} className="select-icon" />
                </div>
              </div>
            )}

            {/* Manual Entry Fields */}
            {(vehicleMode === 'manual' || savedVehicles.length === 0) && (
              <>
                <div className="vehicle-selectors-row">
                  <div className="vehicle-select-wrapper">
                    <select
                      value={vehicleYear}
                      onChange={(e) => setVehicleYear(e.target.value)}
                      required
                      className={vehicleYear ? 'has-value' : ''}
                    >
                      <option value="">Year</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="select-icon" />
                  </div>

                  <div className="vehicle-select-wrapper">
                    <select
                      value={vehicleMake}
                      onChange={(e) => setVehicleMake(e.target.value)}
                      required
                      className={vehicleMake ? 'has-value' : ''}
                    >
                      <option value="">Make</option>
                      {makes.map(make => (
                        <option key={make} value={make}>{make}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="select-icon" />
                  </div>
                </div>

                <div className="vehicle-input-wrapper" style={{ marginTop: '10px' }}>
                  <input
                    type="text"
                    value={vehicleModel}
                    onChange={(e) => setVehicleModel(e.target.value)}
                    placeholder="Model (e.g., Camry, Civic, F-150)"
                    required
                    className={vehicleModel ? 'has-value' : ''}
                  />
                </div>
              </>
            )}

            {isVehicleComplete && (
              <div className="vehicle-preview">
                <Car size={16} />
                <span>{vehicleYear} {vehicleMake} {vehicleModel}</span>
              </div>
            )}
          </div>

          <div className="form-section-divider" />

          <div className="form-group">
            <label>Mileage <span className="optional-label">(optional)</span></label>
            <div className="mileage-input-wrapper">
              <input
                type="text"
                value={vehicleMileage}
                onChange={(e) => setVehicleMileage(e.target.value)}
                placeholder="e.g., 85,000"
              />
              <span className="mileage-suffix">miles</span>
            </div>
          </div>

          <div className="form-group form-group-optional">
            <label>Photos <span className="optional-label">(optional)</span></label>

            <div className="photo-upload-area">
              {photos.length < 3 && (
                <label className="photo-upload-btn compact">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <Upload size={18} />
                  <span>Add photos</span>
                  <small>{photos.length}/3</small>
                </label>
              )}

              {photos.map(photo => (
                <div key={photo.id} className="photo-thumbnail">
                  <img src={photo.preview} alt={photo.name} />
                  <button
                    type="button"
                    className="photo-remove-btn"
                    onClick={() => removePhoto(photo.id)}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <p className="photo-helper-text">
              Helps mechanics diagnose faster and give accurate quotes
            </p>
          </div>

          <div className="form-section-divider" />

          <div className="form-group">
            <label>How urgent is this?</label>
            <div className="urgency-options">
              <button
                type="button"
                className={`urgency-btn ${urgency === 'can_wait' ? 'active' : ''}`}
                onClick={() => setUrgency('can_wait')}
              >
                <Clock size={18} />
                <span>Can Wait</span>
                <small>Within a week</small>
              </button>
              <button
                type="button"
                className={`urgency-btn ${urgency === '24_hours' ? 'active' : ''}`}
                onClick={() => setUrgency('24_hours')}
              >
                <Calendar size={18} />
                <span>24 Hours</span>
                <small>Need it soon</small>
              </button>
              <button
                type="button"
                className={`urgency-btn asap ${urgency === 'asap' ? 'active' : ''}`}
                onClick={() => setUrgency('asap')}
              >
                <Zap size={18} />
                <span>ASAP</span>
                <small>Emergency</small>
              </button>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={!isProblemValid || !isVehicleComplete}>
            Get AI Diagnosis
            <ArrowRight size={20} />
          </button>
        </form>
      </div>
    </div>
  );
}

// AI Diagnosis Component - Enhanced Educational Version
function AIDiagnosis({ problemData, diagnosis, loading, onContinue, onBack }) {
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'low': return 'severity-low';
      case 'medium': return 'severity-medium';
      case 'high': return 'severity-high';
      default: return 'severity-medium';
    }
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency?.toLowerCase()) {
      case 'can wait': return { class: 'urgency-low', text: 'Can Wait', icon: <Clock size={14} /> };
      case 'soon': return { class: 'urgency-medium', text: 'Address Soon', icon: <Clock size={14} /> };
      case 'urgent': return { class: 'urgency-high', text: 'Urgent', icon: <AlertTriangle size={14} /> };
      default: return { class: 'urgency-medium', text: 'Address Soon', icon: <Clock size={14} /> };
    }
  };

  const urgencyInfo = getUrgencyBadge(diagnosis?.urgency_level);

  if (loading) {
    return (
      <div className="diagnosis-page">
        <div className="loading-container diagnosis-loading">
          <div className="ai-loading-animation">
            <div className="ai-brain-icon">
              <Zap size={32} />
            </div>
            <div className="loading-pulse"></div>
          </div>
          <h2>Analyzing Your Vehicle...</h2>
          <p>Our AI is examining your {problemData.carInfo} symptoms</p>
          <div className="loading-steps">
            <span className="loading-step active">Analyzing symptoms</span>
            <span className="loading-step">Identifying likely causes</span>
            <span className="loading-step">Estimating costs</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="diagnosis-page">
      <div className="form-top-bar">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <div className="step-indicator">
          <span className="step-dot active"></span>
          <span className="step-line active"></span>
          <span className="step-dot active"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
        </div>
        <span className="step-label">Step 2 of 5</span>
      </div>

      <div className="diagnosis-container enhanced">
        {/* Status Header */}
        <div className="diagnosis-hero">
          <div className="diagnosis-status-line">
            <CheckCircle size={14} />
            <span>Analysis Complete</span>
          </div>
          <p className="diagnosis-subtitle">{problemData?.carInfo}</p>
        </div>

        {/* Main Diagnosis Card */}
        <div className="diagnosis-card premium">
          {/* Header: label + inline severity, then title */}
          <div className="diagnosis-issue-header">
            <div className="issue-label-row">
              <span className="issue-label">AI Diagnosis</span>
              <div className={`severity-pill ${getSeverityColor(diagnosis?.severity)}`}>
                {diagnosis?.severity || 'Medium'}
              </div>
            </div>
            <h3 className="issue-title">{diagnosis?.likely_issue || 'Unable to determine'}</h3>
          </div>

          {/* Stats - spec sheet rows */}
          <div className="diagnosis-specs">
            <div className="spec-row">
              <span className="spec-label"><DollarSign size={15} /> Estimated Cost</span>
              <span className="spec-value">{diagnosis?.estimated_cost || '$100 - $400'}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label"><Clock size={15} /> Repair Time</span>
              <span className="spec-value">{diagnosis?.time_estimate || '1-2 hours'}</span>
            </div>
            <div className={`spec-row spec-urgency ${urgencyInfo.class}`}>
              <span className="spec-label"><AlertTriangle size={15} /> Urgency</span>
              <span className="spec-value">{urgencyInfo.text}</span>
            </div>
          </div>

          {/* Explanation - styled callout, max 3 sentences */}
          <div className="diagnosis-callout">
            <p>
              {(() => {
                const text = diagnosis?.whats_happening || diagnosis?.explanation ||
                  "Your vehicle needs a professional inspection to pinpoint the exact issue.";
                const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
                return sentences.slice(0, 3).join('').trim();
              })()}
            </p>
          </div>

          {/* Tips */}
          <div className="diagnosis-tips-card">
            <span className="tips-label">Quick Tips</span>
            {(diagnosis?.quick_tips || diagnosis?.recommendations || [
              "Note when the issue occurs — cold starts, highway speeds, etc.",
              "Avoid long drives until a mechanic inspects it"
            ]).slice(0, 2).map((tip, i) => (
              <div className="tip-item" key={i}>
                <CheckCircle size={14} />
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <button className="continue-btn" onClick={onContinue}>
          Find Mechanics Near Me
          <ArrowRight size={20} />
        </button>

      </div>
    </div>
  );
}

// Service Type Selection Component
function ServiceTypeSelection({ onSelect, onBack }) {
  return (
    <div className="service-type-page">
      <div className="form-top-bar">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <div className="step-indicator">
          <span className="step-dot active"></span>
          <span className="step-line active"></span>
          <span className="step-dot active"></span>
          <span className="step-line active"></span>
          <span className="step-dot active"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
        </div>
        <span className="step-label">Step 3 of 5</span>
      </div>

      <div className="service-type-selection">
        <div className="selection-header">
          <h2>How would you like service?</h2>
          <p>Choose how you'd like to get your vehicle serviced</p>
        </div>

        <div className="service-type-cards">
          <button className="service-type-card" onClick={() => onSelect('mobile')}>
            <div className="service-type-card-header">
              <div className="service-type-icon mobile">
                <MapPin size={22} />
              </div>
              <h3>Mechanic comes to me</h3>
            </div>
            <p>A mechanic will travel to your location</p>
            <ul className="service-type-benefits">
              <li><CheckCircle size={14} /> Convenient — no need to leave home</li>
              <li><CheckCircle size={14} /> Service at your location</li>
              <li><CheckCircle size={14} /> ETA provided when accepted</li>
            </ul>
          </button>

          <button className="service-type-card" onClick={() => onSelect('shop')}>
            <div className="service-type-card-header">
              <div className="service-type-icon shop">
                <Wrench size={22} />
              </div>
              <h3>I'll go to a shop</h3>
            </div>
            <p>You drive to the mechanic's shop</p>
            <ul className="service-type-benefits">
              <li><CheckCircle size={14} /> Professional shop environment</li>
              <li><CheckCircle size={14} /> Full equipment available</li>
              <li><CheckCircle size={14} /> Address provided for directions</li>
            </ul>
          </button>
        </div>
      </div>
    </div>
  );
}

// Location Step Component - Collect address before showing mechanics (mobile only)
function LocationStep({ onLocationConfirmed, onBack }) {
  const [address, setAddress] = useState('');
  const [location, setLocation] = useState(null);
  const [detectedZone, setDetectedZone] = useState(null);
  const [noZoneError, setNoZoneError] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [geolocating, setGeolocating] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [externalLocation, setExternalLocation] = useState(null);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser.');
      return;
    }

    setGeolocating(true);
    setGeoError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Reverse geocode to get a formatted address
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const formattedAddress = results[0].formatted_address;
            const postalComponent = results[0].address_components?.find(c => c.types.includes('postal_code'));
            const zip = postalComponent?.short_name || null;
            setAddress(formattedAddress);
            setExternalLocation({ lat, lng });
            handleLocationSelect({ lat, lng, address: formattedAddress, zip });
          } else {
            setGeoError('Unable to determine your address. Please enter it manually.');
          }
          setGeolocating(false);
        });
      },
      (error) => {
        setGeolocating(false);
        if (error.code === error.PERMISSION_DENIED) {
          setGeoError('Location access denied. Please enter your address manually.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setGeoError('Unable to detect location. Please enter your address manually.');
        } else {
          setGeoError('Unable to detect location. Please enter your address manually.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleLocationSelect = ({ lat, lng, address: formattedAddress, zip: structuredZip }) => {
    setLocation({ lat, lng, address: formattedAddress });
    const zip = structuredZip || extractZipFromAddress(formattedAddress);
    const zone = zip ? getZoneFromZip(zip) : null;
    setDetectedZone(zone);
    setNoZoneError(!zone);
  };

  const handleContinue = () => {
    if (location && detectedZone) {
      const zip = extractZipFromAddress(location.address);
      onLocationConfirmed({ ...location, zip, zone: detectedZone });
    }
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    setWaitlistSubmitting(true);
    try {
      await supabaseFetch('waitlist', 'POST', {
        email: waitlistEmail.toLowerCase().trim(),
        created_at: new Date().toISOString()
      });
      setWaitlistSubmitted(true);
    } catch (err) {
      if (err.message?.includes('duplicate') || err.message?.includes('unique')) {
        setWaitlistSubmitted(true);
      }
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  return (
    <div className="location-step-page">
      <div className="form-top-bar">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <div className="step-indicator">
          <span className="step-dot active"></span>
          <span className="step-line active"></span>
          <span className="step-dot active"></span>
          <span className="step-line active"></span>
          <span className="step-dot active"></span>
          <span className="step-line active"></span>
          <span className="step-dot active"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
          <span className="step-line"></span>
          <span className="step-dot"></span>
        </div>
        <span className="step-label">Step 4 of 6</span>
      </div>

      <div className="location-step-card">
        <div className="location-step-hero-icon">
          <MapPin size={48} />
        </div>
        <div className="selection-header">
          <h2>Where do you need service?</h2>
          <p>Enter your address so we can find mechanics in your area</p>
        </div>

        <button
          type="button"
          className="use-my-location-btn"
          onClick={handleUseMyLocation}
          disabled={geolocating}
        >
          {geolocating ? (
            <>
              <Loader size={18} className="spin" />
              <span>Detecting your location...</span>
            </>
          ) : (
            <>
              <Crosshair size={18} />
              <span>Use My Location</span>
            </>
          )}
        </button>

        {geoError && (
          <p className="geo-error-msg">
            <AlertTriangle size={14} /> {geoError}
          </p>
        )}

        <div className="location-or-divider">
          <span>or enter address</span>
        </div>

        <LocationPicker
          address={address}
          onAddressChange={setAddress}
          onLocationSelect={handleLocationSelect}
          externalLocation={externalLocation}
        />

        {!location && !detectedZone && !noZoneError && (
          <div className="location-step-placeholder">
            <MapPin size={24} />
            <span>Use your location or type an address to get started</span>
          </div>
        )}

        {detectedZone && (
          <div className="detected-zone">
            <CheckCircle size={16} />
            <span>Service area: <strong>{detectedZone}</strong></span>
          </div>
        )}

        {noZoneError && location && (
          <div className="no-zone-error">
            <AlertTriangle size={20} />
            <h3>We're not in your area yet</h3>
            <p>Revvoo is currently serving the San Diego metro area. Join our waitlist to be the first to know when we expand to your neighborhood!</p>

            {waitlistSubmitted ? (
              <div className="waitlist-success">
                <CheckCircle size={16} />
                <span>You're on the list! We'll notify you when we expand to your area.</span>
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                <input
                  type="email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
                <button type="submit" disabled={waitlistSubmitting}>
                  {waitlistSubmitting ? <Loader size={16} className="spin" /> : 'Join Waitlist'}
                </button>
              </form>
            )}
          </div>
        )}

        {detectedZone && (
          <button
            className="location-continue-btn"
            onClick={handleContinue}
          >
            Find Mechanics
            <ArrowRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

// Mechanics List Component - Premium Redesign
function MechanicsList({ mechanics, loading, onSelectMechanic, onBack, diagnosis, serviceType, customerLocation }) {
  const [selectedCard, setSelectedCard] = useState(null);
  const [sortBy, setSortBy] = useState(customerLocation ? 'distance' : 'recommended');
  const [mechanicsWithDistance, setMechanicsWithDistance] = useState([]);
  const [distanceLoading, setDistanceLoading] = useState(false);

  // Fetch real distance & ETA via Google Maps Distance Matrix API
  useEffect(() => {
    if (!customerLocation || serviceType !== 'mobile' || mechanics.length === 0) {
      setMechanicsWithDistance(mechanics.map(m => ({ ...m, distance: null, duration: null })));
      return;
    }

    setDistanceLoading(true);

    try {
      const service = new window.google.maps.DistanceMatrixService();

      // Build origins from mechanic locations
      const origins = mechanics.map(m => {
        if (m.latitude && m.longitude) return new window.google.maps.LatLng(m.latitude, m.longitude);
        if (m.shop_address) return m.shop_address;
        return 'San Diego, CA';
      });

      const destination = new window.google.maps.LatLng(customerLocation.lat, customerLocation.lng);

      service.getDistanceMatrix({
        origins: origins,
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      }, (response, status) => {
        if (status === 'OK') {
          const enriched = mechanics.map((mechanic, i) => {
            const result = response.rows[i]?.elements[0];
            if (result?.status === 'OK') {
              return {
                ...mechanic,
                distance: parseFloat((result.distance.value / 1609.34).toFixed(1)),
                duration: Math.round(result.duration.value / 60),
                distanceText: result.distance.text,
                durationText: result.duration.text
              };
            }
            return { ...mechanic, distance: null, duration: null };
          });
          setMechanicsWithDistance(enriched);
        } else {
          console.error('Distance Matrix failed:', status);
          setMechanicsWithDistance(mechanics.map(m => ({ ...m, distance: null, duration: null })));
        }
        setDistanceLoading(false);
      });
    } catch (err) {
      console.error('Distance Matrix error:', err);
      setMechanicsWithDistance(mechanics.map(m => ({ ...m, distance: null, duration: null })));
      setDistanceLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mechanics, customerLocation, serviceType]);

  // Default service call fee
  const DEFAULT_SERVICE_CALL_FEE = 45;

  // Get estimated repair cost from AI diagnosis
  const getEstimatedRepairCost = () => {
    if (diagnosis?.estimated_cost) {
      const cost = diagnosis.estimated_cost;
      if (typeof cost === 'string' && cost.includes('-')) {
        return { min: parseInt(cost.split('-')[0].replace(/\D/g, '')), max: parseInt(cost.split('-')[1].replace(/\D/g, '')) };
      }
      return { min: 150, max: 200 };
    }
    return { min: 150, max: 200 };
  };

  const repairCost = getEstimatedRepairCost();

  const calculatePricing = (mechanic) => {
    const serviceCallFee = mechanic.service_call_fee || DEFAULT_SERVICE_CALL_FEE;
    const platformFee = Math.round(serviceCallFee * 0.15 * 100) / 100;
    const dueNow = serviceCallFee; // Customer pays exactly the mechanic's listed fee
    const totalMin = dueNow + repairCost.min;
    const totalMax = dueNow + repairCost.max;
    return { serviceCallFee, platformFee, dueNow, totalMin, totalMax };
  };

  // Use distance-enriched mechanics when available
  const displayMechanics = mechanicsWithDistance.length > 0 ? mechanicsWithDistance : mechanics;

  // Sort mechanics based on selected option
  const sortedMechanics = [...displayMechanics].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return (b.rating || 4.5) - (a.rating || 4.5);
      case 'distance':
        return (a.distance ?? 999) - (b.distance ?? 999);
      case 'price':
        return (a.service_call_fee || DEFAULT_SERVICE_CALL_FEE) - (b.service_call_fee || DEFAULT_SERVICE_CALL_FEE);
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="mechanics-page-v2">
        <div className="mechanics-loading">
          <div className="loading-pulse">
            <div className="pulse-ring"></div>
            <div className="pulse-ring"></div>
            <div className="pulse-ring"></div>
            <Car size={32} />
          </div>
          <h2>Finding your perfect mechanic</h2>
          <p>Matching you with top-rated pros nearby...</p>
          <div className="loading-progress">
            <div className="progress-bar"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mechanics-page-v2">
      {/* Step Indicator */}
      <div className="form-top-bar">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        {serviceType === 'mobile' ? (
          <>
            <div className="step-indicator">
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line"></span>
              <span className="step-dot"></span>
            </div>
            <span className="step-label">Step 5 of 6</span>
          </>
        ) : (
          <>
            <div className="step-indicator">
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line"></span>
              <span className="step-dot"></span>
            </div>
            <span className="step-label">Step 4 of 5</span>
          </>
        )}
      </div>
      <div className="mechanics-page-title">
        <h1>Choose Your Mechanic</h1>
        <p>{sortedMechanics.length} verified pro{sortedMechanics.length !== 1 ? 's' : ''} available{customerLocation ? ' in your area' : ''}</p>
      </div>

      {/* Pricing Info */}
      <div className="pricing-info-banner">
        <HelpCircle size={14} />
        <span>
          {PROMO_FIRST_SERVICE_CALL_ENABLED
            ? 'New customers pay 50% off the service call fee upfront. After inspection, your mechanic sends a detailed repair quote for your approval.'
            : 'You pay the service call fee upfront. After inspection, your mechanic will send a detailed repair quote for your approval before any work begins.'}
        </span>
      </div>

      {/* Sort Options */}
      <div className="sort-bar">
        <span className="sort-label">Sort by:</span>
        <div className="sort-options">
          {[
            { value: 'recommended', label: 'Recommended' },
            { value: 'rating', label: 'Top Rated' },
            { value: 'distance', label: 'Nearest' },
            { value: 'price', label: 'Best Value' }
          ].map((option) => (
            <button
              key={option.value}
              className={`sort-option ${sortBy === option.value ? 'active' : ''}`}
              onClick={() => setSortBy(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* No Mechanics Available */}
      {sortedMechanics.length === 0 && !loading && !distanceLoading && (
        <div className="no-mechanics-message">
          <MapPin size={32} />
          <h3>No mechanics available in your area right now</h3>
          <p>We're expanding! Check back soon or try a different service type.</p>
          <button className="back-btn-large" onClick={onBack}>
            <ArrowLeft size={18} />
            Go Back
          </button>
        </div>
      )}

      {/* Mechanics Grid */}
      <div className="mechanics-grid-v2">
        {sortedMechanics.map((mechanic, index) => {
          const pricing = calculatePricing(mechanic);
          const isSelected = selectedCard === mechanic.id;

          return (
            <div
              key={mechanic.id}
              className={`mechanic-card-v2 ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedCard(isSelected ? null : mechanic.id)}
            >
              {/* Featured Badge */}
              {index === 0 && sortBy === 'recommended' && (
                <div className="featured-badge">
                  <Sparkles size={12} />
                  Best Match
                </div>
              )}

              {/* Card Header */}
              <div className="card-header-v2">
                <div className="mechanic-profile">
                  <div className="profile-avatar">
                    {mechanic.profile_photo_url ? (
                      <img src={mechanic.profile_photo_url} alt={mechanic.business_name} className="avatar-img" />
                    ) : (
                      <span className="avatar-initials">
                        {(mechanic.business_name || 'M').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="profile-info">
                    <h3 className="mechanic-name-row">
                      {mechanic.available && <span className="name-online-dot"></span>}
                      {mechanic.business_name}
                    </h3>
                    <div className="rating-row">
                      <Star size={14} className="star-filled" />
                      <span className="rating-value">{(mechanic.rating || 4.5).toFixed(1)}</span>
                      <span className="rating-count">({mechanic.reviews_count || 0})</span>
                      {(mechanic.rating || 4.5) >= 4.8 && (
                        <span className="top-rated-tag">Top Rated</span>
                      )}
                    </div>
                    <div className="service-type-badges">
                      {mechanic.offers_mobile !== false && (
                        <span className="service-badge mobile"><Navigation size={12} /> Mobile</span>
                      )}
                      {mechanic.offers_shop && (
                        <span className="service-badge shop"><Building size={12} /> Shop</span>
                      )}
                    </div>
                    {mechanic.distance !== null && mechanic.duration !== null && (
                      <div className="mechanic-distance-info">
                        <span className="distance-badge">
                          <Navigation size={12} />
                          {mechanic.distance} mi away
                        </span>
                        <span className="eta-badge">
                          <Clock size={12} />
                          ~{mechanic.duration} min
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="price-tag">
                  {PROMO_FIRST_SERVICE_CALL_ENABLED ? (
                    <>
                      <span className="price-amount-discounted">
                        ${Math.round(pricing.serviceCallFee * PROMO_FIRST_SERVICE_DISCOUNT)}
                      </span>
                      <span className="price-amount-original">${pricing.serviceCallFee.toFixed(0)}</span>
                      <span className="price-label">new customer</span>
                    </>
                  ) : (
                    <>
                      <span className="price-amount">${pricing.serviceCallFee.toFixed(0)}</span>
                      <span className="price-label">service call</span>
                    </>
                  )}
                </div>
              </div>

              {/* Verified Badge */}
              <div className="quick-stats">
                <div className="stat">
                  <CheckCircle size={14} />
                  <span>Verified Pro</span>
                </div>
              </div>

              {/* Specialties */}
              <div className="specialties-v2">
                {(mechanic.specialties || ['General Repair', 'Brakes', 'Engine']).slice(0, 3).map((specialty, idx) => (
                  <span key={idx} className="specialty-chip">{specialty}</span>
                ))}
              </div>

              {/* Shop Address (for shop service type) */}
              {serviceType === 'shop' && mechanic.offers_shop && mechanic.shop_address && (
                <div className="shop-address-display">
                  <MapPin size={14} />
                  <span>{mechanic.shop_address}</span>
                </div>
              )}

              {/* Action Button */}
              <button
                className="select-btn-v2"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectMechanic(mechanic);
                }}
              >
                <span>Select & Continue</span>
                <ArrowRight size={18} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Trust Footer */}
      <div className="trust-footer">
        <div className="trust-item">
          <Shield size={14} />
          <span>Verified & insured</span>
        </div>
        <div className="trust-item">
          <CreditCard size={14} />
          <span>Pay only service call upfront</span>
        </div>
        <div className="trust-item">
          <CheckCircle size={14} />
          <span>Approve quote before work</span>
        </div>
      </div>
    </div>
  );
}

// Booking Confirmation Component
// Stripe Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#0f172a',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      '::placeholder': {
        color: '#94a3b8',
      },
      iconColor: '#64748b',
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
  hidePostalCode: true,
};

// Checkout Form Component (inside Elements provider)
function CheckoutForm({ customerInfo, total, serviceCallFee, onSuccess, onError, loading, setLoading }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const paymentInProgressRef = useRef(false); // H7 FIX: useRef guard against double-click

  // H11 FIX: Use refs so the Apple Pay paymentmethod handler always reads current values.
  // The handler is registered once in useEffect([stripe]) — without refs it would capture
  // stale closure values for serviceCallFee, total, customerInfo, onSuccess, and onError.
  const serviceCallFeeRef = useRef(serviceCallFee);
  const totalRef = useRef(total);
  const customerInfoRef = useRef(customerInfo);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => { serviceCallFeeRef.current = serviceCallFee; }, [serviceCallFee]);
  useEffect(() => { totalRef.current = total; }, [total]);
  useEffect(() => { customerInfoRef.current = customerInfo; }, [customerInfo]);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Set up Payment Request (Apple Pay / Google Pay)
  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Revvoo Service Call',
        amount: Math.round((serviceCallFee || total) * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
      }
    });

    // Handle the paymentmethod event from Apple Pay / Google Pay
    pr.on('paymentmethod', async (ev) => {
      setLoading(true);
      setCardError(null);

      // H11 FIX: Read current values from refs, not the stale closure
      const currentFee = serviceCallFeeRef.current;
      const currentTotal = totalRef.current;
      const currentCustomerInfo = customerInfoRef.current;

      try {
        // Create PaymentIntent on the backend
        const response = await fetch(`${SUPABASE_URL}/functions/v1/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            amount: Math.round((currentFee || currentTotal) * 100),
            currency: 'usd',
            customerEmail: ev.payerEmail || currentCustomerInfo.email,
            description: `Revvoo Service Call Fee - ${ev.payerName || currentCustomerInfo.name}`,
            captureMethod: 'automatic',
            paymentType: 'service_call_fee'
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create payment intent');
        }

        const { clientSecret, customerId: createdCustomerId } = await response.json();

        // Confirm the payment with the Apple Pay / Google Pay payment method
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
          setCardError(getStripeErrorMessage(confirmError));
          setLoading(false);
          return;
        }

        if (paymentIntent.status === 'requires_action') {
          const { error: actionError } = await stripe.confirmCardPayment(clientSecret);
          if (actionError) {
            ev.complete('fail');
            setCardError(getStripeErrorMessage(actionError));
            setLoading(false);
            return;
          }
        }

        ev.complete('success');

        // Save payment record
        if (paymentIntent.status === 'succeeded') {
          const paymentMethodId = paymentIntent.payment_method;
          let stripeCustomerId = createdCustomerId || paymentIntent.customer;

          // M6 FIX: Get last4 directly from the Apple Pay / Google Pay payment method event.
          // stripe.retrievePaymentMethod() does not exist in the browser Stripe.js SDK
          // and was silently failing. ev.paymentMethod.card contains the card details.
          const paymentMethodLast4 = ev.paymentMethod?.card?.last4 || null;

          try {
            await supabaseFetch('payments', 'POST', {
              payment_intent_id: paymentIntent.id,
              amount: currentFee || currentTotal,
              currency: 'usd',
              status: 'succeeded',
              customer_email: ev.payerEmail || currentCustomerInfo.email,
              customer_name: ev.payerName || currentCustomerInfo.name,
              payment_type: 'service_call_fee',
              created_at: new Date().toISOString()
            });
          } catch (saveError) {
            // Continue anyway - payment was successful
          }

          onSuccessRef.current(paymentIntent.id, paymentMethodId, paymentMethodLast4, stripeCustomerId);
        } else {
          setCardError('Payment was not completed. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        ev.complete('fail');
        console.error('Apple Pay / Google Pay error:', err);
        setCardError(getStripeErrorMessage(err));
        onErrorRef.current(err);
        setLoading(false);
      }
    });

    // Cleanup not needed — Stripe handles it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
  };

  const handlePayment = async () => {
    if (!stripe || !elements) {
      return;
    }

    // H7 FIX: useRef guard prevents double-charge from fast double-tap on mobile.
    // React state (setLoading) is async and may not have re-rendered yet on second tap.
    if (paymentInProgressRef.current) {
      return;
    }
    paymentInProgressRef.current = true;

    setLoading(true);
    setCardError(null);

    try {

      // Step 1: Create PaymentIntent on the backend
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          amount: Math.round((serviceCallFee || total) * 100), // Convert to cents
          currency: 'usd',
          customerEmail: customerInfo.email,
          description: `Revvoo Service Call Fee - ${customerInfo.name}`,
          captureMethod: 'automatic',
          paymentType: 'service_call_fee'
        }),
      });


      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const { clientSecret, customerId: createdCustomerId } = await response.json();

      // Step 2: Confirm the payment with card details
      const cardElement = elements.getElement(CardElement);

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone,
          },
        },
      });

      if (error) {
        setCardError(getStripeErrorMessage(error));
        setLoading(false);
        paymentInProgressRef.current = false;
        return;
      }


      // Step 3: Save payment record to Supabase
      if (paymentIntent.status === 'succeeded') {
        // Extract payment method details for future repair charges
        const paymentMethodId = paymentIntent.payment_method;
        let stripeCustomerId = createdCustomerId || paymentIntent.customer;

        // M6 FIX: stripe.retrievePaymentMethod() does not exist in the browser Stripe.js SDK
        // and was silently failing. For card form payments, last4 is not directly accessible
        // after confirmCardPayment(). The payment_method ID is sufficient for future charges.
        // last4 display ("Card ending in ****") is cosmetic and optional.
        const paymentMethodLast4 = null;

        try {
          await supabaseFetch('payments', 'POST', {
            payment_intent_id: paymentIntent.id,
            amount: serviceCallFee || total,
            currency: 'usd',
            status: 'succeeded',
            customer_email: customerInfo.email,
            customer_name: customerInfo.name,
            payment_type: 'service_call_fee',
            created_at: new Date().toISOString()
          });
        } catch (saveError) {
          // Continue anyway - payment was successful
        }

        onSuccess(paymentIntent.id, paymentMethodId, paymentMethodLast4, stripeCustomerId);
      } else {
        setCardError('Payment was not completed. Please try again.');
        setLoading(false);
        paymentInProgressRef.current = false;
      }
    } catch (err) {
      console.error('Payment flow error:', err);
      setCardError(getStripeErrorMessage(err));
      onError(err);
      setLoading(false);
      paymentInProgressRef.current = false;
    }
  };

  return (
    <div className="payment-section">
      <h4>
        <CreditCard size={20} />
        Pay Service Call Fee
      </h4>
      <p className="payment-note service-call-note">
        This ${serviceCallFee?.toFixed(2) || total.toFixed(2)} service call fee will be charged immediately.
        Repair costs are only charged after you approve the mechanic's quote.
      </p>

      {/* Apple Pay / Google Pay Button */}
      {paymentRequest && (
        <div className="payment-request-button-container">
          <PaymentRequestButtonElement
            options={{ paymentRequest, style: { paymentRequestButton: { height: '48px' } } }}
          />
          <div className="payment-divider">
            <span>or pay with card</span>
          </div>
        </div>
      )}

      <div className="card-element-container">
        <CardElement options={cardElementOptions} onChange={handleCardChange} />
      </div>

      {cardError && (
        <div className="card-error">
          <AlertTriangle size={16} />
          {cardError}
        </div>
      )}

      <div className="payment-security">
        <Shield size={14} />
        <span>Secured by Stripe. Your payment info is encrypted.</span>
      </div>

      <button
        type="button"
        className="confirm-btn service-call-btn"
        onClick={handlePayment}
        disabled={loading || !stripe || !cardComplete}
      >
        {loading ? (
          <>
            <Loader className="spinner" size={20} />
            Processing...
          </>
        ) : (
          <>
            <Lock size={20} />
            Pay ${total.toFixed(2)} Service Call Fee
          </>
        )}
      </button>
    </div>
  );
}

// Location Picker Component with Geocoding (no deprecated Autocomplete)
function LocationPicker({ address, onAddressChange, onLocationSelect, externalLocation }) {
  const [mapCenter, setMapCenter] = useState({ lat: 32.7157, lng: -117.1611 }); // San Diego default
  const [markerPosition, setMarkerPosition] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Show map when an external location is provided (e.g. from "Use My Location")
  useEffect(() => {
    if (externalLocation?.lat && externalLocation?.lng) {
      setMapCenter({ lat: externalLocation.lat, lng: externalLocation.lng });
      setMarkerPosition({ lat: externalLocation.lat, lng: externalLocation.lng });
      setShowMap(true);
      setGeocodeError('');
    }
  }, [externalLocation]);

  const mapContainerStyle = {
    width: '100%',
    height: '250px',
    borderRadius: '12px'
  };

  // Custom red dot marker icon
  const customMarkerIcon = {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">' +
      '<circle cx="11" cy="11" r="8" fill="%23DC2626" stroke="white" stroke-width="3"/>' +
      '<circle cx="11" cy="11" r="3" fill="white"/>' +
      '</svg>'
    ),
    scaledSize: window.google?.maps ? new window.google.maps.Size(22, 22) : null,
    anchor: window.google?.maps ? new window.google.maps.Point(11, 11) : null
  };

  // Clean minimal map style
  const mapStyles = [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'simplified' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e0f2fe' }] },
    { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#f8fafc' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e2e8f0' }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#f1f5f9' }] },
    { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#f8fafc' }] }
  ];

  // San Diego bounds for biasing autocomplete results
  const SD_BOUNDS = {
    south: 32.5,
    west: -117.4,
    north: 33.1,
    east: -116.8
  };

  // Initialize Google Places Autocomplete on mount
  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;
    if (autocompleteRef.current) return; // Already initialized

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'us' },
      bounds: SD_BOUNDS,
      fields: ['formatted_address', 'geometry', 'address_components'],
      types: ['address']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const formattedAddress = place.formatted_address || '';
      const postalComponent = place.address_components?.find(c => c.types.includes('postal_code'));
      const zip = postalComponent?.short_name || null;

      setMapCenter({ lat, lng });
      setMarkerPosition({ lat, lng });
      setShowMap(true);
      setGeocodeError('');
      onAddressChange(formattedAddress);
      onLocationSelect({ lat, lng, address: formattedAddress, zip });
    });

    autocompleteRef.current = autocomplete;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Geocode address using Google Maps JavaScript API Geocoder (manual fallback)
  const geocodeAddress = () => {
    if (!address || address.trim().length < 5) {
      setGeocodeError('Please enter a complete address');
      return;
    }

    setGeocoding(true);
    setGeocodeError('');

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        const formattedAddress = results[0].formatted_address;
        const postalComponent = results[0].address_components?.find(c => c.types.includes('postal_code'));
        const zip = postalComponent?.short_name || null;

        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setShowMap(true);
        onAddressChange(formattedAddress);
        onLocationSelect({ lat, lng, address: formattedAddress, zip });
      } else {
        console.error('Geocoding failed:', status);

        if (status === 'ZERO_RESULTS') {
          setGeocodeError('Could not find that address. Please try a more specific address.');
        } else if (status === 'OVER_QUERY_LIMIT') {
          setGeocodeError('API quota exceeded. Please try again later.');
        } else if (status === 'REQUEST_DENIED') {
          setGeocodeError('Location service unavailable. Please try again.');
        } else {
          setGeocodeError('Unable to find that address. Please try again.');
        }
      }

      setGeocoding(false);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      geocodeAddress();
    }
  };

  return (
    <div className="location-picker">
      <div className="location-input-group">
        <div className="input-with-icon">
          <MapPin size={20} />
          <input
            ref={inputRef}
            type="text"
            placeholder="Start typing an address..."
            value={address}
            onChange={(e) => {
              onAddressChange(e.target.value);
              setGeocodeError('');
            }}
            onKeyPress={handleKeyPress}
          />
        </div>
        <button
          type="button"
          className="find-location-btn"
          onClick={geocodeAddress}
          disabled={geocoding || !address}
        >
          {geocoding ? <Loader className="spinner" size={16} /> : <Search size={16} />}
          {geocoding ? 'Finding...' : 'Find'}
        </button>
      </div>

      {geocodeError && (
        <p className="geocode-error">
          <AlertTriangle size={14} /> {geocodeError}
        </p>
      )}

      {showMap && markerPosition && (
        <div className="location-map-container confirmed">
          <div className="location-map-wrapper">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={16}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                styles: mapStyles
              }}
            >
              <Marker
                position={markerPosition}
                icon={customMarkerIcon}
              />
            </GoogleMap>
          </div>
          <p className="map-instruction">
            <CheckCircle size={14} /> Not the right spot? Edit your address above.
          </p>
        </div>
      )}
    </div>
  );
}

function BookingConfirmation({ mechanic, problemData, diagnosis, onConfirm, onBack, loading, setLoading, confirmed, bookingError, selectedServiceType: passedServiceType, onMyBookings, customerLocation }) {
  // Service call fee model
  const DEFAULT_SERVICE_CALL_FEE = 45;
  const serviceCallFee = mechanic.service_call_fee || DEFAULT_SERVICE_CALL_FEE;

  // Get estimated repair cost from diagnosis
  const getEstimatedRepairCost = () => {
    if (diagnosis?.estimated_cost) {
      const cost = diagnosis.estimated_cost;
      if (typeof cost === 'string' && cost.includes('-')) {
        const min = parseInt(cost.split('-')[0].replace(/\D/g, ''));
        const max = parseInt(cost.split('-')[1].replace(/\D/g, ''));
        return { min, max, display: `$${min} - $${max}` };
      }
      if (typeof cost === 'number') {
        return { min: cost, max: cost, display: `$${cost}` };
      }
    }
    return { min: 100, max: 200, display: '$100 - $200' };
  };

  const estimatedRepair = getEstimatedRepairCost();

  // Calculate estimated arrival time: distance * 2.5 minutes
  const distance = mechanic.distance || 2.5;
  const estimatedMinutes = Math.round(distance * 2.5);

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: customerLocation?.address || '',
    latitude: customerLocation?.lat || null,
    longitude: customerLocation?.lng || null
  });
  const [formValid, setFormValid] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);
  const serviceType = passedServiceType || 'mobile';

  // Promo: new customer detection
  const [isNewCustomer, setIsNewCustomer] = useState(null); // null = not checked, true/false = result
  const [promoChecking, setPromoChecking] = useState(false);
  const lastCheckedEmailRef = useRef('');

  const promoApplied = PROMO_FIRST_SERVICE_CALL_ENABLED && isNewCustomer === true;
  const effectiveFee = promoApplied
    ? Math.round(serviceCallFee * PROMO_FIRST_SERVICE_DISCOUNT * 100) / 100
    : serviceCallFee;
  const platformFee = Math.round(effectiveFee * 0.15 * 100) / 100;
  const dueNow = effectiveFee;

  const checkIsNewCustomer = async (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!PROMO_FIRST_SERVICE_CALL_ENABLED || !emailRegex.test(email)) return;
    if (email === lastCheckedEmailRef.current) return; // Skip duplicate checks
    lastCheckedEmailRef.current = email;
    setPromoChecking(true);
    try {
      const existing = await supabaseFetch('bookings', 'GET', null,
        `?customer_email=eq.${encodeURIComponent(email)}&select=id&limit=1`);
      setIsNewCustomer(!existing || existing.length === 0);
    } catch {
      setIsNewCustomer(false); // Fail safe: don't apply discount if check fails
    } finally {
      setPromoChecking(false);
    }
  };

  // Form field validation errors
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldsTouched, setFieldsTouched] = useState({});

  const markTouched = (field) => {
    setFieldsTouched(prev => ({ ...prev, [field]: true }));
  };

  // Check if customer form is valid
  useEffect(() => {
    const { name, email, phone, address, latitude, longitude } = customerInfo;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    const phoneDigits = phone.replace(/\D/g, '');
    const errors = {};

    if (name.trim().length > 0 && name.trim().length < 2) errors.name = 'Name must be at least 2 characters';
    else if (name.trim() && !nameRegex.test(name.trim())) errors.name = 'Name can only contain letters and spaces';

    if (email && !emailRegex.test(email)) errors.email = 'Enter a valid email address';

    if (phone && phoneDigits.length > 0 && phoneDigits.length !== 10) errors.phone = 'Phone number must be 10 digits';

    if (serviceType === 'mobile' && address && address.trim().length > 0 && address.trim().length < 5) {
      errors.address = 'Address must be at least 5 characters';
    }

    setFieldErrors(errors);

    const nameValid = name.trim().length >= 2 && nameRegex.test(name.trim());
    const emailValid = emailRegex.test(email);
    const phoneValid = phoneDigits.length === 10;
    const addressValid = serviceType === 'shop' || (address.trim().length >= 5 && latitude && longitude);
    setFormValid(nameValid && emailValid && phoneValid && addressValid);
  }, [customerInfo, serviceType]);

  const handleLocationSelect = ({ lat, lng, address }) => {
    setCustomerInfo(prev => ({
      ...prev,
      address: address || prev.address,
      latitude: lat,
      longitude: lng
    }));
  };

  const handleContinueToPayment = (e) => {
    e.preventDefault();
    if (formValid) {
      setShowPayment(true);
    }
  };

  const handlePaymentSuccess = (paymentIntentId, stripePaymentMethodId, paymentMethodLast4, stripeCustomerId) => {
    // Google Ads conversion tracking — track actual charged amount
    window.gtag && window.gtag('event', 'conversion', {
      'send_to': 'AW-17945788401/LnqXCNWl_PgbEPH_m-1C',
      'value': dueNow,
      'currency': 'USD',
      'transaction_id': paymentIntentId
    });

    // Meta Pixel purchase tracking — track actual charged amount
    window.fbq && window.fbq('track', 'Purchase', {
      value: dueNow,
      currency: 'USD'
    });

    // Pass service call fee data along with booking info
    onConfirm(customerInfo, {
      serviceCallFee: effectiveFee,
      platformFee,
      dueNow,
      estimatedRepair,
      promoType: promoApplied ? 'first_service_50_off' : null,
      paymentMethodId: paymentIntentId,
      stripePaymentMethodId,
      paymentMethodLast4,
      stripeCustomerId
    });
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setInternalLoading(false);
  };

  if (bookingError) {
    return (
      <div className="success-page-wrapper">
        <div className="success-page-container">
          <div className="success-header-v2">
            <div className="success-icon-circle" style={{ background: '#fef2f2', color: '#dc2626' }}>
              <AlertTriangle size={64} />
            </div>
            <h2 className="success-title-v2">Booking Failed</h2>
            <p className="success-subtitle-v2">{bookingError}</p>
          </div>
          <button className="success-cta-primary" onClick={onBack} style={{ marginTop: '24px' }}>
            <ArrowLeft size={18} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="success-page-wrapper">
        <div className="success-page-container">

          {/* Success Header */}
          <div className="success-header-v2">
            <div className="success-icon-circle">
              <CheckCircle size={64} />
            </div>
            <h2 className="success-title-v2">Service Call Booked!</h2>
            <p className="success-subtitle-v2">Your mechanic is on the way to inspect your vehicle.</p>
          </div>

          {/* Payment Confirmed Banner */}
          <div className="success-payment-banner">
            <CheckCircle size={18} />
            <span>Payment Confirmed — ${dueNow.toFixed(2)} Service Call Fee{promoApplied && ' (50% new customer discount applied)'}</span>
          </div>

          {/* Mechanic Card */}
          <div className="success-card-section">
            <div className="booking-mechanic-card">
              <div className="booking-mechanic-avatar">
                <Wrench size={20} />
              </div>
              <div className="booking-mechanic-info">
                <h3>{mechanic.business_name}</h3>
                <div className="booking-mechanic-meta">
                  <Star className="star-icon" size={13} />
                  <span>{mechanic.rating || '5.0'}</span>
                  <span className="booking-meta-dot">·</span>
                  <span className="booking-service-type">{serviceType === 'mobile' ? 'Mobile Service' : 'Shop Service'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Details - Two Column */}
          <div className="success-card-section">
            <h4 className="success-section-title">Booking Details</h4>
            <div className="success-details-grid">
              <div className="success-detail-row">
                <span className="success-detail-label">Reported Issue</span>
                <span className="success-detail-value">{diagnosis?.likely_issue || problemData.problem || 'Car Repair'}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Vehicle</span>
                <span className="success-detail-value">{problemData.carInfo}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Estimated Repair</span>
                <span className="success-detail-value">{estimatedRepair.display}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Service Type</span>
                <span className="success-detail-value">{serviceType === 'mobile' ? 'Mobile Service' : 'Shop Visit'}</span>
              </div>
              <div className="success-detail-row last">
                <span className="success-detail-label">Est. Arrival</span>
                <span className="success-detail-value">~{estimatedMinutes} minutes</span>
              </div>
            </div>
          </div>

          {/* What Happens Next - Timeline */}
          <div className="success-card-section">
            <h4 className="success-section-title">What Happens Next</h4>
            <div className="success-timeline">
              <div className="timeline-step">
                <div className="timeline-node">
                  <span className="timeline-number">1</span>
                  <div className="timeline-line"></div>
                </div>
                <div className="timeline-content">
                  <strong>Mechanic Arrives & Inspects</strong>
                  <span>{mechanic.business_name} will come to your location and diagnose the issue on-site</span>
                </div>
              </div>
              <div className="timeline-step">
                <div className="timeline-node">
                  <span className="timeline-number">2</span>
                  <div className="timeline-line"></div>
                </div>
                <div className="timeline-content">
                  <strong>Receive Your Quote</strong>
                  <span>You'll get a detailed repair quote with transparent pricing — no surprises</span>
                </div>
              </div>
              <div className="timeline-step">
                <div className="timeline-node">
                  <span className="timeline-number">3</span>
                </div>
                <div className="timeline-content">
                  <strong>Approve & Get It Fixed</strong>
                  <span>Review the quote and only pay for repairs you approve. You're always in control</span>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="success-info-box">
            <Shield size={16} />
            <span>You'll receive a final repair quote before any work begins. You can approve or decline — no obligation.</span>
          </div>

          {/* CTA Buttons */}
          <div className="success-cta-group">
            <button className="success-cta-primary" onClick={onMyBookings}>
              View My Bookings
              <ArrowRight size={16} />
            </button>
            <button className="success-cta-secondary" onClick={() => window.location.href = '/'}>
              Back to Home
            </button>
            <p className="success-support-text">Questions? Contact us at revvoo.app@gmail.com</p>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <div className="form-top-bar">
        <button className="back-btn" onClick={showPayment ? () => setShowPayment(false) : onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        {serviceType === 'mobile' ? (
          <>
            <div className="step-indicator">
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
            </div>
            <span className="step-label">Step 6 of 6</span>
          </>
        ) : (
          <>
            <div className="step-indicator">
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
              <span className="step-line active"></span>
              <span className="step-dot active"></span>
            </div>
            <span className="step-label">Step 5 of 5</span>
          </>
        )}
      </div>

      <div className="booking-container-v2">
        <h2 className="booking-title-v2">
          {showPayment ? 'Complete Payment' : 'Confirm Your Booking'}
        </h2>

        {/* Mechanic Card */}
        <div className="booking-mechanic-card">
          <div className="booking-mechanic-avatar">
            <Wrench size={20} />
          </div>
          <div className="booking-mechanic-info">
            <h3>{mechanic.business_name}</h3>
            <div className="booking-mechanic-meta">
              <Star className="star-icon" size={13} />
              <span>{mechanic.rating?.toFixed(1) || '4.5'}</span>
              <span className="booking-meta-dot">·</span>
              <span className="booking-service-type">
                {serviceType === 'mobile' ? 'Mobile Service' : 'Shop Service'}
              </span>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="booking-pricing">
          <div className="booking-price-row booking-price-main">
            <div>
              <span className="booking-price-label">Due Now</span>
              <span className="booking-price-desc">
                Service Call Fee
                {promoApplied && <span className="promo-applied-tag">50% off - New Customer</span>}
                {promoChecking && <span className="promo-checking-text">Checking eligibility...</span>}
              </span>
            </div>
            <div className="booking-price-amount-group">
              {promoApplied && (
                <span className="booking-price-original">${serviceCallFee.toFixed(2)}</span>
              )}
              <span className="booking-price-amount">${dueNow.toFixed(2)}</span>
            </div>
          </div>
          <div className="booking-price-divider"></div>
          <div className="booking-price-row booking-price-estimate">
            <div>
              <span className="booking-price-label">After Inspection</span>
              <span className="booking-price-desc">Estimated Repair Cost</span>
            </div>
            <span className="booking-price-estimate-amount">{estimatedRepair.display}</span>
          </div>
          <div className="booking-estimate-note">
            <AlertCircle size={13} />
            <span>Your mechanic will provide a final quote after inspection. You approve before any work begins.</span>
          </div>
        </div>

        {!showPayment ? (
          <form onSubmit={handleContinueToPayment} className="booking-form-v2">
            <label className="booking-section-label">Your Information</label>
            <div className="booking-input-group">
              <div className={`booking-input-wrap ${fieldsTouched.name && fieldErrors.name ? 'input-error' : ''}`}>
                <User size={16} />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                  onBlur={() => markTouched('name')}
                  required
                />
              </div>
              {fieldsTouched.name && fieldErrors.name && <span className="field-error-text">{fieldErrors.name}</span>}
              <div className={`booking-input-wrap ${fieldsTouched.email && fieldErrors.email ? 'input-error' : ''}`}>
                <Mail size={16} />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={customerInfo.email}
                  onChange={(e) => { setCustomerInfo({...customerInfo, email: e.target.value}); setIsNewCustomer(null); lastCheckedEmailRef.current = ''; }}
                  onBlur={() => { markTouched('email'); checkIsNewCustomer(customerInfo.email); }}
                  required
                />
              </div>
              {fieldsTouched.email && fieldErrors.email && <span className="field-error-text">{fieldErrors.email}</span>}
              <div className={`booking-input-wrap ${fieldsTouched.phone && fieldErrors.phone ? 'input-error' : ''}`}>
                <Phone size={16} />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  onBlur={() => markTouched('phone')}
                  required
                />
              </div>
              {fieldsTouched.phone && fieldErrors.phone && <span className="field-error-text">{fieldErrors.phone}</span>}
            </div>

            {/* Address field for mobile service */}
            {serviceType === 'mobile' && (
              <div className="booking-location-section">
                <label className="booking-section-label">Service Location</label>
                <LocationPicker
                  address={customerInfo.address}
                  onAddressChange={(address) => setCustomerInfo({...customerInfo, address})}
                  onLocationSelect={handleLocationSelect}
                />
                {customerInfo.latitude && customerInfo.longitude && (
                  <p className="location-confirmed">
                    <CheckCircle size={14} /> Location confirmed
                  </p>
                )}
              </div>
            )}

            <button type="submit" className="booking-cta-btn" disabled={!formValid}>
              Continue to Payment
              <ArrowRight size={18} />
            </button>
          </form>
        ) : stripePromise ? (
          <Elements stripe={stripePromise}>
            <CheckoutForm
              customerInfo={customerInfo}
              total={dueNow}
              serviceCallFee={effectiveFee}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              loading={loading || internalLoading}
              setLoading={setLoading || setInternalLoading}
            />
          </Elements>
        ) : (
          <div className="stripe-error-message" style={{ padding: '20px', textAlign: 'center', color: '#DC2626' }}>
            <p>Payment processing is temporarily unavailable. Please try again later.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Booking Status Tracker Component
function BookingStatusTracker({ status, mechanicStep }) {
  const isServiceCallModel = ['pending_inspection', 'quote_sent', 'quote_approved', 'quote_declined'].includes(status) || mechanicStep;

  const standardStatuses = [
    { key: 'pending', label: 'Pending' },
    { key: 'accepted', label: 'Accepted' },
    { key: 'en_route', label: 'En Route' },
    { key: 'completed', label: 'Completed' }
  ];

  // When mechanic_step is active, use step-based progress
  const stepBasedStatuses = [
    { key: 'heading_to_customer', label: 'On the Way', stepKey: 'heading_to_customer' },
    { key: 'inspecting', label: 'Inspecting', stepKey: 'inspecting' },
    { key: 'waiting_approval', label: 'Quote', stepKey: 'waiting_approval' },
    { key: 'repairing', label: 'Repairing', stepKey: 'repairing' },
    { key: 'completing', label: 'Complete', stepKey: 'completing' },
  ];

  const serviceCallStatuses = [
    { key: 'pending', label: 'Booked' },
    { key: 'accepted', label: 'Accepted' },
    { key: 'pending_inspection', label: 'Inspecting' },
    { key: 'quote_sent', label: 'Quote Sent' },
    { key: 'quote_approved', label: 'Repairing' },
    { key: 'completed', label: 'Complete' }
  ];

  // Use step-based tracker if mechanic_step exists
  if (mechanicStep) {
    const stepIcons = [Truck, Search, FileText, Wrench, CheckCircle];
    const currentIdx = stepBasedStatuses.findIndex(s => s.stepKey === mechanicStep);
    const isComplete = status === 'completed' || mechanicStep === 'completing';

    return (
      <div className="tracker-v2 tracker-step-based">
        <div className="tracker-steps">
          {stepBasedStatuses.map((s, index) => {
            const isStepCompleted = index < currentIdx || isComplete;
            const isCurrent = index === currentIdx && !isComplete;
            const StepIcon = stepIcons[index];

            return (
              <div key={s.key} className="tracker-step-wrap">
                <div className="tracker-step">
                  <div className={`tracker-circle ${isStepCompleted ? 'done' : ''} ${isCurrent ? 'current' : ''}`}>
                    {isStepCompleted ? <CheckCircle size={14} /> : <StepIcon size={14} />}
                  </div>
                  <span className={`tracker-label ${isStepCompleted ? 'done' : ''} ${isCurrent ? 'current' : ''}`}>{s.label}</span>
                </div>
                {index < stepBasedStatuses.length - 1 && (
                  <div className={`tracker-line ${isStepCompleted ? 'done' : ''}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const statuses = isServiceCallModel ? serviceCallStatuses : standardStatuses;

  if (status === 'quote_declined') {
    return (
      <div className="tracker-v2">
        <div className="status-declined-notice">
          <X size={16} />
          <span>Quote declined - Service complete</span>
        </div>
      </div>
    );
  }

  const currentIndex = statuses.findIndex(s => s.key === status);

  return (
    <div className="tracker-v2">
      <div className="tracker-steps">
        {statuses.map((s, index) => {
          const isCompleted = index < currentIndex || (index === currentIndex && status === 'completed');
          const isCurrent = index === currentIndex && status !== 'completed';

          return (
            <div key={s.key} className="tracker-step-wrap">
              <div className="tracker-step">
                <div className={`tracker-circle ${isCompleted ? 'done' : ''} ${isCurrent ? 'current' : ''}`}>
                  {(isCompleted || (isCurrent && status === 'completed')) ? <CheckCircle size={14} /> : <span>{index + 1}</span>}
                </div>
                <span className={`tracker-label ${isCompleted ? 'done' : ''} ${isCurrent ? 'current' : ''}`}>{s.label}</span>
              </div>
              {index < statuses.length - 1 && (
                <div className={`tracker-line ${isCompleted ? 'done' : ''}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Repair Card Form - for entering a new card during quote approval
function RepairCardForm({ booking, onAuthorize, loading }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);

  // Set up Payment Request (Apple Pay / Google Pay) for repair authorization
  useEffect(() => {
    if (!stripe || !booking?.repair_quote) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Revvoo Repair',
        amount: Math.round(booking.repair_quote * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
      }
    });

    // Handle the paymentmethod event — pass the payment method to parent for authorization
    pr.on('paymentmethod', async (ev) => {
      // For repair authorization, we just need the payment method ID
      // The parent (handleApproveQuote) will call the authorize-repair-payment Edge Function
      ev.complete('success');
      onAuthorize(ev.paymentMethod.id);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, booking?.repair_quote]);

  const handleSubmit = async () => {
    if (!stripe || !elements || !cardComplete) return;

    setProcessing(true);
    setCardError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      // Create a payment method from the new card
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setCardError(getStripeErrorMessage(error));
        setProcessing(false);
        return;
      }

      // Pass the new payment method ID to the parent to authorize
      onAuthorize(paymentMethod.id);
    } catch (err) {
      setCardError(getStripeErrorMessage(err));
      setProcessing(false);
    }
  };

  return (
    <div className="repair-card-form">
      {/* Apple Pay / Google Pay Button */}
      {paymentRequest && (
        <div className="payment-request-button-container">
          <PaymentRequestButtonElement
            options={{ paymentRequest, style: { paymentRequestButton: { height: '48px' } } }}
          />
          <div className="payment-divider">
            <span>or pay with card</span>
          </div>
        </div>
      )}

      <div className="card-element-container">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1a1a1a',
                '::placeholder': { color: '#999' },
              },
              invalid: { color: '#dc2626' },
            },
          }}
          onChange={(e) => {
            setCardError(e.error ? e.error.message : null);
            setCardComplete(e.complete);
          }}
        />
      </div>
      {cardError && (
        <div className="card-error" style={{ marginTop: '8px', color: '#dc2626', fontSize: '14px' }}>
          <AlertTriangle size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
          {cardError}
        </div>
      )}
      <button
        className="btn-approve-quote"
        onClick={handleSubmit}
        disabled={!cardComplete || processing || loading}
        style={{ marginTop: '12px' }}
      >
        {processing || loading ? (
          <>
            <Loader className="spinner" size={20} />
            Authorizing...
          </>
        ) : (
          <>
            <Lock size={18} />
            Approve & Authorize ${booking.repair_quote?.toFixed(2)}
          </>
        )}
      </button>
    </div>
  );
}

// Customer Dashboard Component
function CustomerDashboard({ bookings, loading, onRefresh, onSilentRefresh, userReviews = [], onLeaveReview, onEditReview }) {
  const [activeTab, setActiveTab] = useState('bookings');
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalResult, setApprovalResult] = useState(null); // { type: 'approved' | 'declined', booking }
  const [useNewCard, setUseNewCard] = useState({}); // { [bookingId]: true/false }
  const [approvalError, setApprovalError] = useState(null);

  // Auto-refresh bookings silently only when there's a passive waiting step
  // (mechanic is doing something, customer is just watching progress)
  useEffect(() => {
    const passiveSteps = ['heading_to_customer', 'inspecting', 'repairing'];
    const hasPassiveStep = bookings.some(b =>
      b.mechanic_step &&
      passiveSteps.includes(b.mechanic_step) &&
      !['completed', 'cancelled', 'declined'].includes(b.status)
    );
    if (!hasPassiveStep) return;
    const silentFn = onSilentRefresh || onRefresh;
    const interval = setInterval(() => {
      silentFn();
    }, 15000);
    return () => clearInterval(interval);
  }, [bookings, onRefresh, onSilentRefresh]);

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: 'Pending', color: 'orange', description: 'Waiting for mechanic to accept' },
      accepted: { label: 'Accepted', color: 'green', description: 'Mechanic confirmed your booking' },
      en_route: { label: 'En Route', color: 'blue', description: 'Mechanic is on the way' },
      pending_inspection: { label: 'Inspection', color: 'purple', description: 'Mechanic is inspecting your vehicle' },
      quote_sent: { label: 'Quote Ready', color: 'orange', description: 'Review and approve the repair quote' },
      quote_approved: { label: 'Approved', color: 'green', description: 'Repair in progress' },
      quote_declined: { label: 'Declined', color: 'red', description: 'Quote was declined' },
      quote_expired: { label: 'Expired', color: 'amber', description: 'Quote expired before response' },
      completed: { label: 'Completed', color: 'green', description: 'Service completed' },
      declined: { label: 'Declined', color: 'red', description: 'Your mechanic was unable to accept. Service call fee has been refunded.' },
      cancelled: { label: 'Cancelled', color: 'gray', description: 'Booking cancelled. Service call fee has been refunded.' }
    };
    return statusMap[status] || statusMap.pending;
  };

  // Handle quote approval — authorize repair payment on saved card
  const handleApproveQuote = async (booking, newPaymentMethodId = null) => {
    const paymentMethodId = newPaymentMethodId || booking.stripe_payment_method_id;

    if (!paymentMethodId) {
      setApprovalError('No payment method on file. Please add a card.');
      setUseNewCard(prev => ({ ...prev, [booking.id]: true }));
      return;
    }

    setApprovalLoading(true);
    setApprovalError(null);

    try {
      // Step 1: Create the repair payment intent via Edge Function (no server-side confirm)
      const repairAmountCents = Math.round(booking.repair_quote * 100);
      const authResponse = await fetch(`${SUPABASE_URL}/functions/v1/authorize-repair-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          amount: repairAmountCents,
          payment_method_id: paymentMethodId,
          customer_id: booking.stripe_customer_id || null,
          booking_id: booking.id,
          description: booking.repair_description || booking.problem_description || 'Vehicle Repair',
          customer_email: booking.customer_email,
        }),
      });

      const authResult = await authResponse.json();

      if (!authResponse.ok || !authResult.success) {
        throw new Error(authResult.error || 'Card authorization failed. Your card may have been declined.');
      }

      // C8 FIX: Confirm the payment on the frontend to handle 3DS/SCA
      // The Edge Function no longer uses confirm:true, so the PI is in 'requires_confirmation'.
      // We use stripe.confirmCardPayment() which handles 3DS challenges in the browser.
      const stripeInstance = await stripePromise;
      if (!stripeInstance) {
        throw new Error('Payment processing is temporarily unavailable. Please try again.');
      }

      const { error: confirmError, paymentIntent: confirmedPI } = await stripeInstance.confirmCardPayment(
        authResult.client_secret,
        { payment_method: paymentMethodId }
      );

      if (confirmError) {
        throw new Error(confirmError.message || 'Card authorization failed. Your card may have been declined.');
      }

      // After confirmation, PI should be in 'requires_capture' (manual capture mode)
      if (confirmedPI.status !== 'requires_capture') {
        throw new Error(`Unexpected payment status: ${confirmedPI.status}. Please try again.`);
      }

      // Step 2: Update booking with repair payment intent and status
      await supabaseFetch('bookings', 'PATCH', {
        repair_quote_approved: true,
        repair_amount_authorized: true,
        repair_payment_intent_id: authResult.payment_intent_id,
        quote_approved_at: new Date().toISOString(),
        status: 'quote_approved',
        // If a new card was used, save it
        ...(newPaymentMethodId ? { stripe_payment_method_id: newPaymentMethodId } : {}),
      }, `?id=eq.${booking.id}`);

      // Step 3: Create payment record for the authorization
      try {
        await supabaseFetch('payments', 'POST', {
          booking_id: booking.id,
          payment_intent_id: authResult.payment_intent_id,
          amount: booking.repair_quote,
          currency: 'usd',
          status: 'authorized',
          payment_type: 'repair_authorization',
          customer_email: booking.customer_email,
          customer_name: booking.customer_name,
          created_at: new Date().toISOString()
        });
      } catch (paymentRecordError) {
        console.error('Failed to save payment record:', paymentRecordError);
      }

      // Step 4: Send email notification to mechanic
      try {
        const mechanicData = await supabaseFetch('mechanics', 'GET', null, `?id=eq.${booking.mechanic_id}&select=email,business_name`);
        const mechanicEmail = mechanicData?.[0]?.email;
        const mechanicName = mechanicData?.[0]?.business_name || booking.mechanic_name || 'Mechanic';

        if (mechanicEmail) {
          await sendEmail('quote_approved', mechanicEmail, {
            mechanicName: mechanicName,
            customerName: booking.customer_name || 'Customer',
            vehicle: booking.car_info || 'Vehicle',
            approvedAmount: booking.repair_quote || 0,
            dashboardUrl: 'https://www.revvoo.app/mechanic-dashboard'
          });
        }
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      setApprovalResult({ type: 'approved', booking });
      setUseNewCard(prev => ({ ...prev, [booking.id]: false }));
      onRefresh();
    } catch (error) {
      console.error('Error approving quote:', error);
      setApprovalError(error.message);
    }
    setApprovalLoading(false);
  };

  // Handle quote decline
  const handleDeclineQuote = async (booking) => {
    if (!window.confirm('Decline this repair quote? Your service call fee has already been charged for the inspection.')) {
      return;
    }

    setApprovalLoading(true);
    try {
      await supabaseFetch('bookings', 'PATCH', {
        repair_quote_approved: false,
        status: 'quote_declined'
      }, `?id=eq.${booking.id}`);

      // Send email notification to mechanic
      try {
        const mechanicData = await supabaseFetch('mechanics', 'GET', null, `?id=eq.${booking.mechanic_id}&select=email,business_name`);
        const mechanicEmail = mechanicData?.[0]?.email;
        const mechanicName = mechanicData?.[0]?.business_name || booking.mechanic_name || 'Mechanic';

        if (mechanicEmail) {
          await sendEmail('quote_declined', mechanicEmail, {
            mechanicName: mechanicName,
            customerName: booking.customer_name || 'Customer',
            vehicle: booking.car_info || 'Vehicle',
            quoteAmount: booking.repair_quote || 0
          });
        }
      } catch (emailError) {
        console.error('Quote decline notification email failed:', emailError);
      }

      setApprovalResult({ type: 'declined', booking });
      onRefresh();
    } catch (error) {
      console.error('Error declining quote:', error);
      alert(`Failed to decline quote: ${error.message}`);
    }
    setApprovalLoading(false);
  };

  const [cancellingBookingId, setCancellingBookingId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelModalBooking, setCancelModalBooking] = useState(null);
  // M11 FIX: Use useRef instead of useState for the expired quote ID set.
  // useState(() => new Set()) gets re-initialized when React StrictMode double-mounts,
  // causing the PATCH to fire twice. useRef persists across StrictMode remounts.
  const expiredQuoteIdsRef = useRef(new Set());

  // Quote countdown timer — ticks every second for live countdown
  const [quoteCountdowns, setQuoteCountdowns] = useState({});
  useEffect(() => {
    const quoteSentBookings = bookings.filter(b => b.status === 'quote_sent' && b.quote_expires_at);
    if (quoteSentBookings.length === 0) return;
    const interval = setInterval(() => {
      const updated = {};
      quoteSentBookings.forEach(b => {
        updated[b.id] = getTimeRemaining(b.quote_expires_at);
      });
      setQuoteCountdowns(updated);
    }, 1000);
    return () => clearInterval(interval);
  }, [bookings]);

  // No-show check — recalculate every 30 seconds
  const [noShowFlags, setNoShowFlags] = useState({});
  useEffect(() => {
    const enRouteBookings = bookings.filter(b =>
      b.mechanic_step === 'heading_to_customer' &&
      b.accepted_at &&
      b.estimated_arrival &&
      b.service_type !== 'shop' &&
      !['completed', 'cancelled', 'declined'].includes(b.status)
    );
    if (enRouteBookings.length === 0) return;
    const check = () => {
      const flags = {};
      enRouteBookings.forEach(b => {
        flags[b.id] = checkNoShowThreshold(b.accepted_at, b.estimated_arrival);
      });
      setNoShowFlags(flags);
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [bookings]);

  // Open cancel confirmation modal
  const openCancelModal = (booking) => {
    setCancelModalBooking(booking);
    setShowCancelModal(true);
  };

  // Handle customer cancel booking (called from ConfirmModal)
  const handleCancelBooking = async (booking) => {
    const target = booking || cancelModalBooking;
    if (!target) return;

    setCancellingBookingId(target.id);
    try {
      // Update booking status
      await supabaseFetch('bookings', 'PATCH', {
        status: 'cancelled'
      }, `?id=eq.${target.id}`);

      // Refund service call fee
      let refundFailed = false;
      if (target.payment_intent_id) {
        try {
          const refundRes = await fetch(`${SUPABASE_URL}/functions/v1/refund-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({ paymentIntentId: target.payment_intent_id }),
          });
          const refundResult = await refundRes.json();
          if (!refundResult.success) refundFailed = true;
        } catch (refundError) {
          console.error('Refund failed:', refundError);
          refundFailed = true;
        }
      }

      // Also cancel repair authorization if one exists
      if (target.repair_payment_intent_id) {
        try {
          const repairRefundRes = await fetch(`${SUPABASE_URL}/functions/v1/refund-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({ paymentIntentId: target.repair_payment_intent_id }),
          });
          const repairRefundResult = await repairRefundRes.json();
          if (!repairRefundResult.success) refundFailed = true;
        } catch (refundError) {
          console.error('Repair authorization cancel failed:', refundError);
          refundFailed = true;
        }
      }

      // Send email to mechanic if one is assigned
      if (target.mechanic_id) {
        try {
          const mechanicData = await supabaseFetch('mechanics', 'GET', null, `?id=eq.${target.mechanic_id}&select=email,business_name`);
          const mechanicEmail = mechanicData?.[0]?.email;
          const mechanicName = mechanicData?.[0]?.business_name || target.mechanic_name || 'Mechanic';

          if (mechanicEmail) {
            await sendEmail('booking_cancelled', mechanicEmail, {
              mechanicName: mechanicName,
              customerName: target.customer_name || 'Customer',
              vehicle: target.car_info || 'Vehicle'
            });
          }
        } catch (emailError) {
          console.error('Cancellation notification email failed:', emailError);
        }
      }

      setShowCancelModal(false);
      setCancelModalBooking(null);

      if (refundFailed) {
        alert('Booking cancelled, but there was an issue processing your refund. Please contact support if you don\'t see it within 5-10 business days.');
      }

      onRefresh();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Failed to cancel booking. Please try again.');
    } finally {
      setCancellingBookingId(null);
    }
  };

  const hasReviewedBooking = (bookingId) => {
    return userReviews.some(r => r.booking_id === bookingId);
  };

  const completedBookings = bookings.filter(b => b.status === 'completed');
  const needsReview = completedBookings.filter(b => !hasReviewedBooking(b.id));

  const [refreshSpin, setRefreshSpin] = useState(false);
  const handleRefresh = () => {
    setRefreshSpin(true);
    onRefresh();
    setTimeout(() => setRefreshSpin(false), 800);
  };

  return (
    <div className="mybookings-page">
      <div className="mybookings-container">

        {/* Header */}
        <div className="mybookings-header">
          <h2 className="mybookings-title">My Bookings</h2>
          <button className="mybookings-refresh" onClick={handleRefresh} title="Refresh">
            <Loader size={18} className={refreshSpin ? 'spin-icon' : ''} />
          </button>
        </div>

        {/* Quote Pending Approval Banner - URGENT */}
        {bookings.filter(b => b.status === 'quote_sent').length > 0 && (
          <div className="quote-pending-banner">
            <div className="banner-icon-pulse">
              <DollarSign size={28} />
            </div>
            <div className="banner-content">
              <div className="banner-text">
                <strong>Repair Quote Ready!</strong>
                <span>Your mechanic has sent a repair quote. Review and approve to begin work.</span>
              </div>
            </div>
          </div>
        )}

        {/* Approval Result Modal */}
        {approvalResult && (
          <div className="approval-result-overlay" onClick={() => setApprovalResult(null)}>
            <div className="approval-result-modal" onClick={e => e.stopPropagation()}>
              {approvalResult.type === 'approved' ? (
                <>
                  <div className="result-icon success">
                    <CheckCircle size={48} />
                  </div>
                  <h3>Repair Approved!</h3>
                  <p>Your mechanic will begin work on your vehicle now.</p>
                  <div className="result-amount">
                    <span>Authorized Amount</span>
                    <strong>${approvalResult.booking.repair_quote?.toFixed(2)}</strong>
                  </div>
                  <p className="result-note">You will be charged once the repair is complete.</p>
                </>
              ) : (
                <>
                  <div className="result-icon declined">
                    <X size={48} />
                  </div>
                  <h3>Quote Declined</h3>
                  <p>Your service call fee (${(approvalResult.booking.service_call_fee || 45).toFixed(2)}) covered the inspection.</p>
                  <p className="result-note">The mechanic will wrap up and leave. No additional charges.</p>
                </>
              )}
              <button className="btn-close-result" onClick={() => setApprovalResult(null)}>
                Got It
              </button>
            </div>
          </div>
        )}

        {/* Review Prompt Banner - Slim */}
        {needsReview.length > 0 && !bookings.some(b => b.status === 'quote_sent') && (
          <div className="review-prompt-v2">
            <div className="review-prompt-left">
              <Star size={18} />
              <span>Rate your recent service with <strong>{needsReview[0].mechanic_name || 'your mechanic'}</strong></span>
            </div>
            <button className="review-prompt-btn" onClick={() => onLeaveReview(needsReview[0])}>
              Leave Review
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="mybookings-tabs">
          <button
            className={`mybookings-tab ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            Bookings ({bookings.length})
          </button>
          <button
            className={`mybookings-tab ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Your Reviews ({userReviews.length})
          </button>
        </div>

        {activeTab === 'bookings' && (
          <>
            {loading ? (
              <div className="mybookings-loading">
                <Loader className="spinner" size={28} />
                <p>Loading your bookings...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="mybookings-empty">
                <div className="mybookings-empty-icon">
                  <Wrench size={36} />
                </div>
                <h3>No bookings yet</h3>
                <p>When you book a service, it will show up here.</p>
                <button className="mybookings-empty-cta" onClick={() => window.location.href = '/'}>
                  Book Your First Service
                  <ArrowRight size={16} />
                </button>
              </div>
            ) : (
              <div className="mybookings-list">
                {bookings.map((booking) => {
                  const statusInfo = getStatusInfo(booking.status);
                  const canReview = booking.status === 'completed' && !hasReviewedBooking(booking.id);
                  const dateStr = new Date(booking.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

                  return (
                    <div key={booking.id} className="bcard">
                      {/* Card Header */}
                      <div className="bcard-header">
                        <div className="bcard-vehicle">
                          <Car size={16} />
                          <span>{booking.car_info || 'Vehicle'}</span>
                        </div>
                        <span className={`bcard-badge badge-${booking.status === 'pending' ? 'gray' : booking.status === 'accepted' ? 'blue' : booking.status === 'en_route' ? 'amber' : booking.status === 'completed' ? 'green' : booking.status === 'declined' || booking.status === 'quote_declined' ? 'red' : booking.status === 'cancelled' ? 'gray' : booking.status === 'quote_expired' ? 'amber' : statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>

                      {/* Status Tracker */}
                      <BookingStatusTracker status={booking.status} mechanicStep={booking.mechanic_step} />

                      {/* Mechanic Step Status Detail */}
                      {booking.mechanic_step && !['completed', 'cancelled', 'declined', 'quote_declined'].includes(booking.status) && (
                        <div className="customer-step-detail">
                          {booking.mechanic_step === 'heading_to_customer' && (
                            <>
                              <div className="step-detail-card on-the-way">
                                <Truck size={24} />
                                <div>
                                  <strong>Your mechanic is on the way!</strong>
                                  <p>{booking.mechanic_name || 'Your mechanic'}{booking.estimated_arrival ? ` - ETA: ${booking.estimated_arrival}` : ''}</p>
                                </div>
                              </div>
                              {/* No-show warning — mobile service only */}
                              {noShowFlags[booking.id]?.exceeded && booking.service_type !== 'shop' && (
                                <div className="no-show-warning-card">
                                  <div className="no-show-warning-header">
                                    <AlertTriangle size={22} />
                                    <div>
                                      <strong>Your mechanic hasn't arrived yet</strong>
                                      <p>They're {noShowFlags[booking.id].minutesOverdue} min past the expected arrival time.</p>
                                    </div>
                                  </div>
                                  <button
                                    className="no-show-cancel-btn"
                                    onClick={() => openCancelModal(booking)}
                                    disabled={cancellingBookingId === booking.id}
                                  >
                                    <X size={16} />
                                    Cancel & Get Full Refund
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                          {booking.mechanic_step === 'inspecting' && (
                            <div className="step-detail-card inspecting">
                              <Search size={24} />
                              <div>
                                <strong>Your mechanic is inspecting your vehicle</strong>
                                <p>You'll receive a detailed quote shortly.</p>
                              </div>
                            </div>
                          )}
                          {(booking.mechanic_step === 'quote_sent' || booking.mechanic_step === 'waiting_approval') && booking.status !== 'quote_sent' && (
                            <div className="step-detail-card quote-ready">
                              <FileText size={24} />
                              <div>
                                <strong>Quote sent</strong>
                                <p>Review the quote below and approve to begin repair.</p>
                              </div>
                            </div>
                          )}
                          {booking.mechanic_step === 'repairing' && (
                            <div className="step-detail-card repairing">
                              <Wrench size={24} />
                              <div>
                                <strong>Repair in progress</strong>
                                <p>Your mechanic is working on your vehicle. Approved: ${(booking.repair_quote || 0).toFixed(2)}</p>
                              </div>
                            </div>
                          )}
                          {booking.mechanic_step === 'completing' && (
                            <div className="step-detail-card complete">
                              <CheckCircle size={24} />
                              <div>
                                <strong>Job complete!</strong>
                                <p>Your vehicle is ready. Final charge: ${((booking.service_call_fee || 0) + (booking.repair_quote || 0)).toFixed(2)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Details */}
                      <div className="bcard-details">
                        {booking.mechanic_name && (
                          <div className="bcard-mechanic">
                            <div className="bcard-mechanic-icon">
                              <Wrench size={14} />
                            </div>
                            <span>{booking.mechanic_name}</span>
                          </div>
                        )}
                        <div className="bcard-meta-row">
                          <span className="bcard-date">
                            <Calendar size={13} />
                            {dateStr}
                          </span>
                          <span className="bcard-price">${(booking.service_call_fee || booking.total_cost || 0).toFixed(2)}</span>
                        </div>
                        <p className="bcard-problem">{booking.problem_description || 'No description'}</p>
                      </div>

                      {/* QUOTE APPROVAL SECTION */}
                      {booking.status === 'quote_sent' && booking.repair_quote && (() => {
                        const secsLeft = quoteCountdowns[booking.id] ?? getTimeRemaining(booking.quote_expires_at);
                        const isExpired = secsLeft !== null && secsLeft <= 0;
                        if (isExpired) {
                          // Auto-update status to quote_expired (only once per booking)
                          if (!expiredQuoteIdsRef.current.has(booking.id)) {
                            expiredQuoteIdsRef.current.add(booking.id);
                            supabaseFetch('bookings', 'PATCH', { status: 'quote_expired', mechanic_step: null }, `?id=eq.${booking.id}`).catch(() => {});
                          }
                          return (
                            <div className="quote-expired-section" style={{ background: '#FEF3C7', borderRadius: '12px', padding: '20px', marginTop: '12px' }}>
                              <p style={{ margin: '0 0 8px', color: '#92400E', fontWeight: 600, fontSize: '15px' }}>This quote has expired.</p>
                              <p style={{ margin: 0, color: '#92400E', fontSize: '14px' }}>The 30-minute window to respond has passed.</p>
                            </div>
                          );
                        }
                        return (
                        <div className="quote-approval-section">
                          <div className="quote-ready-banner">
                            <div className="quote-ready-icon">
                              <DollarSign size={28} />
                            </div>
                            <div className="quote-ready-text">
                              <h4>Repair Quote Ready!</h4>
                              <p>Review and approve to begin repair</p>
                            </div>
                          </div>
                          {secsLeft !== null && (
                            <div className={`quote-countdown-timer ${secsLeft < 300 ? 'urgent' : ''}`}>
                              <Clock size={16} />
                              <span>Expires in <strong>{formatCountdown(secsLeft)}</strong></span>
                            </div>
                          )}

                          <div className="quote-details-box">
                            <div className="quote-from">
                              <span>Repair Quote from</span>
                              <strong>{booking.mechanic_name || 'Your Mechanic'}</strong>
                            </div>

                            {booking.repair_description && (
                              <div className="quote-repair-desc">
                                <label>What needs to be fixed:</label>
                                <p>{booking.repair_description}</p>
                              </div>
                            )}

                            <div className="quote-cost-breakdown">
                              {booking.parts_cost > 0 && (
                                <div className="cost-line">
                                  <span>Parts</span>
                                  <span>${booking.parts_cost.toFixed(2)}</span>
                                </div>
                              )}
                              {booking.labor_cost > 0 && (
                                <div className="cost-line">
                                  <span>Labor</span>
                                  <span>${booking.labor_cost.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="cost-line total stacked">
                                <span>Total Repair Cost</span>
                                <span className="total-amount">${booking.repair_quote.toFixed(2)}</span>
                              </div>
                            </div>

                            <div className="service-fee-paid">
                              <CheckCircle size={16} />
                              <span>Service call fee already paid: ${(booking.service_call_fee || 45).toFixed(2)}</span>
                            </div>

                            {/* Payment method info */}
                            <div className="quote-payment-method">
                              {!useNewCard[booking.id] ? (
                                <>
                                  <div className="saved-card-info">
                                    <CreditCard size={16} />
                                    <span>
                                      {booking.payment_method_last4
                                        ? `Payment: Card ending in ${booking.payment_method_last4}`
                                        : booking.stripe_payment_method_id
                                          ? 'Payment: Saved card on file'
                                          : 'No card on file — please add one'}
                                    </span>
                                  </div>
                                  {booking.stripe_payment_method_id && (
                                    <button
                                      className="use-different-card-link"
                                      onClick={() => setUseNewCard(prev => ({ ...prev, [booking.id]: true }))}
                                    >
                                      Use a different card
                                    </button>
                                  )}
                                </>
                              ) : (
                                <div className="new-card-section">
                                  <p className="new-card-label">Enter new card:</p>
                                  {stripePromise ? (
                                    <Elements stripe={stripePromise}>
                                      <RepairCardForm
                                        booking={booking}
                                        onAuthorize={(paymentMethodId) => handleApproveQuote(booking, paymentMethodId)}
                                        loading={approvalLoading}
                                      />
                                    </Elements>
                                  ) : (
                                    <div style={{ padding: '12px', textAlign: 'center', color: '#DC2626' }}>
                                      <p>Payment processing is temporarily unavailable.</p>
                                    </div>
                                  )}
                                  <button
                                    className="use-different-card-link"
                                    onClick={() => setUseNewCard(prev => ({ ...prev, [booking.id]: false }))}
                                  >
                                    Use saved card instead
                                  </button>
                                </div>
                              )}
                            </div>

                            {approvalError && (() => {
                              const errDetail = getStripeErrorDetail({ message: approvalError });
                              return (
                                <div className="card-error-box">
                                  <div className="card-error-icon">
                                    <AlertTriangle size={22} />
                                  </div>
                                  <div className="card-error-content">
                                    <div className="card-error-title">{errDetail.title}</div>
                                    <div className="card-error-message">{errDetail.message}</div>
                                    {errDetail.canRetry && !useNewCard[booking.id] && (
                                      <button
                                        className="card-error-retry"
                                        onClick={() => {
                                          setApprovalError(null);
                                          setUseNewCard(prev => ({ ...prev, [booking.id]: true }));
                                        }}
                                      >
                                        <CreditCard size={14} />
                                        Try Another Card
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {!useNewCard[booking.id] && (
                            <div className="quote-approval-buttons">
                              <button
                                className="btn-approve-quote"
                                onClick={() => handleApproveQuote(booking)}
                                disabled={approvalLoading}
                              >
                                {approvalLoading ? (
                                  <>
                                    <Loader className="spinner" size={20} />
                                    Authorizing...
                                  </>
                                ) : (
                                  <>
                                    <Lock size={18} />
                                    Approve & Authorize ${booking.repair_quote.toFixed(2)}
                                  </>
                                )}
                              </button>
                              <button
                                className="btn-decline-quote"
                                onClick={() => handleDeclineQuote(booking)}
                                disabled={approvalLoading}
                              >
                                <X size={20} />
                                Decline Quote
                              </button>
                            </div>
                          )}
                        </div>
                        );
                      })()}

                      {/* Show quote expired info */}
                      {booking.status === 'quote_expired' && (
                        <div className="quote-expired-section" style={{ background: '#FEF3C7', borderRadius: '12px', padding: '20px', marginTop: '12px' }}>
                          <p style={{ margin: '0 0 8px', color: '#92400E', fontWeight: 600, fontSize: '15px' }}>This quote has expired.</p>
                          <p style={{ margin: 0, color: '#92400E', fontSize: '14px' }}>The 30-minute window to respond has passed. Please book a new service if needed.</p>
                        </div>
                      )}

                      {/* Show approved quote info */}
                      {booking.status === 'quote_approved' && booking.repair_quote && (
                        <div className="quote-approved-info">
                          <div className="approved-banner">
                            <CheckCircle size={20} />
                            <span>Repair Approved - ${booking.repair_quote.toFixed(2)}</span>
                          </div>
                          <p>Your mechanic is working on your vehicle.</p>
                        </div>
                      )}

                      {/* Show declined quote info */}
                      {booking.status === 'quote_declined' && (
                        <div className="quote-declined-info">
                          <p>Quote was declined. Service call fee covered the inspection.</p>
                        </div>
                      )}

                      {/* Show mechanic declined info */}
                      {booking.status === 'declined' && (
                        <div className="booking-declined-info" style={{ background: '#fef2f2', borderRadius: '12px', padding: '16px', marginTop: '12px' }}>
                          <p style={{ margin: 0, color: '#991b1b', fontSize: '14px', lineHeight: '1.5' }}>
                            Your mechanic was unable to accept. Service call fee has been refunded.
                          </p>
                        </div>
                      )}

                      {/* Show cancelled info */}
                      {booking.status === 'cancelled' && (
                        <div className="booking-cancelled-info" style={{ background: '#f1f5f9', borderRadius: '12px', padding: '16px', marginTop: '12px' }}>
                          <p style={{ margin: 0, color: '#475569', fontSize: '14px', lineHeight: '1.5' }}>
                            Booking cancelled. Your refund will appear in 5-10 business days.
                          </p>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="bcard-footer">
                        {canReview && (
                          <button className="bcard-review-btn" onClick={() => onLeaveReview(booking)}>
                            <Star size={14} />
                            Leave Review
                          </button>
                        )}
                        {hasReviewedBooking(booking.id) && (
                          <span className="bcard-reviewed">
                            <CheckCircle size={13} />
                            Reviewed
                          </span>
                        )}
                        {booking.status === 'completed' && !canReview && !hasReviewedBooking(booking.id) && (
                          <span className="bcard-completed-badge">
                            <CheckCircle size={13} />
                            Completed
                          </span>
                        )}
                        {['pending', 'accepted', 'quote_sent'].includes(booking.status) && (
                          <button
                            className="bcard-cancel-btn"
                            onClick={() => openCancelModal(booking)}
                            disabled={cancellingBookingId === booking.id}
                          >
                            <X size={14} />
                            {cancellingBookingId === booking.id ? 'Cancelling...' : 'Cancel Booking'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'reviews' && (
          <div className="user-reviews-section">
            {userReviews.length === 0 ? (
              <div className="mybookings-empty">
                <div className="mybookings-empty-icon">
                  <Star size={36} />
                </div>
                <h3>No reviews yet</h3>
                <p>After completing a service, you can leave reviews for mechanics</p>
              </div>
            ) : (
              <div className="reviews-list">
                {userReviews.map(review => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    showMechanic={true}
                    showCustomer={false}
                    canEdit={true}
                    onEdit={onEditReview}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancel Booking Confirmation Modal */}
      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => { setShowCancelModal(false); setCancelModalBooking(null); }}
        onConfirm={() => handleCancelBooking(cancelModalBooking)}
        title="Cancel Booking?"
        message={
          cancelModalBooking ? (
            <div>
              <p style={{ margin: '0 0 12px', color: '#475569', fontSize: '15px', lineHeight: '1.6' }}>
                {cancelModalBooking.mechanic_name
                  ? `Your booking with ${cancelModalBooking.mechanic_name} will be cancelled.`
                  : 'This booking will be cancelled.'}
              </p>
              <div style={{ background: '#F0FDF4', borderRadius: '8px', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={16} style={{ color: '#059669', flexShrink: 0 }} />
                <span style={{ color: '#065F46', fontSize: '14px' }}>
                  Your service call fee of ${(cancelModalBooking.service_call_fee || 45).toFixed(2)} will be refunded
                </span>
              </div>
              {cancelModalBooking.repair_payment_intent_id && (
                <div style={{ background: '#F0FDF4', borderRadius: '8px', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <CheckCircle size={16} style={{ color: '#059669', flexShrink: 0 }} />
                  <span style={{ color: '#065F46', fontSize: '14px' }}>
                    Repair authorization of ${(cancelModalBooking.repair_quote || 0).toFixed(2)} will be released
                  </span>
                </div>
              )}
            </div>
          ) : null
        }
        confirmText="Yes, Cancel Booking"
        cancelText="Go Back"
        variant="danger"
        loading={cancellingBookingId != null}
      />
    </div>
  );
}

// ========================================
// REVIEW SYSTEM COMPONENTS
// ========================================

// Interactive Star Rating Component
function StarRating({ rating, onRate, size = 24, interactive = false, showCount = false, count = 0 }) {
  const [hoverRating, setHoverRating] = useState(0);

  const handleClick = (star) => {
    if (interactive && onRate) {
      onRate(star);
    }
  };

  const handleMouseEnter = (star) => {
    if (interactive) {
      setHoverRating(star);
    }
  };

  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(0);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <div className={`star-rating ${interactive ? 'interactive' : ''}`}>
      <div className="stars-container">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star-btn ${star <= displayRating ? 'filled' : ''} ${interactive ? 'clickable' : ''}`}
            onClick={() => handleClick(star)}
            onMouseEnter={() => handleMouseEnter(star)}
            onMouseLeave={handleMouseLeave}
            disabled={!interactive}
          >
            <Star
              size={size}
              fill={star <= displayRating ? '#F59E0B' : 'none'}
              stroke={star <= displayRating ? '#F59E0B' : '#D1D5DB'}
              strokeWidth={2}
            />
          </button>
        ))}
      </div>
      {showCount && (
        <span className="rating-count">({count} reviews)</span>
      )}
    </div>
  );
}

// Rating Badge Component
// eslint-disable-next-line no-unused-vars
function RatingBadge({ rating }) {
  if (rating >= 4.9) {
    return (
      <span className="rating-badge top-rated">
        <Award size={14} />
        Top Rated
      </span>
    );
  } else if (rating >= 4.7) {
    return (
      <span className="rating-badge highly-rated">
        <Award size={14} />
        Highly Rated
      </span>
    );
  }
  return null;
}

// Review Modal Component
function ReviewModal({ isOpen, onClose, onSubmit, booking, mechanic }) {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (rating === 0) return;
    // If text is provided, enforce minimum length
    if (reviewText.trim().length > 0 && reviewText.trim().length < 10) return;

    setSubmitting(true);
    try {
      await onSubmit({
        booking_id: booking.id,
        mechanic_id: mechanic.id,
        rating,
        review_text: reviewText.trim() || null
      });
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setRating(0);
        setReviewText('');
      }, 2000);
    } catch (error) {
      console.error('Error submitting review:', error);
    }
    setSubmitting(false);
  };

  const handleSkip = () => {
    onClose();
    setRating(0);
    setReviewText('');
  };

  return (
    <div className="modal-overlay review-modal-overlay">
      <div className="review-modal">
        {submitted ? (
          <div className="review-success">
            <div className="success-animation">
              <CheckCircle size={64} />
              <Sparkles className="sparkle sparkle-1" size={20} />
              <Sparkles className="sparkle sparkle-2" size={16} />
              <Sparkles className="sparkle sparkle-3" size={18} />
            </div>
            <h3>Thanks for your feedback!</h3>
            <p>Your review helps other customers find great mechanics.</p>
          </div>
        ) : (
          <>
            <button className="modal-close" onClick={handleSkip}>
              <X size={24} />
            </button>

            <div className="review-modal-header">
              <div className="review-mechanic-avatar">
                <Wrench size={28} />
              </div>
              <h3>How was your experience?</h3>
              <p>with {mechanic?.business_name || 'your mechanic'}</p>
            </div>

            <div className="review-rating-section">
              <StarRating
                rating={rating}
                onRate={setRating}
                size={44}
                interactive={true}
              />
              <p className="rating-hint">
                {rating === 0 && 'Tap to rate'}
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent!'}
              </p>
            </div>

            <div className="review-text-section">
              <label>Tell us more about your experience (optional)</label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="What did you like? How was the service quality? Would you recommend this mechanic?"
                rows={4}
                maxLength={500}
              />
              <div className="field-hint-row">
                {reviewText.trim().length > 0 && reviewText.trim().length < 10 && (
                  <span className="field-error-text">Review must be at least 10 characters</span>
                )}
                <span className="char-count">{reviewText.length}/500</span>
              </div>
            </div>

            <div className="review-modal-actions">
              <button
                className="review-skip-btn"
                onClick={handleSkip}
                disabled={submitting}
              >
                Skip
              </button>
              <button
                className="review-submit-btn"
                onClick={handleSubmit}
                disabled={rating === 0 || submitting || (reviewText.trim().length > 0 && reviewText.trim().length < 10)}
              >
                {submitting ? (
                  <>
                    <Loader className="spinner" size={18} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Review
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Single Review Card Component
function ReviewCard({ review, showMechanic = false, showCustomer = true, canEdit = false, canReply = false, onEdit, onReply, onFlag }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const canEditReview = () => {
    if (!canEdit) return false;
    const date = new Date(review.created_at);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const handleReplySubmit = () => {
    if (onReply && replyText.trim()) {
      onReply(review.id, replyText.trim());
      setShowReplyForm(false);
      setReplyText('');
    }
  };

  return (
    <div className="review-card">
      <div className="review-card-header">
        {showCustomer && (
          <div className="reviewer-info">
            <div className="reviewer-avatar">
              <User size={16} />
            </div>
            <span className="reviewer-name">{review.customer_name || 'Customer'}</span>
          </div>
        )}
        {showMechanic && (
          <div className="reviewer-info">
            <div className="reviewer-avatar mechanic">
              <Wrench size={16} />
            </div>
            <span className="reviewer-name">{review.mechanic_name || 'Mechanic'}</span>
          </div>
        )}
        <span className="review-date">{getTimeAgo(review.created_at)}</span>
      </div>

      <div className="review-card-rating">
        <StarRating rating={review.rating} size={16} />
      </div>

      {review.review_text && (
        <p className="review-text">{review.review_text}</p>
      )}

      {review.mechanic_reply && (
        <div className="mechanic-reply">
          <div className="reply-header">
            <Wrench size={14} />
            <span>Response from {review.mechanic_name || 'Mechanic'}</span>
          </div>
          <p>{review.mechanic_reply}</p>
        </div>
      )}

      <div className="review-card-actions">
        {canEditReview() && (
          <button className="review-action-btn" onClick={() => onEdit?.(review)}>
            <Edit3 size={14} />
            Edit
          </button>
        )}
        {canReply && !review.mechanic_reply && (
          <button className="review-action-btn" onClick={() => setShowReplyForm(!showReplyForm)}>
            <MessageSquare size={14} />
            Reply
          </button>
        )}
        {canReply && (
          <button className="review-action-btn flag" onClick={() => onFlag?.(review)}>
            <Flag size={14} />
            Report
          </button>
        )}
      </div>

      {showReplyForm && (
        <div className="reply-form">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write your response..."
            rows={3}
          />
          <div className="reply-form-actions">
            <button className="cancel-btn" onClick={() => setShowReplyForm(false)}>Cancel</button>
            <button className="submit-btn" onClick={handleReplySubmit} disabled={!replyText.trim()}>
              <Send size={14} />
              Send Reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Reviews Section Component (for mechanic profile)
// eslint-disable-next-line no-unused-vars
function ReviewsSection({ reviews, loading, mechanicName, showAll = false, onShowAll }) {
  const displayedReviews = showAll ? reviews : reviews.slice(0, 5);

  if (loading) {
    return (
      <div className="reviews-section">
        <h4>Customer Reviews</h4>
        <div className="reviews-skeleton">
          {[1, 2, 3].map(i => (
            <div key={i} className="review-skeleton-card">
              <div className="skeleton-header"></div>
              <div className="skeleton-stars"></div>
              <div className="skeleton-text"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="reviews-section">
        <h4>Customer Reviews</h4>
        <div className="no-reviews">
          <Star size={32} />
          <p>No reviews yet</p>
          <span>Be the first to review {mechanicName}!</span>
        </div>
      </div>
    );
  }

  return (
    <div className="reviews-section">
      <h4>Customer Reviews ({reviews.length})</h4>
      <div className="reviews-list">
        {displayedReviews.map(review => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </div>
      {!showAll && reviews.length > 5 && (
        <button className="see-all-reviews-btn" onClick={onShowAll}>
          See all {reviews.length} reviews
          <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}

// Rating Analytics Component (for mechanic dashboard)
// eslint-disable-next-line no-unused-vars
function RatingAnalytics({ reviews, currentRating }) {
  const [previousRating, setPreviousRating] = useState(null);

  useEffect(() => {
    // Calculate rating from last month
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    const oldReviews = reviews.filter(r => new Date(r.created_at) < lastMonth);
    if (oldReviews.length > 0) {
      const avgOld = oldReviews.reduce((sum, r) => sum + r.rating, 0) / oldReviews.length;
      setPreviousRating(avgOld);
    }
  }, [reviews]);

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => {
      distribution[r.rating] = (distribution[r.rating] || 0) + 1;
    });
    return distribution;
  };

  const distribution = getRatingDistribution();
  const maxCount = Math.max(...Object.values(distribution), 1);
  const trend = previousRating ? currentRating - previousRating : 0;

  return (
    <div className="rating-analytics">
      <div className="rating-overview">
        <div className="big-rating">
          <span className="rating-number">{currentRating.toFixed(1)}</span>
          <Star size={32} fill="#F59E0B" stroke="#F59E0B" />
        </div>
        <div className="rating-meta">
          <span className="total-reviews">{reviews.length} total reviews</span>
          {trend !== 0 && (
            <span className={`rating-trend ${trend > 0 ? 'up' : 'down'}`}>
              {trend > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {Math.abs(trend).toFixed(1)} from last month
            </span>
          )}
        </div>
      </div>

      <div className="rating-distribution">
        {[5, 4, 3, 2, 1].map(stars => (
          <div key={stars} className="distribution-row">
            <span className="star-label">{stars}</span>
            <Star size={14} fill="#F59E0B" stroke="#F59E0B" />
            <div className="distribution-bar">
              <div
                className="distribution-fill"
                style={{ width: `${(distribution[stars] / maxCount) * 100}%` }}
              />
            </div>
            <span className="distribution-count">{distribution[stars]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// Main App Content (wrapped by AuthProvider)
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, needsUserType, needsMechanicOnboarding, authError, setAuthError } = useAuth();

  // Restore booking flow state from sessionStorage on mount
  const savedBookingState = (() => {
    try {
      const saved = sessionStorage.getItem('revvoo_booking_flow');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  })();

  const [currentStep, setCurrentStep] = useState(savedBookingState?.currentStep || 'landing');
  const [problemData, setProblemData] = useState(savedBookingState?.problemData || null);
  const [diagnosis, setDiagnosis] = useState(savedBookingState?.diagnosis || null);
  const [mechanics, setMechanics] = useState([]);
  const [selectedMechanic, setSelectedMechanic] = useState(savedBookingState?.selectedMechanic || null);
  const [loading, setLoading] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [selectedServiceType, setSelectedServiceType] = useState(savedBookingState?.selectedServiceType || null); // 'mobile' or 'shop'
  const [customerLocation, setCustomerLocation] = useState(savedBookingState?.customerLocation || null); // { lat, lng, address, zip, zone }

  // Re-fetch mechanics if restoring to mechanics or booking step
  useEffect(() => {
    if (savedBookingState && ['mechanics', 'booking'].includes(savedBookingState.currentStep) && savedBookingState.selectedServiceType) {
      fetchMechanics(savedBookingState.selectedServiceType, savedBookingState.customerLocation || null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save booking flow state to sessionStorage on step changes
  useEffect(() => {
    const bookingSteps = ['form', 'diagnosis', 'service-type', 'mechanics', 'booking'];
    if (bookingSteps.includes(currentStep)) {
      try {
        sessionStorage.setItem('revvoo_booking_flow', JSON.stringify({
          currentStep,
          problemData,
          diagnosis,
          selectedMechanic,
          selectedServiceType,
          customerLocation
        }));
      } catch (e) { /* sessionStorage full or unavailable */ }
    } else {
      // Not in booking flow — clear saved state
      sessionStorage.removeItem('revvoo_booking_flow');
    }
  }, [currentStep, problemData, diagnosis, selectedMechanic, selectedServiceType, customerLocation]);

  // Step-to-number mapping for URL hash and back button
  const stepOrder = ['landing', 'form', 'diagnosis', 'service-type', 'location', 'mechanics', 'booking'];
  const stepToNum = (step) => { const idx = stepOrder.indexOf(step); return idx >= 0 ? idx : 0; };

  // Update URL hash when booking step changes
  const isUpdatingFromHash = useRef(false);
  useEffect(() => {
    if (isUpdatingFromHash.current) {
      isUpdatingFromHash.current = false;
      return;
    }
    const stepNum = stepToNum(currentStep);
    if (stepNum > 0 && location.pathname === '/') {
      // Push a new hash entry so back button can navigate steps
      window.history.pushState({ bookingStep: currentStep }, '', `#step=${stepNum}`);
    } else if (stepNum === 0 && window.location.hash.includes('step=')) {
      // Returning to landing — clean the hash
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Listen for browser back/forward (popstate) to navigate booking steps
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state?.bookingStep) {
        isUpdatingFromHash.current = true;
        setCurrentStep(e.state.bookingStep);
      } else {
        // No booking step in history state — go to landing
        const hashMatch = window.location.hash.match(/step=(\d+)/);
        if (hashMatch) {
          const num = parseInt(hashMatch[1], 10);
          if (num >= 0 && num < stepOrder.length) {
            isUpdatingFromHash.current = true;
            setCurrentStep(stepOrder[num]);
            return;
          }
        }
        // No valid hash — user backed out to landing
        isUpdatingFromHash.current = true;
        setCurrentStep('landing');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CRITICAL: Redirect mechanics to their dashboard immediately
  useEffect(() => {
    if (!authLoading && user?.user_type === 'mechanic') {
      navigate('/mechanic-dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Auto-navigate to My Bookings when visiting /my-bookings route
  useEffect(() => {
    if (location.pathname === '/my-bookings' && !authLoading && user) {
      setCurrentStep('my-bookings');
      fetchCustomerBookings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, authLoading, user]);

  // Auto-navigate to booking form when ?book=true is in the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('book') === 'true' && location.pathname === '/') {
      setCurrentStep('form');
      // Clean up the URL
      navigate('/', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Scroll to top on step change or booking confirmation
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [currentStep, bookingConfirmed]);

  // Customer dashboard state
  const [customerBookings, setCustomerBookings] = useState([]);

  // Review system state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewMechanic, setReviewMechanic] = useState(null);
  const [userReviews, setUserReviews] = useState([]);

  // Check for pending booking after login
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user) {
      const storedMechanicId = sessionStorage.getItem('pendingMechanicId');
      if (storedMechanicId) {
        sessionStorage.removeItem('pendingMechanicId');
        // Find the mechanic and go to booking
        const mechanic = mechanics.find(m => m.id === storedMechanicId);
        if (mechanic) {
          setSelectedMechanic(mechanic);
          setCurrentStep('booking');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, mechanics]);

  // Handle Book Now click (login gate)
  const handleBookNow = (mechanic) => {
    setSelectedMechanic(mechanic);
    if (user) {
      // User is logged in, go to booking
      setCurrentStep('booking');
    } else {
      // Store mechanic ID and redirect to signup
      sessionStorage.setItem('pendingMechanicId', mechanic.id);
      navigate('/signup?returnTo=/');
    }
  };

  // Handle My Bookings click
  const handleMyBookingsClick = () => {
    setCurrentStep('my-bookings');
    fetchCustomerBookings();
  };

  // Fetch customer bookings from Supabase
  const fetchCustomerBookings = async ({ silent = false } = {}) => {
    if (!user) return;
    if (!silent) setLoading(true);
    try {
      // Try to fetch by customer_email first (more reliable since booking stores email)
      // Also try by customer_id as fallback
      let data = await supabaseFetch('bookings', 'GET', null, `?customer_email=eq.${encodeURIComponent(user.email)}&order=created_at.desc`);

      // If no results by email, try by customer_id
      if (!data || data.length === 0) {
        data = await supabaseFetch('bookings', 'GET', null, `?customer_id=eq.${user.id}&order=created_at.desc`);
      }

      setCustomerBookings(data || []);
    } catch (error) {
      console.error('Error fetching customer bookings:', error);
      if (!silent) setCustomerBookings([]);
    }
    if (!silent) setLoading(false);
  };

  // Fetch user reviews
  const fetchUserReviews = async () => {
    if (!user) return;
    try {
      const data = await supabaseFetch('reviews', 'GET', null, `?customer_id=eq.${user.id}&order=created_at.desc`);
      setUserReviews(data);
    } catch (error) {
      console.error('Error fetching user reviews:', error);
      setUserReviews([]);
    }
  };

  // Open review modal
  const handleLeaveReview = (booking) => {
    setReviewBooking(booking);
    // Find mechanic info
    const mechanic = {
      id: booking.mechanic_id,
      business_name: booking.mechanic_name || 'Your Mechanic'
    };
    setReviewMechanic(mechanic);
    setShowReviewModal(true);
  };

  // Submit review
  const handleSubmitReview = async (reviewData) => {
    try {
      await supabaseFetch('reviews', 'POST', {
        ...reviewData,
        customer_id: user.id,
        customer_name: user.name || user.email?.split('@')[0],
        created_at: new Date().toISOString()
      });

      fetchUserReviews();
      fetchCustomerBookings();

    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    }
  };

  // Map problem keywords to mechanic specialty categories
  const KEYWORD_TO_SPECIALTIES = {
    'brake': ['Brakes', 'Brake Repair'],
    'brakes': ['Brakes', 'Brake Repair'],
    'grinding': ['Brakes', 'Brake Repair'],
    'squeal': ['Brakes', 'Brake Repair'],
    'engine': ['Engine', 'Engine Diagnostics', 'General Repair'],
    'motor': ['Engine', 'Engine Diagnostics'],
    'transmission': ['Transmission'],
    'trans ': ['Transmission'],
    'shifting': ['Transmission'],
    'ac ': ['AC/Heating', 'AC Repair'],
    'a/c': ['AC/Heating', 'AC Repair'],
    'air conditioning': ['AC/Heating', 'AC Repair'],
    'heater': ['AC/Heating', 'AC Repair'],
    'heating': ['AC/Heating', 'AC Repair'],
    'hvac': ['AC/Heating', 'AC Repair'],
    'evap': ['Engine', 'Engine Diagnostics', 'Diagnostics'],
    'oil change': ['Oil Change'],
    'oil leak': ['Engine', 'Oil Change'],
    'battery': ['Battery', 'Battery Service', 'Batteries'],
    'electrical': ['Electrical'],
    'wiring': ['Electrical'],
    'tire': ['Tires', 'Tire Service'],
    'flat': ['Tires', 'Tire Service'],
    'suspension': ['Suspension'],
    'shock': ['Suspension'],
    'strut': ['Suspension'],
    'check engine': ['Diagnostics', 'Engine Diagnostics'],
    'diagnostic': ['Diagnostics', 'Engine Diagnostics'],
    'won\'t start': ['Battery', 'Battery Service', 'Engine', 'Diagnostics'],
    'no start': ['Battery', 'Battery Service', 'Engine', 'Diagnostics'],
    'overheating': ['Engine', 'Engine Diagnostics'],
    'coolant': ['Engine', 'Engine Diagnostics'],
    'radiator': ['Engine', 'Engine Diagnostics'],
  };

  // Fetch mechanics from Supabase, filtered by service type, service zone, and specialties
  const fetchMechanics = async (serviceType = null, locationData = null) => {
    setLoading(true);
    try {
      // Build query based on service type — only show active, approved mechanics
      let query = '?available=eq.true&status=eq.active&stripe_onboarding_complete=eq.true';
      if (serviceType === 'mobile') {
        query += '&offers_mobile=eq.true';
      } else if (serviceType === 'shop') {
        query += '&offers_shop=eq.true';
      }

      const data = await supabaseFetch('mechanics', 'GET', null, query);
      let filtered = data || [];

      // Filter by service zone for mobile service
      if (serviceType === 'mobile' && locationData?.zone) {
        filtered = filtered.filter(mechanic => {
          try {
            const zones = typeof mechanic.service_area === 'string'
              ? JSON.parse(mechanic.service_area)
              : mechanic.service_area;
            return Array.isArray(zones) && zones.includes(locationData.zone);
          } catch {
            return false;
          }
        });
      }

      // Filter by specialty match based on customer problem description
      if (problemData?.problem) {
        const problemLower = (' ' + problemData.problem.toLowerCase() + ' ');
        const neededSpecialties = new Set();
        for (const [keyword, specialties] of Object.entries(KEYWORD_TO_SPECIALTIES)) {
          if (problemLower.includes(keyword)) {
            specialties.forEach(s => neededSpecialties.add(s));
          }
        }
        // Only filter if we detected specific specialties needed
        if (neededSpecialties.size > 0) {
          filtered = filtered.filter(mechanic => {
            const mechSpecialties = mechanic.specialties || [];
            // Mechanics with General Repair always pass through
            if (mechSpecialties.includes('General Repair')) return true;
            // Check if mechanic has at least one matching specialty
            return mechSpecialties.some(s => neededSpecialties.has(s));
          });
        }
      }

      setMechanics(filtered);
    } catch (error) {
      console.error('Error fetching mechanics:', error);
      setMechanics([]);
    }
    setLoading(false);
  };

  // AI Diagnosis via Supabase Edge Function
  const getDiagnosis = async (data) => {
    if (loading) return; // Prevent multiple concurrent calls
    setLoading(true);
    setCurrentStep('diagnosis');
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/ai-diagnosis`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            carInfo: data.carInfo,
            problem: data.problem,
            urgency: data.urgency,
          }),
        }
      );

      if (response.ok) {
        const diagnosisData = await response.json();
        setDiagnosis(diagnosisData);
      } else {
        throw new Error(`Edge function returned ${response.status}`);
      }
    } catch (error) {
      console.error('AI diagnosis failed, using fallback:', error);
      // Enhanced fallback diagnosis based on problem keywords
      const problem = data.problem.toLowerCase();
      let fallbackDiagnosis = {
        likely_issue: "Mechanical Issue Requiring Inspection",
        severity: "medium",
        estimated_cost: "$100 - $400",
        urgency_level: "soon",
        time_estimate: "1-2 hours",
        whats_happening: "Based on your description, your vehicle needs a professional inspection to pinpoint the exact issue. Modern cars have many interconnected systems, and symptoms can sometimes indicate multiple possible causes. This is completely normal and nothing to worry about.",
        why_it_matters: "Getting a proper diagnosis early helps prevent small issues from becoming expensive repairs. The good news is that most car problems are straightforward to fix once properly identified. A skilled mechanic can quickly narrow down the cause.",
        what_to_expect: "A mechanic will perform a comprehensive inspection, checking the most likely culprits based on your symptoms. They'll explain exactly what they find and give you options before any work begins. Most diagnostic inspections take about an hour.",
        quick_tips: [
          "Note any changes in the symptoms",
          "Avoid long drives until inspected",
          "Keep track of when the issue occurs"
        ],
        confidence_note: "Our mechanics are experienced with all types of issues and will take good care of you."
      };

      if (problem.includes('brake') || problem.includes('grinding') || problem.includes('squeal')) {
        fallbackDiagnosis = {
          likely_issue: "Worn Brake Pads",
          severity: "high",
          estimated_cost: "$150 - $350",
          urgency_level: "urgent",
          time_estimate: "1-2 hours",
          whats_happening: "Based on your description, this sounds like worn brake pads. This happens when the friction material on your brake pads wears down to the metal backing plate, which then grinds against your brake rotors. It's common after 30,000-50,000 miles of driving, especially with frequent city driving.",
          why_it_matters: "If left unaddressed, the grinding will damage your rotors, turning a $200 brake pad replacement into a $500+ job. More importantly, worn brakes reduce your stopping power, which is a safety concern. The good news is this is one of the most routine repairs mechanics handle daily.",
          what_to_expect: "A mechanic will inspect your entire brake system, replace the pads (and rotors if damaged), and test everything. The repair typically takes 1-2 hours. Parts are readily available and this is a straightforward fix. You'll notice the difference immediately—smooth, quiet braking.",
          quick_tips: [
            "Avoid heavy braking when possible",
            "Leave extra stopping distance",
            "Don't delay scheduling service"
          ],
          confidence_note: "Brake repairs are among the most common services—mechanics do these daily."
        };
      } else if (problem.includes('engine') || problem.includes('start') || problem.includes('won\'t start') || problem.includes('crank')) {
        fallbackDiagnosis = {
          likely_issue: "Battery or Starter Issue",
          severity: "medium",
          estimated_cost: "$100 - $350",
          urgency_level: "soon",
          time_estimate: "30 min - 1 hour",
          whats_happening: "Based on your description, this sounds like a battery or starter motor issue. Car batteries typically last 3-5 years, and extreme temperatures can shorten their lifespan. If you hear a clicking sound, it's usually the battery; if there's no sound at all, it could be the starter or a connection issue.",
          why_it_matters: "A failing battery can leave you stranded at inconvenient times. However, this is usually a quick and affordable fix. The good news is that battery and starter issues are easy to diagnose, and replacements are straightforward.",
          what_to_expect: "A mechanic will test your battery's charge and cold cranking amps, check the alternator output, and inspect the starter if needed. Battery replacement takes about 30 minutes; starters take a bit longer. Most shops stock common batteries.",
          quick_tips: [
            "Keep jumper cables handy",
            "Check for corroded terminals",
            "Avoid short trips that don't fully charge the battery"
          ],
          confidence_note: "Battery issues are quick to diagnose and fix—you'll be back on the road soon."
        };
      } else if (problem.includes('oil') || problem.includes('leak') || problem.includes('drip')) {
        fallbackDiagnosis = {
          likely_issue: "Oil Leak from Gasket",
          severity: "medium",
          estimated_cost: "$150 - $500",
          urgency_level: "soon",
          time_estimate: "1-3 hours",
          whats_happening: "Based on your description, you likely have an oil leak from a gasket or seal. Over time, these rubber components dry out and shrink, allowing oil to seep through. Common culprits include the valve cover gasket, oil pan gasket, or front/rear main seals. This is a normal wear item.",
          why_it_matters: "While small leaks aren't emergencies, ignoring them can lead to low oil levels which can damage your engine. The good news is that most gasket replacements are routine repairs. Catching it early prevents bigger problems.",
          what_to_expect: "A mechanic will clean the area and identify exactly where the leak originates. They'll replace the affected gasket and check for any other potential leak points. Repair time varies based on which gasket needs replacement—valve covers are quick, main seals take longer.",
          quick_tips: [
            "Check your oil level weekly",
            "Place cardboard under the car to track the leak",
            "Top off oil as needed to maintain proper levels"
          ],
          confidence_note: "Gasket repairs are common maintenance—nothing to stress about."
        };
      } else if (problem.includes('overheat') || problem.includes('temperature') || problem.includes('hot') || problem.includes('coolant')) {
        fallbackDiagnosis = {
          likely_issue: "Cooling System Issue",
          severity: "high",
          estimated_cost: "$150 - $600",
          urgency_level: "urgent",
          time_estimate: "1-3 hours",
          whats_happening: "Based on your description, your cooling system isn't keeping your engine at the right temperature. This could be a coolant leak, failed thermostat, worn water pump, or a clogged radiator. Overheating is your car's way of telling you something needs attention right away.",
          why_it_matters: "Continued driving while overheated can cause serious engine damage, including a blown head gasket or warped engine block—repairs costing thousands. The good news is that most cooling issues are fixable with basic repairs if caught early. Pull over and let it cool if the gauge goes into the red.",
          what_to_expect: "A mechanic will pressure test the cooling system to find leaks, check the thermostat operation, and inspect the water pump and radiator. Most cooling repairs are same-day fixes. They'll top off your coolant and ensure everything's sealed properly.",
          quick_tips: [
            "Don't drive if the temperature gauge is in the red",
            "Never open the radiator cap when hot",
            "Check coolant level when engine is cold"
          ],
          confidence_note: "Cooling repairs are very common and usually straightforward to fix."
        };
      } else if (problem.includes('noise') || problem.includes('sound') || problem.includes('rattle') || problem.includes('clunk')) {
        fallbackDiagnosis = {
          likely_issue: "Suspension or Exhaust Component",
          severity: "medium",
          estimated_cost: "$100 - $400",
          urgency_level: "soon",
          time_estimate: "1-2 hours",
          whats_happening: "Based on your description, you're hearing an unusual noise from your vehicle. Rattles and clunks often come from worn suspension components like bushings, struts, or sway bar links. They can also come from loose exhaust components or heat shields. The location and timing of the noise helps pinpoint the source.",
          why_it_matters: "While many noises are just annoying rather than dangerous, some can indicate worn components that affect handling or safety. Getting it checked prevents a small issue from becoming a bigger problem. The good news is that most noise-related repairs are affordable.",
          what_to_expect: "A mechanic will take the car for a test drive to hear the noise firsthand, then inspect the likely culprits on a lift. Once identified, most suspension or exhaust repairs are quick. They'll show you exactly what's worn before recommending the fix.",
          quick_tips: [
            "Note when the noise occurs (speed, turns, bumps)",
            "Try to identify if it's from the front or rear",
            "Safe to drive unless handling feels affected"
          ],
          confidence_note: "Rattles and clunks are usually inexpensive fixes once the source is found."
        };
      }

      setDiagnosis(fallbackDiagnosis);
    }
    setLoading(false);
  };

  // Create booking
  const createBooking = async (customerInfo, paymentData) => {
    setLoading(true);
    try {
      // Extract payment data - paymentMethodId is now actually a paymentIntentId
      const { serviceCallFee, platformFee, dueNow, estimatedRepair, promoType, paymentMethodId: paymentIntentId, stripePaymentMethodId, paymentMethodLast4, stripeCustomerId } = paymentData;

      // C7 FIX: Look up existing user by email before creating a new one.
      // Previously this unconditionally POSTed a new row, causing duplicates for
      // logged-in users and unique constraint errors for returning guests.
      let userId = user?.id || null;

      if (!userId) {
        // Not logged in — check if a user with this email already exists
        try {
          const existingUsers = await supabaseFetch('users', 'GET', null, `?email=eq.${encodeURIComponent(customerInfo.email)}&select=id&limit=1`);
          if (existingUsers && existingUsers.length > 0) {
            userId = existingUsers[0].id;
          }
        } catch (lookupErr) {
          console.warn('User lookup failed, will create new:', lookupErr);
        }
      }

      if (!userId) {
        // No existing user found — create a new one
        // M10 FIX: If POST fails (e.g. unique constraint on email for a signed-up user),
        // retry the lookup instead of crashing. This handles the race condition where
        // the initial lookup failed but the user does exist.
        try {
          const userData = await supabaseFetch('users', 'POST', {
            email: customerInfo.email,
            name: customerInfo.name,
            phone: customerInfo.phone,
            user_type: 'customer'
          });
          userId = userData[0]?.id;
        } catch (createErr) {
          console.warn('User creation failed (likely duplicate), retrying lookup:', createErr);
          // Retry lookup — the user probably already exists
          try {
            const retryUsers = await supabaseFetch('users', 'GET', null, `?email=eq.${encodeURIComponent(customerInfo.email)}&select=id&limit=1`);
            if (retryUsers && retryUsers.length > 0) {
              userId = retryUsers[0].id;
            } else {
              throw new Error('Could not create or find your account. Please try again.');
            }
          } catch (retryErr) {
            throw new Error('Could not create or find your account. Please try again.');
          }
        }
      }

      // Create booking with service call fee model fields
      const bookingData = await supabaseFetch('bookings', 'POST', {
        customer_id: userId,
        mechanic_id: selectedMechanic.id,
        // Customer info for display
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        // Mechanic info for display
        mechanic_name: selectedMechanic.business_name || selectedMechanic.name,
        // Problem info
        problem_description: problemData.problem,
        car_info: problemData.carInfo,
        diagnosis: diagnosis,
        // Status - starts as PENDING until mechanic accepts
        status: 'pending',
        // Service call fee fields
        service_call_fee: serviceCallFee,
        service_call_fee_captured: true, // Captured immediately
        platform_fee: platformFee,
        // Repair quote fields (null until mechanic sends quote)
        repair_quote: null,
        repair_quote_approved: false,
        repair_amount_authorized: false,
        // Estimated values for reference
        estimated_repair_min: estimatedRepair?.min || null,
        estimated_repair_max: estimatedRepair?.max || null,
        // Total will be calculated when repair is approved
        total_cost: dueNow, // For now, just the service call fee
        // Payment - now using payment_intent_id from successful charge
        payment_intent_id: paymentIntentId,
        // Saved payment method for future repair charges
        stripe_payment_method_id: stripePaymentMethodId || null,
        stripe_customer_id: stripeCustomerId || null,
        payment_method_last4: paymentMethodLast4 || null,
        // Promo tracking
        promo_type: promoType || null,
        // Service type - mobile or shop
        service_type: selectedServiceType || 'mobile',
        // Customer service location
        service_address: customerInfo.address || customerLocation?.address || null,
        service_lat: customerInfo.latitude || customerLocation?.lat || null,
        service_lng: customerInfo.longitude || customerLocation?.lng || null,
        customer_address: customerInfo.address || customerLocation?.address || null,
        latitude: customerInfo.latitude || customerLocation?.lat || null,
        longitude: customerInfo.longitude || customerLocation?.lng || null
      });


      const bookingId = bookingData[0]?.id || 'NEW';

      // Update payment record with booking_id
      if (bookingId !== 'NEW' && paymentIntentId) {
        try {
          await supabaseFetch('payments', 'PATCH', {
            booking_id: bookingId
          }, `?payment_intent_id=eq.${paymentIntentId}`);
        } catch (paymentUpdateError) {
          console.error('Failed to link payment to booking:', paymentUpdateError);
        }
      }

      // Build SMS with service call fee model messaging
      const mechanicPhone = selectedMechanic.phone || '+1234567890';
      const smsMessage = buildMechanicSMSServiceCall(
        customerInfo.name,
        problemData.problem,
        problemData.carInfo,
        bookingId,
        serviceCallFee,
        estimatedRepair
      );

      await sendSMS(mechanicPhone, smsMessage);

      // Send email notification to mechanic
      try {
        const mechanicEmail = selectedMechanic.email;
        if (mechanicEmail) {
          await sendEmail('new_job', mechanicEmail, {
            customerName: customerInfo.name,
            vehicle: problemData.carInfo,
            problemDescription: problemData.problem,
            serviceCallFee: serviceCallFee,
            dashboardUrl: 'https://www.revvoo.app/mechanic-dashboard'
          });
        }
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
        // Don't block booking flow for email errors
      }

      setBookingConfirmed(true);
      sessionStorage.removeItem('revvoo_booking_flow');
    } catch (error) {
      console.error('Error creating booking:', error);

      // C6 FIX: Booking insert failed — attempt to refund the service call payment.
      // Only tell the customer "refunded" if the refund actually succeeded.
      const paymentIntentId = paymentData.paymentMethodId; // This is the payment_intent_id
      let refundSucceeded = false;

      if (paymentIntentId) {
        try {
          const refundResponse = await fetch(`${SUPABASE_URL}/functions/v1/refund-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({ paymentIntentId }),
          });

          if (refundResponse.ok) {
            const refundResult = await refundResponse.json();
            refundSucceeded = refundResult.success === true;
          }
        } catch (refundError) {
          console.error('Refund attempt failed:', refundError);
          refundSucceeded = false;
        }
      }

      // Show appropriate error based on whether refund actually worked
      if (refundSucceeded) {
        setBookingError('Failed to create booking. Your payment has been refunded. Please try again.');
      } else if (paymentIntentId) {
        setBookingError('Booking failed. If you were charged, please contact support at support@revvoo.app for a refund.');
      } else {
        setBookingError('Failed to create booking. Please try again.');
      }
    }
    setLoading(false);
  };

  // Fetch user reviews when logged in
  useEffect(() => {
    if (user) {
      fetchUserReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle landing page problem submission
  const handleLandingSubmit = (problem) => {
    const data = { problem, carInfo: '', urgency: 'can_wait', photos: [] };
    setProblemData(data);
    setCurrentStep('form');
  };

  // Render current view
  const renderContent = () => {
    switch (currentStep) {
      case 'landing':
        return <LandingPage onSubmitProblem={handleLandingSubmit} onBrowseMechanics={() => setCurrentStep('form')} />;

      case 'form':
        return (
          <ProblemForm
            onSubmit={(data) => {
              setProblemData(data);
              getDiagnosis(data);
            }}
            onBack={() => setCurrentStep('landing')}
            initialProblem={problemData?.problem || ''}
          />
        );

      case 'diagnosis':
        return (
          <AIDiagnosis
            problemData={problemData}
            diagnosis={diagnosis}
            loading={loading}
            onContinue={() => {
              setCurrentStep('service-type');
            }}
            onBack={() => setCurrentStep('form')}
          />
        );

      case 'service-type':
        return (
          <ServiceTypeSelection
            onSelect={(type) => {
              setSelectedServiceType(type);
              if (type === 'mobile') {
                setCurrentStep('location');
              } else {
                fetchMechanics(type);
                setCurrentStep('mechanics');
              }
            }}
            onBack={() => setCurrentStep('diagnosis')}
          />
        );

      case 'location':
        return (
          <LocationStep
            onLocationConfirmed={(locationData) => {
              setCustomerLocation(locationData);
              fetchMechanics('mobile', locationData);
              setCurrentStep('mechanics');
            }}
            onBack={() => setCurrentStep('service-type')}
          />
        );

      case 'mechanics':
        return (
          <MechanicsList
            mechanics={mechanics}
            loading={loading}
            diagnosis={diagnosis}
            serviceType={selectedServiceType}
            onSelectMechanic={handleBookNow}
            onBack={() => selectedServiceType === 'mobile' ? setCurrentStep('location') : setCurrentStep('service-type')}
            customerLocation={customerLocation}
          />
        );

      case 'booking':
        return (
          <BookingConfirmation
            mechanic={selectedMechanic}
            problemData={problemData}
            diagnosis={diagnosis}
            onConfirm={createBooking}
            onBack={() => setCurrentStep('mechanics')}
            loading={loading}
            confirmed={bookingConfirmed}
            bookingError={bookingError}
            selectedServiceType={selectedServiceType}
            onMyBookings={handleMyBookingsClick}
            customerLocation={customerLocation}
          />
        );

      case 'my-bookings':
        return (
          <CustomerDashboard
            bookings={customerBookings}
            loading={loading}
            onRefresh={() => { fetchCustomerBookings(); fetchUserReviews(); }}
            onSilentRefresh={() => fetchCustomerBookings({ silent: true })}
            userReviews={userReviews}
            onLeaveReview={handleLeaveReview}
            onEditReview={() => {}}
          />
        );

      default:
        return <LandingPage onSubmitProblem={handleLandingSubmit} onBrowseMechanics={() => setCurrentStep('form')} />;
    }
  };

  // Show loading spinner while checking auth
  if (authLoading) {
    return (
      <div className="app">
        <div className="auth-loading">
          <Loader className="spinner" size={48} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show user type selection for Google sign-in users
  if (user && needsUserType) {
    return <UserTypeSelection />;
  }

  // Show mechanic onboarding for new mechanics
  if (user && needsMechanicOnboarding) {
    return <MechanicOnboarding />;
  }

  return (
    <div className="app">
      <Header onMyBookingsClick={handleMyBookingsClick} />

      {/* M5 FIX: Show OAuth/auth error banner if sign-in failed */}
      {authError && (
        <div className="auth-error-banner" style={{
          background: '#FEF2F2',
          borderBottom: '1px solid #FECACA',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#DC2626', fontSize: '14px' }}>
            <AlertTriangle size={16} />
            <span>{authError}</span>
          </div>
          <button
            onClick={() => setAuthError(null)}
            style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: '4px' }}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <main className="main-content">
        {renderContent()}
      </main>
      <Footer />

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setReviewBooking(null);
          setReviewMechanic(null);
        }}
        onSubmit={handleSubmitReview}
        booking={reviewBooking}
        mechanic={reviewMechanic}
      />
    </div>
  );
}

// MechanicLandingPage extracted to src/pages/MechanicLanding.js

// ========================================
// STATIC PAGES
// ========================================

// How It Works Page
function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div className="static-page how-it-works-page">
      <header className="static-page-header">
        <nav className="static-nav">
          <Link to="/" className="static-logo">
            <img src="/logo.png" alt="Revvoo" />
          </Link>
          <Link to="/" className="back-link">
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </nav>
      </header>

      <main className="static-content hiw-content">
        <div className="static-hero">
          <h1>How Revvoo Works</h1>
          <p>Your car problem, solved in 3 taps. No shop visits, no waiting rooms, no surprises.</p>
        </div>

        <section className="hiw-steps-section">
          <div className="hiw-steps-grid">
            <div className="hiw-step-card">
              <div className="hiw-step-badge">1</div>
              <div className="hiw-step-icon-wrap"><MessageSquare size={28} /></div>
              <h3>Describe Your Problem</h3>
              <p>Check engine light? Strange noise? Just type it in plain English. No mechanic jargon needed. Our AI understands you.</p>
            </div>

            <div className="hiw-step-card">
              <div className="hiw-step-badge">2</div>
              <div className="hiw-step-icon-wrap"><Sparkles size={28} /></div>
              <h3>Get an Instant AI Diagnosis</h3>
              <p>In seconds, our AI analyzes your issue and gives you a clear breakdown: what's likely wrong, estimated cost range, and urgency level. No guessing.</p>
            </div>

            <div className="hiw-step-card">
              <div className="hiw-step-badge">3</div>
              <div className="hiw-step-icon-wrap"><Star size={28} /></div>
              <h3>Choose Your Mechanic</h3>
              <p>Browse verified mechanics near you with real ratings, transparent service fees, and specialties. Pick the one that fits your needs and budget.</p>
            </div>

            <div className="hiw-step-card">
              <div className="hiw-step-badge">4</div>
              <div className="hiw-step-icon-wrap"><CreditCard size={28} /></div>
              <h3>Pay a Small Service Fee</h3>
              <p>Lock in your mechanic with a service call fee ($45-$85). That's it upfront. No hidden charges. You approve any repair costs before work begins.</p>
            </div>

            <div className="hiw-step-card">
              <div className="hiw-step-badge">5</div>
              <div className="hiw-step-icon-wrap"><Navigation size={28} /></div>
              <h3>Your Mechanic Comes to You</h3>
              <p>Track your mechanic in real-time as they head to your location. Home, work, or wherever your car is. Average arrival: 15-30 minutes.</p>
            </div>

            <div className="hiw-step-card">
              <div className="hiw-step-badge">6</div>
              <div className="hiw-step-icon-wrap"><CheckCircle size={28} /></div>
              <h3>Problem Solved</h3>
              <p>Your car is fixed on the spot. Rate your mechanic, get a receipt, and you're back on the road. It's that simple.</p>
            </div>
          </div>
        </section>

        <section className="hiw-benefits-section">
          <h2>Why Choose Revvoo?</h2>
          <div className="hiw-benefits-grid">
            <div className="hiw-benefit-card">
              <div className="hiw-benefit-icon"><Shield size={22} /></div>
              <div className="hiw-benefit-text">
                <h4>Verified Mechanics</h4>
                <p>Every mechanic is background-checked, insured, and verified. See their ratings and reviews before you book.</p>
              </div>
            </div>
            <div className="hiw-benefit-card">
              <div className="hiw-benefit-icon"><DollarSign size={22} /></div>
              <div className="hiw-benefit-text">
                <h4>Transparent Pricing</h4>
                <p>Know exactly what you'll pay before any work starts. Service fee upfront, repair quote approved by you. Zero surprises.</p>
              </div>
            </div>
            <div className="hiw-benefit-card">
              <div className="hiw-benefit-icon"><MapPin size={22} /></div>
              <div className="hiw-benefit-text">
                <h4>We Come to You</h4>
                <p>Skip the shop. Our mechanics come to your home, office, or wherever your car is. Save hours of your day.</p>
              </div>
            </div>
            <div className="hiw-benefit-card">
              <div className="hiw-benefit-icon"><Award size={22} /></div>
              <div className="hiw-benefit-text">
                <h4>Satisfaction Guaranteed</h4>
                <p>Not happy with the service? We'll make it right or refund your money. Your trust is everything to us.</p>
              </div>
            </div>
          </div>
        </section>

        <div className="static-cta">
          <h2>Ready to Fix Your Car?</h2>
          <p>Describe your problem and get matched with a verified mechanic in minutes. No shop visits needed.</p>
          <button onClick={() => navigate('/')} className="cta-button">
            Get Started Now <ArrowRight size={20} />
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Pricing Page
function PricingPage() {
  const navigate = useNavigate();

  return (
    <div className="static-page pricing-page">
      <header className="static-page-header">
        <nav className="static-nav">
          <Link to="/" className="static-logo">
            <img src="/logo.png" alt="Revvoo" />
          </Link>
          <Link to="/" className="back-link">
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </nav>
      </header>

      <main className="static-content">
        <div className="static-hero">
          <h1>Transparent Pricing</h1>
          <p>No hidden fees. No surprises. Just honest, upfront pricing for quality auto repair.</p>
        </div>

        <section className="pricing-structure">
          <h2>How Our Pricing Works</h2>

          <div className="pricing-breakdown">
            <div className="pricing-item">
              <div className="pricing-icon"><Car size={32} /></div>
              <h3>Service Call Fee</h3>
              <p className="price-range">$45 - $75</p>
              <p>A flat fee for the mechanic to come to your location. This covers their travel time and initial diagnosis.</p>
            </div>

            <div className="pricing-plus">+</div>

            <div className="pricing-item">
              <div className="pricing-icon"><Clock size={32} /></div>
              <h3>Labor</h3>
              <p className="price-range">$80 - $120/hr</p>
              <p>Competitive hourly labor rates. Most repairs take 1-3 hours. You'll know the estimate upfront.</p>
            </div>

            <div className="pricing-plus">+</div>

            <div className="pricing-item">
              <div className="pricing-icon"><Wrench size={32} /></div>
              <h3>Parts</h3>
              <p className="price-range">At cost + 15%</p>
              <p>Quality OEM or aftermarket parts. We mark up parts minimally to cover procurement.</p>
            </div>
          </div>
        </section>

        <section className="common-repairs">
          <h2>Common Repair Estimates</h2>
          <div className="repair-grid">
            <div className="repair-card">
              <h4>Oil Change</h4>
              <p className="estimate">$75 - $120</p>
              <span className="time">~45 minutes</span>
            </div>
            <div className="repair-card">
              <h4>Brake Pads (front)</h4>
              <p className="estimate">$200 - $350</p>
              <span className="time">~1.5 hours</span>
            </div>
            <div className="repair-card">
              <h4>Battery Replacement</h4>
              <p className="estimate">$150 - $250</p>
              <span className="time">~30 minutes</span>
            </div>
            <div className="repair-card">
              <h4>Alternator Replacement</h4>
              <p className="estimate">$400 - $600</p>
              <span className="time">~2 hours</span>
            </div>
            <div className="repair-card">
              <h4>Starter Motor</h4>
              <p className="estimate">$350 - $550</p>
              <span className="time">~1.5 hours</span>
            </div>
            <div className="repair-card">
              <h4>Spark Plugs</h4>
              <p className="estimate">$150 - $300</p>
              <span className="time">~1 hour</span>
            </div>
          </div>
          <p className="pricing-note">*Estimates vary based on vehicle make/model and local parts availability.</p>
        </section>

        <section className="pricing-guarantee">
          <Shield size={48} />
          <h2>Our Price Match Guarantee</h2>
          <p>Found a lower quote from a licensed shop? We'll match it. We believe in fair, transparent pricing that benefits both customers and mechanics.</p>
        </section>

        <div className="static-cta">
          <h2>Get Your Free Quote</h2>
          <p>Describe your car problem and receive an instant AI-powered estimate.</p>
          <button onClick={() => navigate('/')} className="cta-button">
            Get Started <ArrowRight size={20} />
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Contact Page
function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          to: 'revvoo.app@gmail.com',
          subject: `Revvoo Contact Form: ${formData.subject || 'General Inquiry'} - from ${formData.name}`,
          type: 'contact_form',
          data: {
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            message: formData.message
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitError('Failed to send message. Please try emailing us directly at revvoo.app@gmail.com');
    }
    setSubmitting(false);
  };

  return (
    <div className="static-page contact-page">
      <header className="static-page-header">
        <nav className="static-nav">
          <Link to="/" className="static-logo">
            <img src="/logo.png" alt="Revvoo" />
          </Link>
          <Link to="/" className="back-link">
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </nav>
      </header>

      <main className="static-content">
        <div className="static-hero">
          <h1>Contact Us</h1>
          <p>Have questions? We're here to help. Reach out and we'll respond as soon as we can.</p>
        </div>

        <div className="contact-grid">
          <div className="contact-info">
            <div className="contact-item">
              <Mail size={24} />
              <div>
                <h4>Email Us</h4>
                <p>revvoo.app@gmail.com</p>
              </div>
            </div>
            <div className="contact-item">
              <Phone size={24} />
              <div>
                <h4>Call Us</h4>
                <p>+1 (559) 630-4031</p>
              </div>
            </div>
            <div className="contact-item">
              <MapPin size={24} />
              <div>
                <h4>Location</h4>
                <p>San Diego, CA</p>
              </div>
            </div>
            <div className="contact-item">
              <Clock size={24} />
              <div>
                <h4>Hours</h4>
                <p>Mon-Sun: 7am - 10pm</p>
              </div>
            </div>
          </div>

          <div className="contact-form-wrap">
            {submitted ? (
              <div className="form-success">
                <CheckCircle size={48} />
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label>Your Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Subject</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                  >
                    <option value="">Select a topic</option>
                    <option value="booking">Booking Help</option>
                    <option value="mechanic">Mechanic Inquiry</option>
                    <option value="payment">Payment Issue</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows="5"
                    required
                  />
                </div>
                {submitError && (
                  <div className="auth-error" style={{ marginBottom: '16px' }}>
                    <AlertTriangle size={16} />
                    <span>{submitError}</span>
                  </div>
                )}
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader className="spinner" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// TermsPage and PrivacyPage extracted to src/pages/Legal.js

// Mechanic Benefits Page
function MechanicBenefitsPage() {
  const navigate = useNavigate();

  return (
    <div className="static-page benefits-page">
      <header className="static-page-header">
        <nav className="static-nav">
          <Link to="/" className="static-logo">
            <img src="/logo.png" alt="Revvoo" />
          </Link>
          <Link to="/mechanics" className="back-link">
            <ArrowLeft size={18} />
            Back to Mechanics
          </Link>
        </nav>
      </header>

      <main className="static-content">
        <div className="static-hero benefits-hero">
          <h1>Why Join Revvoo?</h1>
          <p>Grow your business, work on your terms, and earn more with every job.</p>
        </div>

        <section className="benefits-showcase">
          <div className="benefit-card-large">
            <div className="benefit-icon"><DollarSign size={40} /></div>
            <h3>Earn More Per Job</h3>
            <p>Keep 85% of every job. No franchise fees, no monthly dues. You set your service call fee and labor rates.</p>
            <div className="benefit-stat">
              <span className="stat-number">$85</span>
              <span className="stat-label">avg. hourly earnings</span>
            </div>
          </div>

          <div className="benefit-card-large">
            <div className="benefit-icon"><Calendar size={40} /></div>
            <h3>Flexible Schedule</h3>
            <p>Work when you want. Accept jobs that fit your schedule. Take time off without asking permission.</p>
            <div className="benefit-stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">schedule control</span>
            </div>
          </div>

          <div className="benefit-card-large">
            <div className="benefit-icon"><MapPin size={40} /></div>
            <h3>Work Where You Want</h3>
            <p>Set your service radius. Work in neighborhoods you know. No commute to a shop every day.</p>
            <div className="benefit-stat">
              <span className="stat-number">0</span>
              <span className="stat-label">shop overhead costs</span>
            </div>
          </div>

          <div className="benefit-card-large">
            <div className="benefit-icon"><Users size={40} /></div>
            <h3>Steady Customer Flow</h3>
            <p>We bring customers to you through our marketing. Focus on what you do best - fixing cars.</p>
            <div className="benefit-stat">
              <span className="stat-number">24/7</span>
              <span className="stat-label">customer access</span>
            </div>
          </div>
        </section>

        <section className="how-earnings-work">
          <h2>How Your Earnings Work</h2>
          <div className="earnings-example">
            <div className="example-job">
              <h4>Example: Brake Pad Replacement</h4>
              <div className="earnings-breakdown">
                <div className="earning-line">
                  <span>Service Call Fee</span>
                  <span>$55</span>
                </div>
                <div className="earning-line">
                  <span>Labor (2 hours × $90/hr)</span>
                  <span>$180</span>
                </div>
                <div className="earning-line">
                  <span>Parts markup</span>
                  <span>$25</span>
                </div>
                <div className="earning-line total">
                  <span>Job Total</span>
                  <span>$260</span>
                </div>
                <div className="earning-line fee">
                  <span>Revvoo Fee (15%)</span>
                  <span>-$39</span>
                </div>
                <div className="earning-line your-take">
                  <span>Your Earnings</span>
                  <span>$221</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="requirements-section">
          <h2>What You Need to Join</h2>
          <div className="requirements-grid">
            <div className="requirement-item">
              <CheckCircle size={24} />
              <span>Valid auto repair license or ASE certification</span>
            </div>
            <div className="requirement-item">
              <CheckCircle size={24} />
              <span>Liability insurance ($1M minimum)</span>
            </div>
            <div className="requirement-item">
              <CheckCircle size={24} />
              <span>Reliable vehicle for mobile service</span>
            </div>
            <div className="requirement-item">
              <CheckCircle size={24} />
              <span>Professional tools and equipment</span>
            </div>
            <div className="requirement-item">
              <CheckCircle size={24} />
              <span>Smartphone for the Revvoo app</span>
            </div>
            <div className="requirement-item">
              <CheckCircle size={24} />
              <span>Pass background check</span>
            </div>
          </div>
        </section>

        <div className="static-cta">
          <h2>Ready to Grow Your Business?</h2>
          <p>Join verified mechanics earning more with Revvoo.</p>
          <button onClick={() => navigate('/mechanics')} className="cta-button">
            Apply Now <ArrowRight size={20} />
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// LinksPage extracted to src/pages/Links.js

// AdminDashboard extracted to src/pages/AdminDashboard.js

// Customer pages (MyVehiclesPage, PaymentMethodsPage, MyReviewsPage, HelpSupportPage, SettingsPage)
// extracted to src/pages/CustomerSettings.js

// ========================================
// MECHANIC APPLICATION STATUS PAGES
// ========================================

function MechanicApplicationReceived() {
  const { signOut } = useAuth();

  const handleBackToHome = () => {
    signOut();
    localStorage.removeItem('revvoo_session');
    localStorage.removeItem('revvoo_user');
    localStorage.removeItem('revvoo_view_mode');
    window.location.href = '/';
  };

  return (
    <div className="mechanic-application-page">
      <div className="mechanic-application-container">
        <div className="mechanic-application-logo">
          <img src="/logo.png" alt="Revvoo" style={{ height: '48px' }} />
        </div>
        <div className="mechanic-application-icon">
          <CheckCircle size={56} />
        </div>
        <h1 className="mechanic-application-title">Application Received!</h1>
        <p className="mechanic-application-message">
          Thanks for applying to join Revvoo. We'll review your application and notify you by email once approved.
        </p>
        <p className="mechanic-application-timeline">
          This usually takes less than 24 hours.
        </p>
        <p className="mechanic-application-contact">
          Questions? Contact us at <a href="mailto:revvoo.app@gmail.com">revvoo.app@gmail.com</a>
        </p>
        <button className="mechanic-application-cta" onClick={handleBackToHome}>
          Back to Home
        </button>
      </div>
    </div>
  );
}

function MechanicApplicationRejected() {
  const { signOut } = useAuth();

  const handleBackToHome = () => {
    signOut();
    localStorage.removeItem('revvoo_session');
    localStorage.removeItem('revvoo_user');
    localStorage.removeItem('revvoo_view_mode');
    window.location.href = '/';
  };

  return (
    <div className="mechanic-application-page">
      <div className="mechanic-application-container">
        <div className="mechanic-application-logo">
          <img src="/logo.png" alt="Revvoo" style={{ height: '48px' }} />
        </div>
        <div className="mechanic-application-icon rejected">
          <XCircle size={56} />
        </div>
        <h1 className="mechanic-application-title">Application Not Approved</h1>
        <p className="mechanic-application-message">
          Your application was not approved at this time.
        </p>
        <p className="mechanic-application-contact">
          If you believe this is an error, contact us at <a href="mailto:revvoo.app@gmail.com">revvoo.app@gmail.com</a>
        </p>
        <button className="mechanic-application-cta" onClick={handleBackToHome}>
          Back to Home
        </button>
      </div>
    </div>
  );
}

// ========================================
// ROUTER WRAPPER
// ========================================

// Main App Component with AuthProvider and Router
function App() {
  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_MAPS_LIBRARIES}>
      <BrowserRouter>
        <AuthProvider>
          <AppWithSplash />
        </AuthProvider>
      </BrowserRouter>
      <Analytics />
    </LoadScript>
  );
}

// App wrapper with splash screen
function AppWithSplash() {
  const { loading: authLoading } = useAuth();
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [appReady, setAppReady] = useState(false);

  // No global mechanic redirect here. Mechanics can browse the site freely.
  // The MechanicDashboardPage's own useEffect gates non-active mechanics.
  // Login handlers redirect mechanics to the appropriate page after login.

  // Minimum splash display time of 1.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Set app ready when both auth check is done and minimum time has elapsed
  useEffect(() => {
    if (!authLoading && minTimeElapsed) {
      setAppReady(true);
    }
  }, [authLoading, minTimeElapsed]);

  return (
    <>
      <SplashScreen isVisible={!appReady} />
      <AppRoutes />
    </>
  );
}

// ScrollToTop component - scrolls to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

// Page transition wrapper for smooth fade-in
function PageTransition({ children }) {
  const location = useLocation();

  return (
    <div key={location.pathname} className="page-transition">
      {children}
    </div>
  );
}

// Protected route wrapper — guards routes by user type
function ProtectedRoute({ children, requiredType }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null); // null = checking, true/false = resolved

  // H1 FIX: Verify admin status against admin_users table instead of hardcoded email
  useEffect(() => {
    if (requiredType !== 'admin' || !user?.email) {
      setIsAdmin(null);
      return;
    }

    let cancelled = false;
    const checkAdmin = async () => {
      try {
        const result = await supabaseFetch('admin_users', 'GET', null, `?email=eq.${encodeURIComponent(user.email)}&select=id&limit=1`);
        if (!cancelled) {
          setIsAdmin(result && result.length > 0);
        }
      } catch (err) {
        console.error('Admin check failed:', err);
        if (!cancelled) setIsAdmin(false);
      }
    };
    checkAdmin();
    return () => { cancelled = true; };
  }, [requiredType, user?.email]);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      // Not logged in — redirect to appropriate login page
      if (requiredType === 'mechanic') {
        navigate('/mechanics/login', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
      return;
    }

    if (requiredType === 'admin') {
      // Wait for admin check to resolve
      if (isAdmin === false) {
        navigate('/', { replace: true });
      }
      return;
    }

    if (requiredType === 'mechanic' && user.user_type !== 'mechanic') {
      navigate('/', { replace: true });
      return;
    }

    if (requiredType === 'customer' && user.user_type === 'mechanic') {
      navigate('/mechanic-dashboard', { replace: true });
    }
  }, [user, loading, requiredType, navigate, isAdmin]);

  if (loading) return null;
  if (!user) return null;

  if (requiredType === 'admin' && isAdmin !== true) return null; // null = still checking, false = denied
  if (requiredType === 'mechanic' && user.user_type !== 'mechanic') return null;
  if (requiredType === 'customer' && user.user_type === 'mechanic') return null;

  return children;
}

// H4 FIX: Password Reset Page — handles the callback from Supabase recovery emails.
// When a user clicks the reset link in their email, Supabase redirects here with
// #access_token=...&type=recovery in the URL hash.
function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const navigate = useNavigate();

  // Extract the access_token from the URL hash on mount
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const token = hashParams.get('access_token');
    const type = hashParams.get('type');

    if (token && type === 'recovery') {
      setAccessToken(token);
      // Clean the hash from the URL
      window.history.replaceState(null, '', window.location.pathname);
    } else if (!token) {
      setError('Invalid or expired reset link. Please request a new password reset.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || data.msg || 'Failed to update password');
      }

      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="auth-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div className="auth-card" style={{ width: '100%', maxWidth: '440px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#0F172A', marginBottom: '8px', textAlign: 'center' }}>
          Set New Password
        </h2>

        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircle size={48} style={{ color: '#22C55E', marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', color: '#0F172A', fontWeight: '600' }}>Password updated successfully!</p>
            <p style={{ fontSize: '14px', color: '#64748b', marginTop: '8px' }}>Redirecting to login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', color: '#DC2626', fontSize: '14px' }}>
                {error}
              </div>
            )}

            {!accessToken ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>{error || 'Invalid reset link.'}</p>
                <button type="button" onClick={() => navigate('/login')} className="confirm-btn" style={{ width: '100%' }}>
                  Back to Login
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#0F172A', marginBottom: '6px' }}>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={6}
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#0F172A', marginBottom: '6px' }}>Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    minLength={6}
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>

                <button type="submit" className="confirm-btn" disabled={loading} style={{ width: '100%' }}>
                  {loading ? (
                    <><Loader className="spinner" size={20} /> Updating...</>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

// 404 Not Found page
function NotFoundPage() {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0F172A',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '440px',
        width: '100%',
      }}>
        <img
          src="/logo.png"
          alt="Revvoo"
          style={{ height: '40px', marginBottom: '48px' }}
        />
        <div style={{
          fontSize: '72px',
          fontWeight: '800',
          color: '#DC2626',
          lineHeight: 1,
          marginBottom: '16px',
        }}>404</div>
        <h1 style={{
          color: 'white',
          fontSize: '24px',
          fontWeight: '700',
          margin: '0 0 12px',
        }}>Page not found</h1>
        <p style={{
          color: '#94a3b8',
          fontSize: '16px',
          lineHeight: '1.6',
          margin: '0 0 32px',
        }}>
          The page you are looking for does not exist or has been moved.
        </p>
        <Link
          to="/"
          style={{
            display: 'inline-block',
            background: '#DC2626',
            color: 'white',
            textDecoration: 'none',
            padding: '14px 32px',
            borderRadius: '12px',
            fontWeight: '600',
            fontSize: '16px',
            transition: 'background 0.2s',
          }}
        >
          Go Home
        </Link>
      </div>
    </div>
  );
}

// Routes component with Suspense for lazy-loaded chunks
function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Main bundle routes (not lazy-loaded) */}
          <Route path="/" element={<PageTransition><AppContent /></PageTransition>} />
          <Route path="/signup" element={<PageTransition><AuthPage mode="signup" /></PageTransition>} />
          <Route path="/login" element={<PageTransition><AuthPage mode="login" /></PageTransition>} />
          <Route path="/dashboard" element={<ProtectedRoute requiredType="customer"><PageTransition><AppContent /></PageTransition></ProtectedRoute>} />
          <Route path="/my-bookings" element={<ProtectedRoute requiredType="customer"><PageTransition><AppContent /></PageTransition></ProtectedRoute>} />
          <Route path="/how-it-works" element={<PageTransition><HowItWorksPage /></PageTransition>} />
          <Route path="/pricing" element={<PageTransition><PricingPage /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
          <Route path="/mechanics/benefits" element={<PageTransition><MechanicBenefitsPage /></PageTransition>} />
          <Route path="/mechanics/application-received" element={<PageTransition><MechanicApplicationReceived /></PageTransition>} />
          <Route path="/mechanics/application-rejected" element={<PageTransition><MechanicApplicationRejected /></PageTransition>} />

          {/* Lazy-loaded chunks */}
          <Route path="/mechanics" element={<PageTransition><MechanicLandingPage /></PageTransition>} />
          <Route path="/mechanics/signup" element={<PageTransition><MechanicAuthPage mode="signup" /></PageTransition>} />
          <Route path="/mechanics/login" element={<PageTransition><MechanicAuthPage mode="login" /></PageTransition>} />
          <Route path="/mechanic-dashboard" element={<ProtectedRoute requiredType="mechanic"><PageTransition><MechanicDashboardPage /></PageTransition></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute requiredType="admin"><PageTransition><AdminDashboard /></PageTransition></ProtectedRoute>} />
          <Route path="/terms" element={<PageTransition><LazyTerms /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><LazyPrivacy /></PageTransition>} />
          <Route path="/links" element={<LinksPage />} />
          <Route path="/my-vehicles" element={<ProtectedRoute requiredType="customer"><PageTransition><LazyMyVehicles /></PageTransition></ProtectedRoute>} />
          <Route path="/payment-methods" element={<ProtectedRoute requiredType="customer"><PageTransition><LazyPaymentMethods /></PageTransition></ProtectedRoute>} />
          <Route path="/my-reviews" element={<ProtectedRoute requiredType="customer"><PageTransition><LazyMyReviews /></PageTransition></ProtectedRoute>} />
          <Route path="/help-support" element={<ProtectedRoute requiredType="customer"><PageTransition><LazyHelpSupport /></PageTransition></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute requiredType="customer"><PageTransition><LazySettings /></PageTransition></ProtectedRoute>} />
          <Route path="/reset-password" element={<PageTransition><ResetPasswordPage /></PageTransition>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

export { useAuth, Header, Footer };
export default App;